import React, { useState, useEffect } from 'react';
import { CallBtn, WABtn, PhoneField } from '../components/ui/PhoneButtons';
import { supabase } from '../lib/supabase';
import ImportButton from '../components/ui/ImportButton';
import { useAuth } from '../hooks/useAuth';
import useLiveRefresh from '../hooks/useLiveRefresh';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Search, Eye, Edit2, X, CheckCircle, XCircle, IndianRupee, Trash2, Download, FileText, Clock } from 'lucide-react';
import StudentTimelineModal from '../components/ui/StudentTimelineModal';
import ExportButton from '../components/ui/ExportButton';
import SearchableSelect from '../components/ui/SearchableSelect';
import GoogleDriveTab from '../components/ui/GoogleDriveTab';
import PassportPhotoUpload from '../components/ui/PassportPhotoUpload';
import { saveStyledXlsx } from '../lib/excelStyle';
import { fmtDate, fmtCurrency } from '../lib/exportUtils';
import { courseShortCode } from '../lib/courseUtils';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// ── Safe helpers (added in review) ──────────────────────────────
// PostgREST returns at most 1000 rows per request, so anything bigger is paged.
async function fetchAllPaged(buildQuery) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + 999);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < 1000) break;
    from += 1000;
  }
  return all;
}
// .in('col', ids) with hundreds of ids makes the URL too long; chunk the ids and page each chunk.
async function fetchByIds(table, select, col, ids, extra) {
  const out = [];
  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100);
    const rows = await fetchAllPaged(() => { let q = supabase.from(table).select(select).in(col, chunk); if (extra) q = extra(q); return q; });
    out.push(...rows);
  }
  return out;
}
// Local (IST) date as YYYY-MM-DD — toISOString() gives the UTC date, which is "yesterday" before 5:30am IST.
// Reports show only students who are still with us: not Discontinued, not Shifted, not deleted
const isReportable = s => s.is_active !== false && !['Discontinued', 'Shifted'].includes(s.student_status);
const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// Make free text safe inside a PostgREST .or() filter (commas / brackets break it; apostrophes must NOT be doubled).
const cleanSearch = t => String(t || '').replace(/[,()\\*%]/g, ' ').replace(/\s+/g, ' ').trim();
// words of the search joined by % so 'mar kothamangalam' also matches 'MAR ATHANASIUS ... ENGINEERING-KOTHAMANGALAM'
const collegeLike = s => `college_name.ilike.%${s.split(' ').filter(Boolean).join('%')}%`;
const csvCell = v => { const t = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t; };

// ── Known Locations by District ─────────────────────────────
const KNOWN_LOCATIONS = [
  // Thiruvananthapuram
  'Thiruvananthapuram','Poojappura','Pattom','Kazhakkoottam','Technopark','Vattiyoorkavu',
  'Nemom','Attingal','Neyyattinkara','Nedumangad','Balaramapuram','Pothencode','Varkala',
  // Kollam
  'Kollam','Karunagappally','Kottarakkara','Punalur','Paravur','Chavara','Kundara',
  // Pathanamthitta
  'Pathanamthitta','Thiruvalla','Adoor','Ranny','Pandalam','Kozhencherry',
  // Alappuzha
  'Alappuzha','Cherthala','Kayamkulam','Mavelikkara','Haripad','Kuttanad',
  // Kottayam
  'Kottayam','Changanacherry','Pala','Ettumanoor','Vaikom','Erattupetta','Thodupuzha',
  // Idukki
  'Idukki','Thodupuzha','Munnar','Kattappana','Adimali','Nedumkandam',
  // Ernakulam
  'Ernakulam','Kochi','Kakkanad','Palarivattom','Edapally','Vytilla','Aluva','Perumbavoor',
  'Muvattupuzha','Angamaly','Thrikkakara','Kalamassery','North Paravur','Kothamangalam',
  'Tripunithura','Piravom','Kolenchery','Chottanikkara','Thripunithura',
  // Thrissur
  'Thrissur','Irinjalakuda','Chalakudy','Kodungallur','Kunnamkulam','Guruvayur','Chavakkad',
  // Palakkad
  'Palakkad','Ottapalam','Shoranur','Mannarghat','Chittur','Alathur',
  // Malappuram
  'Malappuram','Manjeri','Tirur','Perinthalmanna','Ponnani','Kondotty','Kalpakanchery',
  // Kozhikode
  'Kozhikode','Calicut','Vadakara','Koyilandy','Ramanattukara','Feroke','Balussery',
  // Wayanad
  'Wayanad','Kalpetta','Mananthavady','Sulthan Bathery','Vythiri',
  // Kannur
  'Kannur','Thalassery','Iritty','Payyannur','Kuthuparamba','Mattannur',
  // Kasaragod
  'Kasaragod','Kanhangad','Hosdurg','Nileshwar','Bekal',
  // Other states
  'Chennai','Bangalore','Mysore','Mangalore','Mumbai','Pune','Hyderabad','Coimbatore',
  'Madurai','Tirunelveli','Salem','Tiruchirappalli',
  'Other'
].sort();

const TABS = ['Personal', 'Academic', 'Enrollment', 'Documents', 'Fees', 'Social Media', 'Drive Documents', 'Change History'];

const DOCUMENT_LIST = [
  { key: 'id_proof',                    label: 'ID Proof' },
  { key: 'passport_copy',               label: 'Passport Copy' },
  { key: 'sslc_certificate',            label: 'SSLC Certificate' },
  { key: 'course_completion_certificate', label: 'Course Completion Certificate' },
  { key: 'details_verification',        label: 'Details Verification' },
  { key: 'photo_softcopy',              label: 'Photo (Softcopy)' },
  { key: 'parent_contact_verification', label: 'Parent Contact Verification' },
  { key: 'nactet_registration',         label: 'NACTET Registration' },
];

const emptyStudent = {
  full_name: '', dob: '', contact_number: '', whatsapp_number: '', email: '',
  guardian_name: '', guardian_contact: '', place: '', pincode: '', district: '',
  state: '', known_location: '', academic_qualification: '', professional_qualification: '',
  experience: '', registration_date: new Date().toISOString().split('T')[0],
  google_review: false, video_feedback: false, placement_photos_website: false, recently_placed_website: false,
  register_number: '', centre: '', batch_id: '', college_id: '', nactet_register_number: '', training_timing: '', training_mode: '',
  joining_date: '', course_package_id: '', discipline: 'mechanical',
  hostel_needed: false, hostel_fee: '', hostel_joining_date: '',
  id_proof_type: '', id_proof_submitted: false,
  course_certificate_issued: false,
  classplus_installed: false,
  documents_checklist: {
    id_proof: false,
    passport_copy: false,
    sslc_certificate: false,
    course_completion_certificate: false,
    details_verification: false,
    photo_softcopy: false,
    parent_contact_verification: false,
    nactet_registration: false,
  },
  passport_photos_collected: '',
  student_status: 'Continuing',
  course_end_date: '',
  discontinue_reason: '',
  // Fee fields
  fee_tuition: '',
  hostel_fee: '',
  fee_attestation: '',
  // Attestation breakdown
  attest_hrd_mea_count: '',
  attest_hrd_mea_amount: '',
  attest_apostile_count: '',
  attest_apostile_amount: '',
  attest_uae_count: '',
  attest_uae_amount: '',
  fee_package_change: '',
  fee_manpower: '',
  fee_certification: '',
  fee_other: '',
  fee_discount: '',
  discount_reason: '',
};

function fmt(n) {
  return Number(n || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
}

function calcAttestationTotal(form) {
  return Number(form.attest_hrd_mea_amount  || 0)
       + Number(form.attest_apostile_amount || 0)
       + Number(form.attest_uae_amount      || 0);
}

function calcTotalFees(form) {
  const tuition   = Number(form.fee_tuition || 0);
  const hostel    = Number(form.hostel_fee || 0);
  const attest    = calcAttestationTotal(form);
  const pkgChange = Number(form.fee_package_change || 0);
  const manpower  = Number(form.fee_manpower || 0);
  const cert      = Number(form.fee_certification || 0);
  const other     = Number(form.fee_other || 0);
  const discount  = Number(form.fee_discount || 0);
  return tuition + hostel + attest + pkgChange + manpower + cert + other - discount;
}


// ── MultiFilter: searchable multi-select dropdown ───────────────
function MultiFilter({ label, options, selected, onToggle, onClear }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => !q || o.toLowerCase().includes(q.toLowerCase()));

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 110 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
        border: '1px solid var(--border)', borderRadius: 6, background: selected.length ? 'var(--accent-light)' : 'var(--surface)',
        color: selected.length ? 'var(--accent)' : 'var(--text2)', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
        fontWeight: selected.length ? 600 : 400,
      }}>
        {selected.length ? `${label} (${selected.length})` : label} ▾
        {selected.length > 0 && <span onClick={e=>{e.stopPropagation();onClear();}} style={{marginLeft:2,opacity:0.7,fontWeight:700}}>×</span>}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 999, marginTop: 4,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 200, maxWidth: 280,
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
            <input autoFocus placeholder={`Search ${label}...`} value={q} onChange={e=>setQ(e.target.value)}
              style={{ width: '100%', border: 'none', outline: 'none', fontSize: 12, background: 'transparent', color: 'var(--text)' }}/>
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0 && <div style={{padding:'10px 12px',color:'var(--text3)',fontSize:12}}>No results</div>}
            {filtered.map(o => (
              <label key={o} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                cursor: 'pointer', fontSize: 12, borderBottom: '1px solid var(--border)',
                background: selected.includes(o) ? 'var(--accent-light)' : 'transparent',
                color: selected.includes(o) ? 'var(--accent)' : 'var(--text)',
              }}>
                <input type="checkbox" checked={selected.includes(o)} onChange={()=>onToggle(o)}
                  style={{ width: 14, height: 14, accentColor: 'var(--accent)', flexShrink: 0 }}/>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <div style={{ padding: '6px 10px', borderTop: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {selected.map(s => (
                <span key={s} style={{ background: 'var(--accent)', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                  {s.length > 15 ? s.slice(0,15)+'…' : s}
                  <span onClick={()=>onToggle(s)} style={{ cursor: 'pointer', fontWeight: 700 }}>×</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Check if batch is new series (fees should auto-populate)
function isNewBatch(batchName) {
  if (!batchName) return false;
  const b = batchName.toUpperCase().trim();
  // EM47+, EC14+, TC07+, TM23+
  const patterns = [
    { prefix: 'EM', min: 47 },
    { prefix: 'EC', min: 14 },
    { prefix: 'TC', min: 7  },
    { prefix: 'TM', min: 23 },
  ];
  for (const { prefix, min } of patterns) {
    if (b.startsWith(prefix)) {
      const num = parseInt(b.slice(prefix.length));
      if (!isNaN(num) && num >= min) return true;
    }
  }
  return false;
}

function StudentHistory({ studentId }) {
  const [batchHistory, setBatchHistory] = React.useState([]);
  const [courseHistory, setCourseHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    Promise.all([
      supabase.from('student_batch_history').select('*').eq('student_id', studentId).order('transfer_date',{ascending:false}),
      supabase.from('student_course_history').select('*').eq('student_id', studentId).order('change_date',{ascending:false}),
    ]).then(([b, c]) => {
      setBatchHistory(b.data||[]);
      setCourseHistory(c.data||[]);
      setLoading(false);
    });
  }, [studentId]);

  if (loading) return <div style={{textAlign:'center',padding:10,color:'var(--text3)',fontSize:12}}>Loading history...</div>;
  if (!batchHistory.length && !courseHistory.length) return <div style={{textAlign:'center',padding:10,color:'var(--text3)',fontSize:12}}>No transfer/course change history</div>;

  return (
    <div style={{marginTop:12}}>
      {batchHistory.length>0&&(
        <div style={{marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:12,color:'var(--text2)',marginBottom:6}}>⇄ Batch Transfer History</div>
          {batchHistory.map(h=>(
            <div key={h.id} style={{display:'flex',gap:8,alignItems:'center',padding:'6px 10px',background:'var(--bg3)',borderRadius:6,marginBottom:4,fontSize:12}}>
              <span style={{color:'var(--text3)'}}>{fmtDMY(h.transfer_date)}</span>
              <span style={{color:'var(--red)',fontWeight:600}}>{h.from_batch}</span>
              <span>→</span>
              <span style={{color:'var(--green)',fontWeight:600}}>{h.to_batch}</span>
              {h.reason&&<span style={{color:'var(--text3)',fontSize:11}}>({h.reason})</span>}
            </div>
          ))}
        </div>
      )}
      {courseHistory.length>0&&(
        <div>
          <div style={{fontWeight:700,fontSize:12,color:'var(--text2)',marginBottom:6}}>📦 Course Change History</div>
          {courseHistory.map(h=>(
            <div key={h.id} style={{display:'flex',gap:8,alignItems:'center',padding:'6px 10px',background:'var(--bg3)',borderRadius:6,marginBottom:4,fontSize:12}}>
              <span style={{color:'var(--text3)'}}>{fmtDMY(h.change_date)}</span>
              <span style={{color:'var(--red)',fontWeight:600}}>{h.from_course}</span>
              <span>→</span>
              <span style={{color:'var(--green)',fontWeight:600}}>{h.to_course}</span>
              {h.reason&&<span style={{color:'var(--text3)',fontSize:11}}>({h.reason})</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Compact read-only placement summary shown inside Student detail view ──
function PlacementSummaryInline({ studentId }) {
  const [loading, setLoading] = useState(true);
  const [showDocReport, setShowDocReport] = useState(false);
  const [docReportBatch, setDocReportBatch] = useState('');
  const [hrStatus, setHrStatus] = useState(null);
  const [offerCount, setOfferCount] = useState(0);
  const [placement, setPlacement] = useState(null);
  const [afterCount, setAfterCount] = useState(0);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    Promise.all([
      supabase.from('hr_status').select('current_status').eq('student_id', studentId).maybeSingle(),
      supabase.from('placement_offers').select('id', { count: 'exact', head: true }).eq('student_id', studentId),
      supabase.from('placements').select('company_name, job_role, joining_date, resigned').eq('student_id', studentId).maybeSingle(),
      supabase.from('after_placement').select('id', { count: 'exact', head: true }).eq('student_id', studentId),
    ]).then(([hr, off, pl, after]) => {
      setHrStatus(hr.data || null);
      setOfferCount(off.count || 0);
      setPlacement(pl.data || null);
      setAfterCount(after.count || 0);
      setLoading(false);
    });
  }, [studentId]);

  if (loading) return <div style={{ fontSize: 12, color: 'var(--text3)' }}>Loading...</div>;

  const Item = ({ label, value }) => (
    <div className="detail-row">
      <div className="detail-label">{label}</div>
      <div className="detail-value">{value}</div>
    </div>
  );

  return (
    <>
      <Item label="① Initial HR Status" value={hrStatus?.current_status || 'No record yet'} />
      <Item label="② Job Offers" value={offerCount > 0 ? `${offerCount} offer${offerCount > 1 ? 's' : ''}` : 'No record yet'} />
      <Item label="③ Placement" value={placement ? `${placement.company_name || '—'} (${placement.job_role || '—'})${placement.resigned ? ' — Resigned' : ''}` : 'Not placed yet'} />
      <Item label="④ After Placement" value={afterCount > 0 ? `${afterCount} record${afterCount > 1 ? 's' : ''}` : 'No record yet'} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// STUDENT REPORTS
// ═══════════════════════════════════════════════════════════════
function printReport(divId, landscape=false) {
  const el = document.getElementById(divId);
  if (!el) return;
  const w = window.open('','_blank','width=900,height=700');
  const isTable = el.tagName === 'TABLE';
  const content = isTable ? '<table style="width:100%;border-collapse:collapse;">'+el.innerHTML+'</table>' : el.innerHTML;
  w.document.write('<!DOCTYPE html><html><head><title>Print</title><style>@page{size:A4 '+(landscape?'landscape':'portrait')+';margin:8mm;}*{box-sizing:border-box;}body{font-family:Arial,sans-serif;font-size:12px;margin:0;padding:0;}table{width:100%;border-collapse:collapse;border:1px solid #333;}th,td{border:1px solid #333;padding:5px 7px;text-align:left;word-break:break-word;}th{background:#d1d5db;font-weight:bold;font-size:12px;}tr:nth-child(even){background:#f3f4f6;}tr{page-break-inside:avoid;}</style></head><body>'+content+'</body></html>');
  w.document.close();
  w.onafterprint = () => { w.close(); window.focus(); };
  setTimeout(()=>{ w.print(); }, 800);
}

function DocStatusReport({ batches, students, selBatch, setSelBatch, supabase, onReload, onUpdateStudents }) {
  const DOCS = DOCUMENT_LIST;
  const DOC_SHORT = {
    'id_proof': 'ID Proof',
    'passport_copy': 'Passport',
    'sslc_certificate': 'SSLC',
    'course_completion_certificate': 'Course Cert',
    'details_verification': 'Details',
    'photo_softcopy': 'Photo',
    'parent_contact_verification': 'Parent Contact',
    'nactet_registration': 'NACTET',
  };
  const [viewMode, setViewMode] = React.useState('summary');
  const [selDoc, setSelDoc] = React.useState('');
  const [bulkData, setBulkData] = React.useState({});
  const bulkDataRef = React.useRef({});
  const [bulkOrig, setBulkOrig] = React.useState({});
  React.useEffect(() => { bulkDataRef.current = bulkData; }, [bulkData]);
  const [bulkSaving, setBulkSaving] = React.useState(false);
  const [bulkProgress, setBulkProgress] = React.useState('');

  const [allBatchStudents, setAllBatchStudents] = React.useState([]);
  const [batchLoading, setBatchLoading] = React.useState(false);
  React.useEffect(() => {
    if (!selBatch) { setAllBatchStudents([]); return; }
    setBatchLoading(true);
    supabase.from('students').select('id,full_name,register_number,batch_id,batch_name,course_name,contact_number,documents_checklist,passport_photos_collected,classplus_installed,is_active,student_status')
      .eq('batch_name', selBatch).eq('is_active', true)
      .order('register_number')
      .then(({ data }) => { setAllBatchStudents((data||[]).filter(isReportable)); setBatchLoading(false); });
  }, [selBatch]);
  const reportStudents = allBatchStudents.slice().sort((a,b)=>(a.register_number||'').localeCompare(b.register_number||'',undefined,{numeric:true}));

  function printReport() {
    const w = window.open('','_blank');
    const rows = DOCS.map(doc => {
      const sub = reportStudents.filter(s=>s.documents_checklist?.[doc.key]).length;
      const pen = reportStudents.length - sub;
      const pct = reportStudents.length>0?Math.round(sub/reportStudents.length*100):0;
      return `<tr><td>${doc.label}</td><td style="color:green;text-align:center">${sub}</td><td style="color:red;text-align:center">${pen}</td><td style="text-align:center">${reportStudents.length}</td><td style="text-align:center">${pct}%</td></tr>`;
    }).join('');
    w.document.write(`<html><head><title>Document Status Report - ${selBatch||'All Batches'}</title>
    <style>body{font-family:Arial,sans-serif;padding:20px}h2{margin-bottom:4px}h4{color:#666;margin-top:0}
    table{width:100%;border-collapse:collapse;margin-top:16px}th{background:#1e40af;color:#fff;padding:8px 12px;text-align:left}
    td{padding:7px 12px;border-bottom:1px solid #e5e7eb}tr:nth-child(even){background:#f9fafb}
    @media print{button{display:none}}</style></head>
    <body><h2>📋 Document Status Report</h2><h4>${selBatch||'All Batches'} — ${reportStudents.length} Students</h4>
    <table><thead><tr><th>Document</th><th>Submitted</th><th>Pending</th><th>Total</th><th>% Done</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <p style="font-size:11px;color:#999;margin-top:20px">Generated: ${new Date().toLocaleString('en-IN')}</p>
    </body></html>`);
    w.document.close(); w.print();
  }

  function printPendingList() {
    if(!selDoc) return;
    const doc = DOCS.find(d=>d.key===selDoc);
    const pending = reportStudents.filter(s=>!s.documents_checklist?.[selDoc]);
    const rows = pending.map((s,i)=>`<tr><td>${i+1}</td><td>${s.full_name||''}</td><td>${s.register_number||''}</td><td>${s.batch_name||''}</td></tr>`).join('');
    const w = window.open('','_blank');
    w.document.write(`<html><head><title>Pending - ${doc?.label}</title>
    <style>body{font-family:Arial,sans-serif;padding:20px}h2{margin-bottom:4px}h4{color:#666;margin-top:0}
    table{width:100%;border-collapse:collapse;margin-top:16px}th{background:#1e40af;color:#fff;padding:8px 12px;text-align:left}
    td{padding:7px 12px;border-bottom:1px solid #e5e7eb}tr:nth-child(even){background:#f9fafb}</style></head>
    <body><h2>⚠️ Pending: ${doc?.label}</h2><h4>${selBatch||'All Batches'} — ${pending.length} students pending</h4>
    <table><thead><tr><th>#</th><th>Name</th><th>Register No</th><th>Batch</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <p style="font-size:11px;color:#999;margin-top:20px">Generated: ${new Date().toLocaleString('en-IN')}</p>
    </body></html>`);
    w.document.close(); w.print();
  }

  async function saveBulkDocs() {
    setBulkSaving(true);
    try { sessionStorage.removeItem('armstech_report_students_v3'); sessionStorage.removeItem('armstech_report_students_v3_ts'); sessionStorage.clear(); } catch(e){}
    let saved = 0, errs = 0;
    const currentBulkData = bulkDataRef.current;
    const bulkOrigRef = bulkOrig;
    const changedIds = Object.keys(currentBulkData).filter(id => {
      const orig = bulkOrigRef[id] || {};
      const curr = currentBulkData[id] || {};
      // Compare each doc field
      for (const d of DOCS) {
        if (Boolean(curr[d.key]) !== Boolean(orig[d.key])) return true;
      }
      // Compare photos
      const origPc = orig.passport_photos_collected != null ? String(orig.passport_photos_collected) : '';
      const currPc = curr.passport_photos_collected != null ? String(curr.passport_photos_collected) : '';
      if (origPc !== currPc) return true;
      if (Boolean(curr.classplus_installed) !== Boolean(orig.classplus_installed)) return true; // cpBulkDone
      return false;
    });
    if (changedIds.length === 0) { setBulkSaving(false); toast.success('No changes to save'); return; }
    for (const id of changedIds) { const docs = currentBulkData[id];
      const docFields = {};
      DOCS.forEach(d => { docFields[d.key] = docs[d.key] || false; });
      const updatePayload = {documents_checklist: docFields};
      const pc = docs.passport_photos_collected;
      updatePayload.passport_photos_collected = (pc !== '' && pc !== undefined && pc !== null) ? Number(pc) : null;
      updatePayload.classplus_installed = !!docs.classplus_installed;
      const { error } = await supabase.from('students').update(updatePayload).eq('id', id);
      if (error) { console.error('Error saving', id, error); errs++; } else { saved++; setBulkProgress(saved + '/' + changedIds.length); }
    }
    setBulkSaving(false);
    if (errs) toast.error(errs + ' errors saving');
    else {
      toast.success('Saved ' + saved + ' students');
      // Update local bulkOrig so re-opening bulk update shows fresh data
      setBulkOrig(JSON.parse(JSON.stringify(bulkData)));
      // Update local students state immediately so re-open shows fresh data
      if (onUpdateStudents) {
        onUpdateStudents(prev => prev.map(s => {
          if (!bulkDataRef.current[s.id]) return s;
          const docs = bulkDataRef.current[s.id];
          const docFields = {};
          DOCS.forEach(d => { docFields[d.key] = docs[d.key] || false; });
          const pc = docs.passport_photos_collected;
          return {...s, documents_checklist: docFields, classplus_installed: !!docs.classplus_installed, passport_photos_collected: (pc!==''&&pc!==undefined&&pc!==null)?Number(pc)||null:null};
        }));
      }
      if (onReload) onReload();
    }
  }

  return (
    <div>
      {/* Controls */}
      <div style={{display:'flex',gap:10,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
        <SearchableSelect
          options={[{value:'',label:'All Batches'},...sortBatches(batches).map(b=>({value:b.batch_name,label:b.batch_name}))]}
          value={selBatch||''}
          onChange={v=>{setSelBatch(v);setSelDoc('');setViewMode('summary');}}
          placeholder="All Batches"
          style={{minWidth:200}}
        />
        {selBatch&&<button className="btn btn-secondary btn-sm" onClick={()=>setSelBatch('')}>✕</button>}
        <div style={{display:'flex',gap:4,marginLeft:'auto'}}>
          <button className={`btn btn-sm ${viewMode==='summary'?'btn-primary':'btn-secondary'}`} onClick={()=>setViewMode('summary')}>📊 Summary</button>
          <button className={`btn btn-sm ${viewMode==='pending'?'btn-primary':'btn-secondary'}`} onClick={()=>{ const firstPending=DOCS.find(d=>reportStudents.filter(s=>!s.documents_checklist?.[d.key]).length>0); setSelDoc(firstPending?.key||''); setViewMode('pending'); }}>⚠️ Pending Lists</button>
          <button className={`btn btn-sm ${viewMode==='bulk'?'btn-primary':'btn-secondary'}`} onClick={()=>{ const init={}; reportStudents.forEach(s=>{init[s.id]=Object.assign({},s.documents_checklist||{},{passport_photos_collected:s.passport_photos_collected!=null?String(s.passport_photos_collected):'',classplus_installed:!!s.classplus_installed});}); setBulkData(init); setBulkOrig(JSON.parse(JSON.stringify(init))); setViewMode('bulk'); }}>✏️ Bulk Update</button>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={printReport}>🖨️ Print Summary</button>
      </div>

      {viewMode==='summary' && (
        <>
          <div style={{fontSize:12,color:'var(--text3)',marginBottom:8}}>{reportStudents.length} students {selBatch?`in ${selBatch}`:''}</div>
          <div style={{overflowX:'auto',overflowY:'auto',maxHeight:'calc(100vh - 260px)'}}>
            <table style={{width:'100%',borderCollapse:'separate',borderSpacing:0,fontSize:13}}>
              <thead style={{position:'sticky',top:0,zIndex:100,background:'var(--accent-light)'}}><tr>
                  <th style={{background:'#e6f7f5'}}>Document</th>
                  <th style={{textAlign:'center',color:'var(--green)',background:'#e6f7f5'}}>✅ Submitted</th>
                  <th style={{textAlign:'center',color:'var(--red)',background:'#e6f7f5'}}>⚠️ Pending</th>
                  <th style={{textAlign:'center',background:'#e6f7f5'}}>Total</th>
                  <th style={{background:'#e6f7f5'}}>Progress</th>
                  <th style={{textAlign:'center',background:'#e6f7f5'}}>Actions</th>
                </tr></thead>
              <tbody>
                {DOCS.map(doc=>{
                  const sub=reportStudents.filter(s=>s.documents_checklist?.[doc.key]===true).length;
                  const total=reportStudents.length;
                  const pen=total-sub;
                  const pct=total>0?Math.round(sub/total*100):0;
                  return(
                  <tr key={doc.key}>
                    <td style={{padding:'9px 14px',fontWeight:500}}>{doc.label}</td>
                      <td style={{padding:'9px 14px',textAlign:'center',fontWeight:700,color:'var(--green)',fontSize:15}}>{sub}</td>
                      <td style={{padding:'9px 14px',textAlign:'center',fontWeight:700,color:pen>0?'var(--red)':'var(--text3)',fontSize:15}}>{pen}</td>
                      <td style={{padding:'9px 14px',textAlign:'center',color:'var(--text3)'}}>{total}</td>
                      <td style={{padding:'9px 14px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div style={{flex:1,height:8,background:'var(--border)',borderRadius:4}}>
                            <div style={{width:pct+'%',height:'100%',background:pct===100?'var(--green)':pct>60?'var(--amber)':'var(--red)',borderRadius:4}}/>
                          </div>
                          <span style={{fontSize:12,minWidth:36,fontWeight:600,color:pct===100?'var(--green)':pct>60?'var(--amber)':'var(--red)'}}>{pct}%</span>
                        </div>
                      </td>
                      <td style={{padding:'9px 14px',textAlign:'center'}}>
                        {pen>0&&<button className="btn btn-secondary btn-sm" style={{fontSize:11}} onClick={()=>{setSelDoc(doc.key);setViewMode('pending');setSelDoc(doc.key);}}>View {pen} Pending</button>}
                        {pen===0&&<span style={{color:'var(--green)',fontSize:12}}>✅ Complete</span>}
                      </td>
                    </tr>
                  );
                })}
                {(()=>{ const total=reportStudents.length; const sub=reportStudents.filter(s=>s.classplus_installed).length; const pen=total-sub; const pct=total>0?Math.round(sub/total*100):0; return (
                  <tr>
                    <td style={{padding:'9px 14px',fontWeight:500}}>Classplus App Installed</td>
                    <td style={{padding:'9px 14px',textAlign:'center',fontWeight:700,color:'var(--green)',fontSize:15}}>{sub}</td>
                    <td style={{padding:'9px 14px',textAlign:'center',fontWeight:700,color:pen>0?'var(--red)':'var(--text3)',fontSize:15}}>{pen}</td>
                    <td style={{padding:'9px 14px',textAlign:'center',color:'var(--text3)'}}>{total}</td>
                    <td style={{padding:'9px 14px'}}><div style={{display:'flex',alignItems:'center',gap:8}}><div style={{flex:1,height:8,background:'var(--border)',borderRadius:4}}><div style={{width:pct+'%',height:'100%',background:pct===100?'var(--green)':pct>60?'var(--amber)':'var(--red)',borderRadius:4}}/></div><span style={{fontSize:12,minWidth:36,fontWeight:600}}>{pct}%</span></div></td>
                    <td style={{padding:'9px 14px',textAlign:'center'}}>{pen===0&&total>0?<span style={{color:'var(--green)',fontSize:12}}>✅ Complete</span>:null}</td>
                  </tr>); })()}
              </tbody>
            </table>
          </div>
        </>
      )}

      {viewMode==='pending' && (
        <>
          <div style={{display:'flex',gap:10,marginBottom:16,alignItems:'center',flexWrap:'wrap'}}>
            <button className="btn btn-secondary btn-sm" onClick={()=>setViewMode('summary')}>← Back to Summary</button>
            <span style={{fontSize:12,color:'var(--text3)'}}>{reportStudents.length} students {selBatch?`in ${selBatch}`:''}</span>
          </div>
          {(selDoc ? DOCS.filter(d=>d.key===selDoc) : DOCS).map(doc=>{
            const pending = reportStudents.filter(s=>!s.documents_checklist?.[doc.key]);
            if(pending.length===0) return (
              <div key={doc.key} style={{marginBottom:8,padding:'8px 14px',background:'rgba(34,197,94,0.08)',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontSize:13,fontWeight:500}}>{doc.label}</span>
                <span style={{color:'var(--green)',fontSize:12,fontWeight:600}}>✅ All {reportStudents.length} submitted</span>
              </div>
            );
            return (
              <div key={doc.key} style={{marginBottom:16,border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
                <div style={{padding:'10px 14px',background:'var(--bg3)',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
                  <span style={{fontWeight:700,fontSize:13}}>{doc.label}</span>
                  <div style={{display:'flex',gap:12,alignItems:'center'}}>
                    <span style={{fontSize:12,color:'var(--green)'}}><b>{reportStudents.length-pending.length}</b> submitted</span>
                    <span style={{fontSize:12,color:'var(--red)',fontWeight:700}}>⚠️ {pending.length} pending</span>
                    <button className="btn btn-secondary btn-sm" style={{fontSize:11}} onClick={()=>{
                      setSelDoc(doc.key);
                      const rows = pending.map((s,i)=>`<tr><td>${i+1}</td><td>${s.full_name||''}</td><td>${s.register_number||''}</td><td>${s.batch_name||''}</td><td>${s.contact_number||s.whatsapp_number||'—'}</td></tr>`).join('');
                      const w=window.open('','_blank');
                      w.document.write(`<html><head><title>Pending - ${doc.label}</title><style>body{font-family:Arial,sans-serif;padding:20px}h2{margin-bottom:4px}h4{color:#666;margin-top:0}table{width:100%;border-collapse:collapse;margin-top:16px}th{background:#1e40af;color:#fff;padding:8px 12px;text-align:left}td{padding:7px 12px;border-bottom:1px solid #e5e7eb}tr:nth-child(even){background:#f9fafb}</style></head><body><h2>⚠️ Pending: ${doc.label}</h2><h4>${selBatch||'All Batches'} — ${pending.length} students pending</h4><table><thead><tr><th>#</th><th>Name</th><th>Register No</th><th>Batch</th><th>Contact</th></tr></thead><tbody>${rows}</tbody></table><p style="font-size:11px;color:#999;margin-top:20px">Generated: ${new Date().toLocaleString('en-IN')}</p></body></html>`);
                      w.document.close();w.print();
                    }}>🖨️ Print</button>
                  </div>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead>
                      <tr style={{background:'var(--accent-light)'}}>
                        <th style={{padding:'6px 12px'}}>#</th>
                        <th style={{padding:'6px 12px',textAlign:'left'}}>Name</th>
                        <th style={{padding:'6px 12px',textAlign:'left'}}>Register No</th>
                        <th style={{padding:'6px 12px',textAlign:'left'}}>Batch</th>
                        <th style={{padding:'6px 12px',textAlign:'left'}}>Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pending.map((s,i)=>(
                        <tr key={s.id} style={{borderTop:'1px solid var(--border)',background:i%2?'var(--bg3)':'transparent'}}>
                          <td style={{padding:'5px 12px',color:'var(--text3)'}}>{i+1}</td>
                          <td style={{padding:'5px 12px',fontWeight:600}}>{s.full_name}</td>
                          <td style={{padding:'5px 12px',fontFamily:'monospace',fontSize:11}}>{s.register_number}</td>
                          <td style={{padding:'5px 12px'}}>{s.batch_name}</td>
                          <td style={{padding:'5px 12px'}}>{s.contact_number||s.whatsapp_number||'—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </>
      )}

      {viewMode==='bulk' && (
        <>
          <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center',flexWrap:'wrap'}}>
            <button className="btn btn-secondary btn-sm" onClick={()=>setViewMode('summary')}>← Back to Summary</button>
            <span style={{fontSize:12,color:'var(--text3)'}}>{reportStudents.length} students — click cells to toggle</span>
            <button className="btn btn-primary btn-sm" disabled={bulkSaving} style={{marginLeft:'auto'}} onClick={()=>{
              setBulkSaving(true);
              saveBulkDocs();
            }}>{bulkSaving ? (bulkProgress||'Saving...') : '💾 Save All'}</button>
          </div>
          <div style={{overflowX:'auto',overflowY:'auto',maxHeight:'calc(100vh - 260px)'}}>
            <table style={{borderCollapse:'separate',borderSpacing:0,fontSize:12,minWidth:'100%'}}>
              <thead style={{position:'sticky',top:0,background:'#e6f7f5',zIndex:100}}>
                <tr>
                  <th style={{padding:'8px 10px',textAlign:'left',minWidth:160,background:'#e6f7f5'}}>Student</th>
                  <th style={{padding:'8px 6px',textAlign:'left',minWidth:80,fontFamily:'monospace',background:'#e6f7f5'}}>Reg No</th>
                  {DOCS.map(doc=>(
                    <th key={doc.key} style={{padding:'6px 8px',textAlign:'center',fontSize:11,minWidth:70,whiteSpace:'nowrap',background:'#e6f7f5'}}>{DOC_SHORT[doc.key]||doc.label}</th>
                  ))}
                  <th style={{padding:'6px 8px',textAlign:'center',fontSize:11,minWidth:90,whiteSpace:'nowrap',background:'#e6f7f5'}}>
                    <div>Classplus</div>
                    <div style={{fontWeight:400,marginTop:2}}>
                      <button type="button" className="btn btn-ghost btn-sm" style={{padding:'0 5px',fontSize:10,color:'var(--green)'}} title="Mark every student Y" onClick={()=>setBulkData(prev=>{const n={...prev};reportStudents.forEach(st=>{n[st.id]={...(n[st.id]||{}),classplus_installed:true};});return n;})}>All Y</button>
                      <button type="button" className="btn btn-ghost btn-sm" style={{padding:'0 5px',fontSize:10,color:'var(--red)'}} title="Mark every student N" onClick={()=>setBulkData(prev=>{const n={...prev};reportStudents.forEach(st=>{n[st.id]={...(n[st.id]||{}),classplus_installed:false};});return n;})}>All N</button>
                    </div>
                  </th>
                  <th style={{padding:'6px 8px',textAlign:'center',fontSize:11,minWidth:70,whiteSpace:'nowrap',background:'#e6f7f5'}}>📷 Photos</th>
                </tr>
              </thead>
              <tbody>
                {reportStudents.map((s,i)=>(
                  <tr key={s.id} style={{borderTop:'1px solid var(--border)',background:i%2===0?'var(--bg)':'var(--surface)'}}>
                    <td style={{padding:'6px 10px',fontWeight:500}}>{s.full_name}</td>
                    <td style={{padding:'6px 6px',fontFamily:'monospace',fontSize:11}}>{s.register_number}</td>
                    {DOCS.map(doc=>{
                      const val = bulkData[s.id]?.[doc.key] || false;
                      return (
                        <td key={doc.key} style={{padding:'4px 6px',textAlign:'center',cursor:'pointer'}}
                          onClick={()=>setBulkData(prev=>({...prev,[s.id]:{...(prev[s.id]||{}), [doc.key]: !val}}))}>
                          <span style={{fontSize:16}}>{val ? '✅' : '❌'}</span>
                        </td>
                      );
                    })}
                    <td style={{padding:'4px 6px',textAlign:'center',cursor:'pointer'}} onClick={()=>setBulkData(prev=>({...prev,[s.id]:{...(prev[s.id]||{}),classplus_installed:!prev[s.id]?.classplus_installed}}))}>
                      {bulkData[s.id]?.classplus_installed
                        ? <span style={{display:'inline-block',minWidth:28,borderRadius:6,padding:'2px 8px',fontWeight:800,fontSize:12,background:'#dcfce7',color:'#166534'}}>Y</span>
                        : <span style={{display:'inline-block',minWidth:28,borderRadius:6,padding:'2px 8px',fontWeight:800,fontSize:12,background:'#fee2e2',color:'#991b1b'}}>N</span>}
                    </td>
                    <td style={{padding:'2px 4px',textAlign:'center'}}><input type='number' min='0' value={(bulkData[s.id]?.passport_photos_collected??s.passport_photos_collected)??''} onChange={e=>setBulkData(prev=>({...prev,[s.id]:{...(prev[s.id]||{}),passport_photos_collected:e.target.value}}))} style={{width:50,fontSize:11,padding:'2px 4px',textAlign:'center'}}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


function StudentReports({ batches, isActive, supabase, onReload }) {
  const [activeReport, setActiveReport] = React.useState('batchwise');
  const [rptBatch,   setRptBatch]   = React.useState('');
  const [selBatch,   setSelBatch]   = React.useState('');
  const [selPkg,     setSelPkg]     = React.useState('');
  const [students,   setStudents]   = React.useState([]);
  const [paymentMap, setPaymentMap] = React.useState({});
  const [paymentsLoaded, setPaymentsLoaded] = React.useState(false);
  const [loading,    setLoading]    = React.useState(false);
  const [loaded,     setLoaded]     = React.useState(false);
  const [loadError,  setLoadError]  = React.useState('');
  const [reloadKey,  setReloadKey]  = React.useState(0);
  // Ongoing batches first, then upcoming, then completed (newest first inside each group)
  const orderedBatches = React.useMemo(() => sortBatches(batches), [batches]);
  const batchOpts = orderedBatches.map(b => ({ value: b.batch_name, label: b.batch_name }));

  const INR = v => `₹${Number(v||0).toLocaleString('en-IN',{minimumFractionDigits:0})}`;
  const fmtDate = d => { if(!d) return '\u2014'; try { const dt=new Date(d); return dt.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); } catch(e){ return d; } };

  React.useEffect(()=>{
    if (!isActive || loaded) return;
    setLoading(true);
    // Clear any old cache versions
    try { ['armstech_report_students_v3','armstech_report_students_v3'].forEach(k=>{ sessionStorage.removeItem(k); sessionStorage.removeItem(k+'_ts'); }); } catch(e){}
    (async()=>{
      const CACHE_KEY = 'armstech_report_students_v3';
      const CACHE_TIME = 'armstech_report_students_v3_ts';
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
      // Every request is checked for errors and retried (a silent failure used to leave the report empty).
      const retry = async (fn, tries = 3) => {
        let last;
        for (let t = 0; t < tries; t++) {
          const r = await fn();
          if (!r.error) return r;
          last = r.error;
          await new Promise(res => setTimeout(res, 400 * (t + 1)));
        }
        throw new Error(last?.message || 'request failed');
      };
      const inPool = async (jobs, size) => {           // run at most `size` requests at a time
        const out = new Array(jobs.length); let next = 0;
        await Promise.all(Array.from({ length: Math.min(size, jobs.length) }, async () => {
          while (next < jobs.length) { const k = next++; out[k] = await jobs[k](); }
        }));
        return out;
      };
      const STU_COLS = 'id,full_name,register_number,batch_id,batch_name,fee_total,course_name,contact_number,whatsapp_number,email,guardian_name,guardian_contact,college_name,discipline,training_timing,registration_date,joining_date,hostel_needed,nactet_register_number,centre,academic_qualification,documents_checklist,passport_photos_collected,known_location,district,state,place,pincode,dob,student_status,course_package_id,college_id,classplus_installed,professional_qualification,experience,training_mode,google_review,video_feedback,batch_code';
      const loadStudents = async () => {
        const { count } = await retry(() => supabase.from('students').select('id', { count: 'exact', head: true }));
        const total = count || 0;
        const PAGE = 500;
        // if a column named in STU_COLS is missing from the table, drop it and try again instead of failing the whole report
        let cols = STU_COLS.split(',');
        const pageQuery = async (i) => {
          for (let guard = 0; guard < 10; guard++) {
            const r = await supabase.from('students').select(cols.join(',')).order('id').range(i * PAGE, (i + 1) * PAGE - 1);
            if (!r.error) return r;
            const m = /column students\.(\w+) does not exist/.exec(r.error.message || '');
            if (m && cols.includes(m[1])) { console.warn('Reports: students.' + m[1] + ' does not exist, skipping it'); cols = cols.filter(c => c !== m[1]); continue; }
            return r;
          }
          return { error: { message: 'too many missing columns' } };
        };
        const jobs = Array.from({ length: Math.ceil(total / PAGE) }, (_, i) => async () => {
          const r = await retry(() => pageQuery(i));
          return r.data || [];
        });
        const parts = await inPool(jobs, 4);
        return parts.flat().filter(isReportable);
      };
      const loadPaymentsSeq = async () => {              // fallback: plain sequential paging
        let rows = [], from = 0;
        while (true) {
          const r = await retry(() => supabase.from('fee_payments').select('student_id,amount').range(from, from + 999));
          const d = r.data || [];
          rows = rows.concat(d);
          if (d.length < 1000) break;
          from += 1000;
        }
        return rows;
      };
      const loadPayments = async () => { try { return await loadPaymentsFast(); } catch (e) { console.warn('fast payment load failed, using sequential:', e); return loadPaymentsSeq(); } };
      const loadPaymentsFast = async () => {
        const { count } = await retry(() => supabase.from('fee_payments').select('id', { count: 'exact', head: true }));
        const total = count || 0;
        const PAGE = 1000;
        const jobs = Array.from({ length: Math.ceil(total / PAGE) }, (_, i) => async () => {
          const r = await retry(() => supabase.from('fee_payments').select('student_id,amount').order('id').range(i * PAGE, (i + 1) * PAGE - 1));
          return r.data || [];
        });
        return (await inPool(jobs, 4)).flat();
      };
      let all, allPay;
      try {
        [all, allPay] = await Promise.all([loadStudents(), loadPayments()]);
      } catch (e) {
        console.error('StudentReports load failed:', e);
        setLoadError(e.message || 'Could not load the report data'); setLoading(false);
        return;
      }
      const pm={};
      allPay.forEach(p=>{ pm[p.student_id]=(pm[p.student_id]||0)+Number(p.amount||0); });

      setStudents(all); setPaymentMap(pm); setPaymentsLoaded(true); setLoading(false); setLoaded(true);
      console.log('StudentReports loaded fresh:',all.length,'students,',allPay.length,'payments');
    })();
    return () => { if (!isActive) setLoaded(false); };
  },[isActive, reloadKey]);

  function BatchWiseReport() {
    const [rpcRows, setRpcRows] = React.useState([]);
    const [rpcLoading, setRpcLoading] = React.useState(true);
    React.useEffect(()=>{
      supabase.rpc('get_batch_wise_fee_summary').then(({data,error})=>{
        if(error) console.error('batch fee rpc error:',error);
        setRpcRows(data||[]); setRpcLoading(false);
      });
    },[]);
    if(loadError) return (
      <div style={{textAlign:'center',padding:30,color:'var(--red)'}}>
        <div style={{fontWeight:700,marginBottom:6}}>Could not load the student data for this report</div>
        <div style={{fontSize:12,marginBottom:10}}>{loadError}</div>
        <button className="btn btn-primary btn-sm" onClick={()=>{ setLoadError(''); setLoaded(false); setReloadKey(k=>k+1); }}>↻ Retry</button>
      </div>
    );
    if(rpcLoading || !loaded) return <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>Loading fee summary...</div>;
    // Batch strength from the loaded student list (active students only)
    const strength = {};
    (students||[]).filter(isReportable).forEach(x=>{
      const k = x.batch_name || '—';
      const b = strength[k] || (strength[k] = { total:0, continuing:0, passed:0, other:0, fee:0, paid:0 });
      b.total++;
      b.fee  += Number(x.fee_total||0);
      b.paid += Number(paymentMap[x.id]||0);
      const st = x.student_status || 'Continuing';
      if (st==='Continuing') b.continuing++; else if (st==='Passed Out') b.passed++; else b.other++;
    });
    const rows = rpcRows.map((r,i)=>({
      i:i+1, batch_name:r.batch_name,
      sTotal:(strength[r.batch_name]||{}).total||0, sCont:(strength[r.batch_name]||{}).continuing||0,
      sPass:(strength[r.batch_name]||{}).passed||0, sOther:(strength[r.batch_name]||{}).other||0,
      year:r.start_date?new Date(r.start_date).getFullYear():'—',
      start_date:r.start_date,
      // fees are summed from the active students only (discontinued / shifted students are left out)
      actualFee:(strength[r.batch_name]||{}).fee||0,
      paidFee:(strength[r.batch_name]||{}).paid||0,
      balance:((strength[r.batch_name]||{}).fee||0)-((strength[r.batch_name]||{}).paid||0),
    }));
    const tA=rows.reduce((s,r)=>s+r.actualFee,0);
    const tP=rows.reduce((s,r)=>s+r.paidFee,0);
    const tB=rows.reduce((s,r)=>s+r.balance,0);
    const sum = k => rows.reduce((a,r)=>a+r[k],0);
    function exportExcel(){
      const data=rows.map(r=>({'#':r.i,'Batch Name':r.batch_name,'Year':r.year,'Start Date':fmtDate(r.start_date),'Enrolled':r.sTotal,'Continuing':r.sCont,'Passed Out':r.sPass,'Other (On Hold etc.)':r.sOther,'Actual Fee':r.actualFee,'Paid Fee':r.paidFee,'Balance Fee':r.balance}));
      data.push({'#':'','Batch Name':'TOTAL','Year':'','Start Date':'','Enrolled':sum('sTotal'),'Continuing':sum('sCont'),'Passed Out':sum('sPass'),'Other (On Hold etc.)':sum('sOther'),'Actual Fee':tA,'Paid Fee':tP,'Balance Fee':tB});
      saveStyledXlsx('Batch_Wise_Report.xlsx',[{name:'Batch Wise',title:'Batch Wise Fee Report — Armstech Engineers',rows:data}]);
    }
    return (
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:15}}>Batch Wise Fee Report {!paymentsLoaded&&<span style={{fontSize:11,color:'var(--text3)',fontWeight:400}}>⏳ Loading paid/balance...</span>}</div>
          <button className="btn btn-secondary btn-sm" onClick={exportExcel}>&#11015; Export Excel</button>
        </div>
        <div style={{overflowX:'auto'}}>
          <table className="table" style={{fontSize:12,minWidth:1000}}>
            <thead style={{position:'sticky',top:0,zIndex:10,background:'var(--bg)'}}><tr>
              <th style={{width:40,background:'var(--bg)'}}>#</th><th style={{background:'var(--bg)'}}>Batch Name</th><th style={{background:'var(--bg)'}}>Year</th><th style={{background:'var(--bg)'}}>Start Date</th>
              <th style={{textAlign:'right'}} title="Active students in the batch">Enrolled</th>
              <th style={{textAlign:'right'}}>Continuing</th>
              <th style={{textAlign:'right'}}>Passed Out</th>
              <th style={{textAlign:'right'}} title="On Hold / other status">Other</th>
              <th style={{textAlign:'right'}}>Actual Fee</th>
              <th style={{textAlign:'right'}}>Paid Fee</th>
              <th style={{textAlign:'right'}}>Balance Fee</th>
            </tr></thead>
            <tbody>
              {rows.map(r=>(
                <tr key={r.batch_name}>
                  <td>{r.i}</td><td style={{fontWeight:600}}>{r.batch_name}</td><td>{r.year}</td><td>{fmtDate(r.start_date)}</td>
                  <td style={{textAlign:'right',fontWeight:600}}>{loaded?r.sTotal:'...'}</td>
                  <td style={{textAlign:'right'}}>{loaded?r.sCont:'...'}</td>
                  <td style={{textAlign:'right',color:'var(--green)'}}>{loaded?r.sPass:'...'}</td>
                  <td style={{textAlign:'right',color:'var(--text3)'}}>{loaded?r.sOther:'...'}</td>
                  <td style={{textAlign:'right'}}>{INR(r.actualFee)}</td>
                  <td style={{textAlign:'right',color:'var(--green)'}}>{paymentsLoaded?INR(r.paidFee):'...'}</td>
                  <td style={{textAlign:'right',color:r.balance>0?'var(--red)':'var(--text3)'}}>{paymentsLoaded?INR(r.balance):'...'}</td>
                </tr>
              ))}
              <tr style={{fontWeight:700,background:'var(--surface2)'}}>
                <td colSpan={4}>TOTAL</td>
                <td style={{textAlign:'right'}}>{loaded?sum('sTotal'):'...'}</td>
                <td style={{textAlign:'right'}}>{loaded?sum('sCont'):'...'}</td>
                <td style={{textAlign:'right'}}>{loaded?sum('sPass'):'...'}</td>
                <td style={{textAlign:'right'}}>{loaded?sum('sOther'):'...'}</td>
                <td style={{textAlign:'right'}}>{INR(tA)}</td>
                <td style={{textAlign:'right',color:'var(--green)'}}>{paymentsLoaded?INR(tP):'...'}</td>
                <td style={{textAlign:'right',color:'var(--red)'}}>{paymentsLoaded?INR(tB):'...'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function NameCorrectionReport() {
    const [bs, setBs] = React.useState([]);
    const [bsLoading, setBsLoading] = React.useState(false);
    React.useEffect(() => {
      if (!rptBatch) { setBs([]); return; }
      setBsLoading(true);
      supabase.from('students').select('id,student_status,full_name,register_number,course_name,contact_number,whatsapp_number,guardian_name,guardian_contact,college_name,academic_qualification,email,batch_name,is_active')
        .eq('batch_name', rptBatch).eq('is_active', true).order('register_number')
        .then(({data}) => { setBs((data||[]).filter(isReportable)); setBsLoading(false); });
    }, [rptBatch]);
    function exportExcel(){
      if(!bs.length){alert('Select a batch first');return;}
      const data=bs.map((s,i)=>({'#':i+1,'Trainee Name':s.full_name||'','Register No':s.register_number||'','Package':s.course_name||'','Contact No':s.contact_number||'','Guardian Name':s.guardian_name||'','Guardian Phone No':s.guardian_contact||'','College':s.college_name||'','Education':s.academic_qualification||'','Email':s.email||'','Sign':''}));
      saveStyledXlsx(rptBatch+'_Name_List.xlsx',[{name:rptBatch+' Name List',title:'Name List — '+rptBatch,rows:data}]);
    }
    return (
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:15}}>Batch Name List (Name Correction Report)</div>
          <button className="btn btn-secondary btn-sm" onClick={exportExcel} disabled={!bs.length}>&#11015; Export Excel</button>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:12}}>
            <SearchableSelect
            options={batchOpts}
            value={rptBatch}
            onChange={v=>setRptBatch(v)}
            placeholder="-- Select Batch --"
            style={{width:200}}
          />
          {bs.length>0&&<span style={{fontSize:12,color:'var(--text3)'}}>{bs.length} students</span>}
          {bs.length>0&&<button className="btn btn-secondary btn-sm" onClick={()=>printReport('name-list-print',true)}>&#128424; Print PDF (Landscape)</button>}
        </div>
        {rptBatch&&bs.length===0&&<div style={{color:'var(--text3)',fontSize:13}}>No students found in {rptBatch}.</div>}
        {bs.length>0&&(
          <div style={{overflowX:'auto',overflowY:'auto',maxHeight:'calc(100vh - 220px)',border:'1px solid #e2e8f0',borderRadius:6}}>
            <table className="batch-detail-table" style={{fontSize:11,minWidth:1300,borderCollapse:'separate',borderSpacing:0,width:'100%'}} id="name-list-print" style={{borderCollapse:'collapse',border:'1px solid #cbd5e1'}}>
                <thead style={{position:'sticky',top:0,zIndex:100,background:'#f1f5f9'}}><tr style={{background:'#f1f5f9'}}>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',width:36}}>#</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:140}}>Trainee Name</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:90}}>Register No</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:100}}>Package</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:100}}>Contact No</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:130}}>Guardian Name</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:110}}>Guardian Phone No</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:180}}>College</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:80}}>Education</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:160}}>Email</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:80}}>Sign</th>
                </tr></thead>
                <tbody>
                  {bs.map((s,i)=>(
                    <tr key={s.id} style={{background:i%2===0?'#fff':'#f8fafc'}}>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{i+1}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',fontWeight:600}}>{s.full_name}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',fontFamily:'monospace'}}>{s.register_number}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.course_name || '—'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',fontFamily:'monospace'}}>{s.contact_number||'—'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.guardian_name||'—'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',fontFamily:'monospace'}}>{s.guardian_contact||'—'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.college_name||'—'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.academic_qualification||'—'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.email||'—'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}></td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>
        )}
      </div>
    );
  }

  function BatchDetailReport() {
    const [bs, setBs] = React.useState([]);
    const [bsLoading, setBsLoading] = React.useState(false);
    React.useEffect(() => {
      if (!selBatch) { setBs([]); return; }
      setBsLoading(true);
      supabase.from('students').select('id,student_status,full_name,register_number,discipline,centre,contact_number,whatsapp_number,college_name,course_name,training_timing,registration_date,joining_date,batch_name,batch_id,is_active,hostel_needed,nactet_register_number,fee_tuition,fee_manpower,fee_certification,fee_attestation,fee_other,fee_package_change,fee_discount,fee_total')
        .eq('batch_name', selBatch).eq('is_active', true).order('register_number')
        .then(async ({data}) => {
          const list = (data||[]).filter(isReportable);
          try {
            const pays = await fetchByIds('fee_payments', 'student_id,amount', 'student_id', list.map(x=>x.id));
            const pm = {}; pays.forEach(p=>{ pm[p.student_id] = (pm[p.student_id]||0) + Number(p.amount||0); });
            list.forEach(x=>{ x.fee_paid = pm[x.id]||0; });
          } catch (e) { list.forEach(x=>{ x.fee_paid = null; }); }
          setBs(list); setBsLoading(false);
        });
    }, [selBatch]);
    const pkgs = [...new Set(bs.map(s=>s.course_name||'').filter(Boolean))];
    const filtered = selPkg ? bs.filter(s=>(s.course_name||'')===selPkg) : bs;
    function buildRow(s,i){ return {'#':i+1,'Register No':s.register_number||'','Full Name':s.full_name||'','Discipline':s.discipline||'','Contact':s.contact_number||'','WhatsApp':s.whatsapp_number||'','College':s.college_name||'','Package':s.course_name||'','Training Timing':s.training_timing||'','Reg Date':fmtDate(s.registration_date),'Joining Date':fmtDate(s.joining_date),'Hostel':s.hostel_needed?'Yes':'No','NACTET No':s.nactet_register_number||'','Centre':s.centre||'','Tuition Fees':Number(s.fee_tuition||0),'Manpower Fees':Number(s.fee_manpower||0),'Certification Fees':Number(s.fee_certification||0),'Attestation Fees':Number(s.fee_attestation||0),'Other Fees':Number(s.fee_other||0)+Number(s.fee_package_change||0),'Discount':Number(s.fee_discount||0),'Total Fees':Number(s.fee_total||0),'Paid':s.fee_paid==null?'':Number(s.fee_paid),'Balance':s.fee_paid==null?'':Number(s.fee_total||0)-Number(s.fee_paid)}; }
    function exportExcel(){
      if(!filtered.length){alert('Select a batch first');return;}
      const sheets=[{name:selBatch+' All',title:'Student Detail — '+selBatch,rows:filtered.map(buildRow)}];
      if(!selPkg) pkgs.forEach(pkg=>{ const pd=bs.filter(s=>(s.course_name||'')===pkg); if(pd.length) sheets.push({name:pkg.substring(0,28),title:pkg+' — '+selBatch,rows:pd.map(buildRow)}); });
      saveStyledXlsx(selBatch+'_Detail.xlsx',sheets);
    }
    return (
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:15}}>Batch Student Detail Report</div>
          <button className="btn btn-secondary btn-sm" onClick={exportExcel} disabled={!filtered.length}>&#11015; Export Excel</button>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:12}}>
            <SearchableSelect
            options={batchOpts}
            value={selBatch}
            onChange={v=>{setSelBatch(v);setSelPkg('');}}
            placeholder="-- Select Batch --"
            style={{width:200}}
          />
          {pkgs.length>0&&(
            <select style={{width:160,fontSize:12,padding:'4px 6px'}} value={selPkg} onChange={e=>setSelPkg(e.target.value)}>
              <option value="">All Packages ({bs.length})</option>
              {pkgs.map(p=><option key={p} value={p}>{p} ({bs.filter(s=>(s.course_name||'')===p).length})</option>)}
            </select>
          )}
          {filtered.length>0&&<span style={{fontSize:12,color:'var(--text3)'}}>{filtered.length} students</span>}
          {filtered.length>0&&<button className="btn btn-secondary btn-sm" onClick={()=>printReport('detail-print',true)}>&#128424; Print PDF (Landscape)</button>}
        </div>
        {selBatch&&filtered.length===0&&<div style={{color:'var(--text3)',fontSize:13}}>No students found.</div>}
        {filtered.length>0&&(
          <div style={{overflowX:'auto',overflowY:'auto',maxHeight:'60vh',border:'1px solid #e2e8f0',borderRadius:6}}>
            <div id="detail-print">
              <table style={{fontSize:11,minWidth:2300,borderCollapse:'collapse',width:'100%'}}>
                <thead style={{position:'sticky',top:0,zIndex:100,background:'#f1f5f9'}}><tr style={{background:'#f1f5f9'}}>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',width:36,background:'#f1f5f9'}}>#</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:90,background:'#f1f5f9'}}>Register No</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:140}}>Full Name</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:90}}>Discipline</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:100}}>Contact</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:100}}>WhatsApp</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:200}}>College</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:100}}>Package</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:100}}>Training Timing</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:90}}>Reg Date</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:90}}>Joining Date</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:60}}>Hostel</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:100}}>NACTET No</th>
                  <th style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:70}}>Centre</th>
                  {['Tuition','Manpower','Certification','Attestation','Other','Discount','Total Fees','Paid','Balance'].map(h=>(
                    <th key={h} style={{border:'1px solid #cbd5e1',padding:'4px 6px',minWidth:90,textAlign:'right'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filtered.map((s,i)=>(
                    <tr key={s.id} style={{background:i%2===0?'#fff':'#f8fafc'}}>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{i+1}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',fontFamily:'monospace'}}>{s.register_number}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',fontWeight:600}}>{s.full_name}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.discipline||'\u2014'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',fontFamily:'monospace'}}>{s.contact_number||'\u2014'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',fontFamily:'monospace'}}>{s.whatsapp_number||'\u2014'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.college_name||'\u2014'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.course_name || '\u2014'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.training_timing||'\u2014'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{fmtDate(s.registration_date)}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{fmtDate(s.joining_date)}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.hostel_needed?'\u2705':'\u2014'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.nactet_register_number||'\u2014'}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>{s.centre||'\u2014'}</td>
                      {[s.fee_tuition,s.fee_manpower,s.fee_certification,s.fee_attestation,Number(s.fee_other||0)+Number(s.fee_package_change||0),s.fee_discount].map((v,k)=>(
                        <td key={k} style={{border:'1px solid #cbd5e1',padding:'4px 6px',textAlign:'right'}}>{INR(v)}</td>
                      ))}
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',textAlign:'right',fontWeight:700}}>{INR(s.fee_total)}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',textAlign:'right',color:'var(--green)'}}>{s.fee_paid==null?'\u2014':INR(s.fee_paid)}</td>
                      <td style={{border:'1px solid #cbd5e1',padding:'4px 6px',textAlign:'right',color:(Number(s.fee_total||0)-Number(s.fee_paid||0))>0?'var(--red)':'var(--text3)'}}>{s.fee_paid==null?'\u2014':INR(Number(s.fee_total||0)-Number(s.fee_paid||0))}</td>
                    </tr>
                  ))}
                  {filtered.length>0&&(()=>{ const sm=k=>filtered.reduce((t,x)=>t+Number(x[k]||0),0); const paid=filtered.reduce((t,x)=>t+Number(x.fee_paid||0),0); const tot=sm('fee_total'); return (
                    <tr style={{fontWeight:700,background:'#e2e8f0'}}>
                      <td colSpan={14} style={{border:'1px solid #cbd5e1',padding:'4px 6px'}}>TOTAL ({filtered.length} students)</td>
                      {[sm('fee_tuition'),sm('fee_manpower'),sm('fee_certification'),sm('fee_attestation'),sm('fee_other')+sm('fee_package_change'),sm('fee_discount'),tot,paid,tot-paid].map((v,k)=>(
                        <td key={k} style={{border:'1px solid #cbd5e1',padding:'4px 6px',textAlign:'right'}}>{INR(v)}</td>
                      ))}
                    </tr>); })()}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  const REPORTS = [
    { key:'batchwise',      label:'📊 Batch Wise Fee Report' },
    { key:'namecorrection', label:'📋 Batch Name List'       },
    { key:'batchdetail',    label:'📁 Batch Student Detail'  },
    { key:'docstatus',      label:'📋 Document Status Report'  },
    { key:'location',       label:'📍 Location Report'       },
    { key:'outstanding',     label:'💰 Outstanding Balance'    },
    { key:'feecollection',   label:'📅 Fee Collection Summary' },
  ];

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        {REPORTS.map(r=>(
          <button key={r.key}
            className={`btn btn-sm ${activeReport===r.key?'btn-primary':'btn-secondary'}`}
            onClick={()=>setActiveReport(r.key)}
            style={{fontSize:12}}>{r.label}</button>
        ))}
      </div>
      <div style={{background:'var(--surface)',borderRadius:10,padding:16}}>
        {loading
          ? <div style={{textAlign:'center',padding:40,color:'var(--text3)'}}>Loading report data...</div>
          : <>
              {activeReport==='batchwise'      && <BatchWiseReport/>}
              {activeReport==='namecorrection' && <NameCorrectionReport/>}
              {activeReport==='batchdetail'    && <BatchDetailReport/>}
              {activeReport==='docstatus'      && <DocStatusReport batches={batches} students={students} selBatch={selBatch} setSelBatch={setSelBatch} supabase={supabase} onReload={()=>{setLoaded(false); if(onReload) onReload();}} onUpdateStudents={setStudents}/>}
              {activeReport==='location'       && <LocationStudentReport supabase={supabase}/>}
              {activeReport==='outstanding'     && <OutstandingBalanceReport supabase={supabase} batches={orderedBatches}/>}
              {activeReport==='feecollection'   && <FeeCollectionReport supabase={supabase}/>}
            </>
        }
      </div>
    </div>
  );
}


function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  const sorted = [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
  try { window.__batchRank = new Map(sorted.map((b, i) => [b.batch_name, i])); } catch (e) {}
  return sorted;
}
// Order plain batch names the same way as the batch dropdowns (ongoing first, newest first); unknown names go last.
function orderBatchNames(names) {
  const r = window.__batchRank;
  return [...names].sort((a, b) => {
    const pa = r && r.has(a) ? r.get(a) : 1e9, pb = r && r.has(b) ? r.get(b) : 1e9;
    if (pa !== pb) return pa - pb;
    return String(b).localeCompare(String(a), undefined, { numeric: true });
  });
}


// ── Drive Token Expiry Warning (admin only) ──────────────────
function DriveTokenWarning({ userEmail }) {
  const [daysLeft, setDaysLeft] = React.useState(null);
  const ADMIN_EMAIL = 'ershadarmstech@gmail.com';
  
  React.useEffect(() => {
    if (userEmail !== ADMIN_EMAIL) return;
    import('../lib/supabase').then(({ supabase }) => {
      supabase.from('app_config').select('value').eq('key','google_drive_token_refreshed_at').single()
        .then(({ data }) => {
          if (!data?.value) return;
          const refreshedAt = new Date(data.value);
          const expiresAt = new Date(refreshedAt.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days
          const msLeft = expiresAt - new Date();
          const days = Math.floor(msLeft / (1000 * 60 * 60 * 24));
          setDaysLeft(days);
        });
    });
  }, [userEmail]);

  if (userEmail !== ADMIN_EMAIL || daysLeft === null || daysLeft > 3) return null;

  return (
    <div style={{
      background: daysLeft <= 1 ? 'var(--red-light,#fef2f2)' : 'rgba(245,158,11,0.1)',
      border: `1px solid ${daysLeft <= 1 ? 'var(--red,#ef4444)' : 'var(--amber,#f59e0b)'}`,
      borderRadius: 8, padding: '8px 14px', marginBottom: 12,
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'
    }}>
      <span style={{ fontSize: 20 }}>{daysLeft <= 1 ? '🔴' : '🟡'}</span>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, color: daysLeft <= 1 ? 'var(--red)' : 'var(--amber)' }}>
          {daysLeft <= 0 ? '⚠️ Google Drive token EXPIRED!' : `⚠️ Google Drive token expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}!`}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
          Run <strong>refresh_drive_token.bat</strong> on your computer, then update the token in Supabase SQL Editor.
          {daysLeft <= 0 && ' Staff cannot upload/view photos until renewed.'}
        </div>
      </div>
    </div>
  );
}

// ── Audit log (best effort — never blocks the real action) ──────────
const STUDENT_AUDIT_SQL = `create table if not exists students_audit_log (
  id uuid primary key default gen_random_uuid(),
  created_at timestamptz default now(),
  action text, student_id text, student_name text, register_number text, batch_name text,
  detail text, by_email text
);
alter table students_audit_log enable row level security;
create policy "students_audit_all" on students_audit_log for all to authenticated using (true) with check (true);`;
// Who may do what (also enforced in the database by students_permissions.sql)
const OWNER_EMAIL = 'ershadarmstech@gmail.com';
const OFFICE_EMAILS = ['armstechac@gmail.com', 'armstechekm@gmail.com'];
const lcEmail = e => String(e || '').trim().toLowerCase();
async function logStudentAudit(user, action, rows, detailFn) {
  try {
    const list = (Array.isArray(rows) ? rows : [rows]).filter(Boolean);
    if (!list.length) return;
    await supabase.from('students_audit_log').insert(list.map(r => ({
      action, student_id: String(r.id || ''), student_name: r.full_name || null,
      register_number: r.register_number || null, batch_name: r.batch_name || null,
      detail: detailFn ? detailFn(r) : null, by_email: user?.email || null,
    })));
  } catch (e) { /* audit is optional */ }
}

// ── Deleted students (restore) + audit trail ────────────────────────
// Read-only fee installment schedule shown in the student detail window (data comes from Fee Schedules)
function StudentScheduleCard({ studentId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    setRows(null);
    supabase.from('student_fee_schedules').select('id, installment_number, label, amount, paid_amount, status, due_date, late_fee')
      .eq('student_id', studentId).order('installment_number')
      .then(({ data, error }) => { if (alive) setRows(error ? [] : (data || [])); });
    return () => { alive = false; };
  }, [studentId]);
  if (!rows || !rows.length) return null;
  const today = localDate();
  const total = rows.reduce((a, r) => a + Number(r.amount || 0), 0);
  const paid = rows.reduce((a, r) => a + Number(r.paid_amount || 0), 0);
  const money = n => '₹' + Number(n || 0).toLocaleString('en-IN');
  return (
    <>
      <div style={{ marginTop: 16, fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>Installment Schedule</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: 'var(--accent-light)' }}>
            {['#', 'Installment', 'Due', 'Amount', 'Paid', 'Status'].map(h => <th key={h} style={{ padding: '5px 8px', textAlign: h === 'Amount' || h === 'Paid' ? 'right' : 'left' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const overdue = r.status !== 'paid' && r.status !== 'waived' && r.due_date && r.due_date < today;
              return (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '5px 8px', color: 'var(--text3)' }}>{r.installment_number}</td>
                  <td style={{ padding: '5px 8px', fontWeight: 500 }}>{r.label}</td>
                  <td style={{ padding: '5px 8px', color: overdue ? 'var(--red)' : undefined }}>{r.due_date ? new Date(r.due_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right' }}>{money(r.amount)}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--green)' }}>{Number(r.paid_amount) > 0 ? money(r.paid_amount) : '—'}</td>
                  <td style={{ padding: '5px 8px' }}>
                    <span className={`badge ${r.status === 'paid' ? 'badge-green' : r.status === 'waived' ? 'badge-gray' : overdue ? 'badge-red' : r.status === 'partial' ? 'badge-yellow' : 'badge-gray'}`}>
                      {r.status === 'paid' ? 'Paid' : r.status === 'waived' ? 'Waived' : overdue ? 'Overdue' : r.status === 'partial' ? 'Partial' : 'Pending'}
                    </span>
                    {Number(r.late_fee) > 0 && <span style={{ fontSize: 10, color: 'var(--red)', marginLeft: 4 }}>+{money(r.late_fee)} late</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot><tr style={{ fontWeight: 700 }}>
            <td colSpan={3} style={{ padding: '6px 8px' }}>Total</td>
            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{money(total)}</td>
            <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--green)' }}>{money(paid)}</td>
            <td style={{ padding: '6px 8px', color: total - paid > 0 ? 'var(--red)' : 'var(--green)' }}>Balance {money(total - paid)}</td>
          </tr></tfoot>
        </table>
      </div>
    </>
  );
}

function DeletedStudentsModal({ onClose, onRestored, user }) {
  const [tab, setTab] = React.useState('deleted');
  const [rows, setRows] = React.useState([]);
  const [log, setLog] = React.useState([]);
  const [logMissing, setLogMissing] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [q, setQ] = React.useState('');
  const [busyId, setBusyId] = React.useState(null);

  async function loadDeleted() {
    setLoading(true);
    try {
      setRows(await fetchAllPaged(() => supabase.from('students')
        .select('id,full_name,register_number,batch_name,course_name,contact_number,student_status')
        .eq('is_active', false).order('full_name').order('id')));
    } catch (e) { toast.error('Could not load deleted students: ' + e.message); }
    setLoading(false);
  }
  async function loadLog() {
    const { data, error } = await supabase.from('students_audit_log').select('*').order('created_at', { ascending: false }).limit(300);
    if (error) { setLogMissing(true); setLog([]); } else { setLogMissing(false); setLog(data || []); }
  }
  React.useEffect(() => { loadDeleted(); loadLog(); }, []);

  async function restore(r) {
    if (r.register_number) {
      const { data: clash } = await supabase.from('students').select('id,full_name').eq('register_number', r.register_number).eq('is_active', true).limit(1);
      if (clash && clash.length) { toast.error(`Register number ${r.register_number} is now used by ${clash[0].full_name}. Edit one of them first.`); return; }
    }
    if (!window.confirm(`Restore "${r.full_name}"${r.batch_name ? ' (' + r.batch_name + ')' : ''}?`)) return;
    setBusyId(r.id);
    const { error } = await supabase.from('students').update({ is_active: true }).eq('id', r.id);
    setBusyId(null);
    if (error) { toast.error(error.message); return; }
    toast.success('Student restored');
    logStudentAudit(user, 'Restore', r);
    setRows(prev => prev.filter(x => x.id !== r.id));
    onRestored && onRestored();
    loadLog();
  }

  const shown = rows.filter(r => { const t = q.toLowerCase(); return !t || (r.full_name || '').toLowerCase().includes(t) || (r.register_number || '').toLowerCase().includes(t) || (r.batch_name || '').toLowerCase().includes(t) || String(r.contact_number || '').includes(t); });
  return (
    <div className="modal-overlay" style={{zIndex:5000}} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg" style={{ maxWidth: 900, maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <span className="modal-title">🗑 Deleted students & audit trail</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="tabs" style={{ marginBottom: 12 }}>
            <div className={`tab ${tab === 'deleted' ? 'active' : ''}`} onClick={() => setTab('deleted')}>Deleted ({rows.length})</div>
            <div className={`tab ${tab === 'audit' ? 'active' : ''}`} onClick={() => setTab('audit')}>Audit log</div>
          </div>
          {tab === 'deleted' && (
            <>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, reg no, batch, phone…" style={{ marginBottom: 10, width: '100%' }} />
              {loading ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)' }}>Loading…</div>
                : shown.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)' }}>No deleted students.</div>
                : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                      <thead><tr style={{ textAlign: 'left', color: 'var(--text3)' }}>{['Reg No', 'Name', 'Batch', 'Course', 'Phone', 'Status', ''].map(h => <th key={h} style={{ padding: '6px 8px' }}>{h}</th>)}</tr></thead>
                      <tbody>{shown.map(r => (
                        <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.register_number || '—'}</td>
                          <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.full_name}</td>
                          <td style={{ padding: '6px 8px' }}>{r.batch_name || '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{r.course_name || '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{r.contact_number || '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{r.student_status || '—'}</td>
                          <td style={{ padding: '6px 8px' }}><button className="btn btn-primary btn-sm" disabled={busyId === r.id} onClick={() => restore(r)}>↩ Restore</button></td>
                        </tr>))}</tbody>
                    </table>
                  </div>
                )}
            </>
          )}
          {tab === 'audit' && (
            logMissing ? (
              <div style={{ fontSize: 12, color: 'var(--amber)' }}>
                Audit table not set up yet. Run this once in Supabase → SQL Editor (deletes, restores, bulk status changes and transfers are logged after that):
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: 10, background: 'var(--bg3)', padding: 8, borderRadius: 6, marginTop: 6 }}>{STUDENT_AUDIT_SQL}</pre>
              </div>
            ) : log.length === 0 ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)' }}>No audit entries yet.</div>
              : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ textAlign: 'left', color: 'var(--text3)' }}>{['When', 'Action', 'Student', 'Batch', 'Detail', 'By'].map(h => <th key={h} style={{ padding: '6px 8px' }}>{h}</th>)}</tr></thead>
                    <tbody>{log.map(l => (
                      <tr key={l.id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{new Date(l.created_at).toLocaleString('en-IN')}</td>
                        <td style={{ padding: '6px 8px', fontWeight: 600 }}>{l.action}</td>
                        <td style={{ padding: '6px 8px' }}>{l.student_name || '—'}{l.register_number ? ` (${l.register_number})` : ''}</td>
                        <td style={{ padding: '6px 8px' }}>{l.batch_name || '—'}</td>
                        <td style={{ padding: '6px 8px' }}>{l.detail || '—'}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--text3)' }}>{l.by_email || '—'}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
              )
          )}
        </div>
      </div>
    </div>
  );
}

export default function Students() {
  const location = useLocation();
  const { isEditor, isAdmin, canEdit, canView, canEditBatch, assignedBatches, isCoordinator, user } = useAuth();
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const batchStatusRef = React.useRef({});
  const [colleges, setColleges] = useState([]);
  const [courses, setCourses] = useState([]);
  const courseShortMap = React.useMemo(() => {
    const m = {};
    courses.forEach(c => { if (c.course_name) m[c.course_name] = c.course_short_code || c.course_name; });
    return m;
  }, [courses]);
  const [qualOptions, setQualOptions] = useState(['B.Tech-Mechanical','B.Tech-Civil','BE-Mech','BE-Civil','BE-Electrical','BE-Chemical','BE-Computer','Diploma-Mech','Diploma-Civil','Diploma-Electrical','Diploma-Chemical','Diploma-Computer','Diploma-Instrumentation','BSc','BA','BCom','Degree','ITI','Plus Two','SSLC','Other']);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDiscipline, setFilterDiscipline] = useState([]);
  const [filterBatch, setFilterBatch]           = useState([]);
  // 'YYYY-MM' = only students who joined in that month (set from the dashboard "This Month Admissions" card)
  const [filterJoined, setFilterJoined] = useState(() => {
    const m = new URLSearchParams(window.location.search).get('joined');
    return /^\d{4}-\d{2}$/.test(m || '') ? m : '';
  });
  // Apply ?batch= / ?joined= query params from dashboard navigation
  React.useEffect(()=>{
    const params = new URLSearchParams(location.search);
    const batchParam = params.get('batch');
    if (batchParam) setFilterBatch([batchParam]);
    const j = params.get('joined');
    setFilterJoined(/^\d{4}-\d{2}$/.test(j || '') ? j : '');
  },[location.search]);
  const joinedRange = () => {
    if (!filterJoined) return null;
    const [y, m] = filterJoined.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return [`${filterJoined}-01`, `${filterJoined}-${String(last).padStart(2, '0')}`];
  };
  // admissionDatePatched: the month of the joining date, or of the registration date when no joining date is entered (same rule as the dashboard card)
  const applyJoined = (qq) => { const r = joinedRange(); if (!r) return qq; const [y, m] = filterJoined.split('-').map(Number); const nx = `${m === 12 ? y + 1 : y}-${String(m === 12 ? 1 : m + 1).padStart(2, '0')}-01`; return qq.or(`and(joining_date.gte.${r[0]},joining_date.lt.${nx}),and(joining_date.is.null,registration_date.gte.${r[0]},registration_date.lt.${nx})`); };
  const [batchCourses, setBatchCourses]         = useState([]);
  const [batchColleges, setBatchColleges]       = useState([]);
  const [filterPackage, setFilterPackage]       = useState([]);
  const [filterStatus, setFilterStatus]         = useState([]);
  const [filterCollege, setFilterCollege]       = useState([]);
  const [filterYear, setFilterYear]             = useState([]);   // batch start year(s)
  // batches that started in the chosen year(s); combined with any chosen batch
  const yearBatchNames = filterYear.length ? batches.filter(b => b.start_date && filterYear.includes(String(b.start_date).slice(0, 4))).map(b => b.batch_name) : [];
  const effBatch = filterYear.length
    ? ((filterBatch.length ? filterBatch.filter(n => yearBatchNames.includes(n)) : yearBatchNames).length ? (filterBatch.length ? filterBatch.filter(n => yearBatchNames.includes(n)) : yearBatchNames) : ['__no_batch__'])
    : filterBatch;
  const [filterCentre, setFilterCentre]         = useState([]);
  const [filterQual, setFilterQual]             = useState([]);
  const [sortField, setSortField]               = useState('batch_name');
  const [sortDir,   setSortDir]                 = useState('desc');
  // sortKey for select value
  const sortKey = sortField+'|'+sortDir;
  const [filterMode, setFilterMode]             = useState('');
  const [selectedIds,   setSelectedIds]         = useState([]);
  const [bulkStatus,    setBulkStatus]          = useState('');
  const [bulkEndDate,   setBulkEndDate]         = useState('');
  const [showBulkModal, setShowBulkModal]       = useState(false);
  const [mainTab, setMainTab]                   = useState('students');
  // Reports tab state
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailVerif, setDetailVerif] = useState(null);
  useEffect(() => {
    if (!showDetail || !showDetail.id) { setDetailVerif(null); return; }
    let alive = true;
    supabase.from('students').select('*').eq('id', showDetail.id).maybeSingle().then(({ data }) => { if (alive) setDetailVerif(data || null); });
    return () => { alive = false; };
  }, [showDetail && showDetail.id]);
  const [timelineStudent, setTimelineStudent] = useState(null);
  const [showTransfer, setShowTransfer] = useState(null); // student to transfer
  const [transferForm, setTransferForm] = useState({ type:'batch', toBatch:'', toCourse:'', toCentre:'', date:'', reason:'' });
  const [editing, setEditing] = useState(null);
  // permissions: delete + fees = owner and office admins; name / register no / course package lock once both verifications are done (only the owner can still change them)
  const meEmail = lcEmail(user?.email);
  const isOwner = meEmail === OWNER_EMAIL;
  const isOffice = OFFICE_EMAILS.includes(meEmail);
  const canFees = isOwner || isOffice;
  const canDeleteStudent = isOwner || isOffice;
  // staff who are assigned to specific batches can edit only the students of those batches (the owner, office admins and admins are not limited)
  const batchRestricted = !isOwner && !isOffice && !isAdmin && ((assignedBatches || []).length > 0 || !!isCoordinator);
  const canEditStudent = st => canEdit('students') && (!batchRestricted || (typeof canEditBatch === 'function' && !!st && canEditBatch(st.batch_id)));
  const [verif, setVerif] = useState(null);   // verification columns of the student being edited / viewed
  const bothVerified = !!(verif && verif.verified_batch && verif.verified_admin);
  const identityLocked = bothVerified && !isOwner;
  const feesLocked = !!editing && !canFees;
  const [form, setForm] = useState(emptyStudent);
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;

  const [feePayments, setFeePayments] = useState([]);
  // Amount filters on the Total Fees / Paid / Balance column heads: { op, a, b }  (op = lt | lte | gt | gte | eq | between)
  const [amtFilter, setAmtFilter] = useState({ total: null, paid: null, balance: null });
  const [amtPop, setAmtPop] = useState(null);
  const amtCache = React.useRef(null);
  const amtActive = !!(amtFilter.total || amtFilter.paid || amtFilter.balance);
  const AMT_LABEL = { total: 'Total Fees', paid: 'Paid', balance: 'Balance' };
  const AMT_SYM = { lt: '<', lte: '≤', gt: '>', gte: '≥', eq: '=', between: 'between' };
  const amtMatch = (val, f) => {
    if (!f) return true;
    const a = Number(f.a), b = Number(f.b);
    if (f.op === 'lt') return val < a;
    if (f.op === 'lte') return val <= a;
    if (f.op === 'gt') return val > a;
    if (f.op === 'gte') return val >= a;
    if (f.op === 'eq') return Math.round(val) === Math.round(a);
    if (f.op === 'between') return val >= Math.min(a, b) && val <= Math.max(a, b);
    return true;
  };
  const amtText = f => f ? (f.op === 'between' ? `${fmt(f.a)} – ${fmt(f.b)}` : `${AMT_SYM[f.op]} ${fmt(f.a)}`) : '';
  const amtHead = (col) => {
    const f = amtFilter[col];
    return (
      <th style={{ whiteSpace: 'nowrap' }}>
        {AMT_LABEL[col]}
        <button type="button" title={`Filter ${AMT_LABEL[col]}`}
          onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setAmtPop({ col, x: Math.max(8, Math.min(r.left, window.innerWidth - 270)), y: r.bottom + 4, op: f?.op || 'lt', a: f?.a ?? '', b: f?.b ?? '' }); }}
          style={{ marginLeft: 4, padding: '0 4px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10, background: f ? 'var(--accent)' : 'transparent', color: f ? '#fff' : 'var(--text3)' }}>
          {f ? '▼ ' + amtText(f) : '▼'}
        </button>
      </th>
    );
  };
  const [showDeleted, setShowDeleted] = useState(false);
  const debounceRef = React.useRef(null);
  const cpFeesRef = React.useRef({}); // course_name -> course_fees map
  const editingOriginalRef = React.useRef(null); // full DB row of the student being edited (for an accurate change log)

  // Server-side pagination state
  const [totalCount, setTotalCount] = useState(0);
  const [serverPage, setServerPage] = useState(1);
  const SERVER_PER_PAGE = 12;
  const totalPages = Math.ceil(totalCount / SERVER_PER_PAGE) || 1;

  useEffect(() => { loadMeta(); }, []);
  // another staff member added / changed a student or a payment: refresh the list quietly
  useLiveRefresh(['students', 'fee_payments'], () => loadStudentsPage(true), true, 2000);


  // Paid amounts only for the students currently shown (the old global fetch silently stopped at 1000 rows).
  async function loadFeePaymentsFor(rows) {
    try {
      const ids = (rows || []).map(r => r.id);
      if (!ids.length) { setFeePayments([]); return; }
      setFeePayments(await fetchByIds('fee_payments', 'student_id, amount', 'student_id', ids));
    } catch (e) { console.error('fee payments load:', e); }
  }
  useEffect(() => {
    if (filterBatch.length === 0) { setBatchCourses([]); setBatchColleges([]); return; }
    Promise.all([
      supabase.from('students').select('course_name').in('batch_name', filterBatch).eq('is_active', true).not('course_name','is',null),
      supabase.from('students').select('college_name').in('batch_name', filterBatch).eq('is_active', true).not('college_name','is',null),
    ]).then(([cr, col]) => {
      setBatchCourses([...new Set((cr.data||[]).map(r=>r.course_name).filter(Boolean))].sort());
      setBatchColleges([...new Set((col.data||[]).map(r=>r.college_name).filter(Boolean))].sort());
    });
  }, [filterBatch]);
  useEffect(() => {
    // Debounce filter changes to avoid excessive server calls
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadStudentsPage();
    }, search ? 400 : 0); // debounce search typing, instant for dropdown selections
    return () => clearTimeout(debounceRef.current);
  }, [serverPage, search, JSON.stringify(filterDiscipline), JSON.stringify(filterBatch), JSON.stringify(filterYear), batches.length, JSON.stringify(filterPackage), JSON.stringify(filterStatus), JSON.stringify(filterCollege), JSON.stringify(filterCentre), JSON.stringify(filterMode), JSON.stringify(filterQual), filterJoined, sortField, sortDir, JSON.stringify(amtFilter)]);

  async function loadMeta() {
    const [b, col, cp, stf] = await Promise.all([
      supabase.from('batches').select('id, batch_name, discipline, status, start_date, centre, handling_staff_id').order('batch_name'),
      supabase.from('colleges').select('id, college_name, college_short_code'),
      supabase.from('course_packages').select('id, course_name, course_short_code, course_fees'),
      supabase.from('staff').select('id, full_name, email'),
    ]);
    const allBatches = b.data || [];
    const staffMap = {}; const staffEmail = {}; (stf.data||[]).forEach(s=>{ staffMap[s.id]=s.full_name; staffEmail[s.id]=s.email; });
    setBatches(sortBatches(allBatches.map(b=>({...b, coordinator: staffMap[b.handling_staff_id]||null, coordinator_email: staffEmail[b.handling_staff_id]||null}))));
    const _bsm = {}; allBatches.forEach(x => { _bsm[x.batch_name] = x.status || 'completed'; }); batchStatusRef.current = _bsm;
    setColleges((col.data||[]).map(x=>({college_name:x.college_name,id:x.id})));

    // Load distinct course names - try multiple approaches
    let courseNames = [];

    // Approach 1: RPC
    try {
      const { data, error } = await supabase.rpc('get_distinct_course_names');
      if (!error && data?.length) courseNames = data.map(x=>x.course_name).filter(Boolean);
    } catch(e) {}

    // Approach 2: direct paginated query
    if (!courseNames.length) {
      try {
        let all = [], from = 0;
        while(true) {
          const { data } = await supabase.from('students').select('course_name')
            .not('course_name','is',null).neq('course_name','').range(from, from+999);
          if (!data?.length) break;
          all = [...all, ...data];
          if (data.length < 1000) break;
          from += 1000;
        }
        courseNames = [...new Set(all.map(x=>x.course_name).filter(Boolean))].sort();
      } catch(e) {}
    }

    // Approach 3: hardcoded from DB (always works as last resort)
    const cpData = cp.data || [];
    if (!courseNames.length) {
      courseNames = cpData.map(x => x.course_name).sort();
    }

    // Build courses list with fees from course_packages
    const cpFeeMap = {};
    cpData.forEach(x => { cpFeeMap[x.course_name] = { id: x.id, fee: x.course_fees, short_code: x.course_short_code }; });
    cpFeesRef.current = cpFeeMap; // Store IMMEDIATELY for instant lookup
    // Use ALL courses from DB directly (not hardcoded list)
    setCourses(cpData.map(x => ({
      id: x.id,
      course_name: x.course_name,
      course_short_code: x.course_short_code || '',
      course_fees: x.course_fees || 0
    })).sort((a,b) => (a.course_short_code||a.course_name).localeCompare(b.course_short_code||b.course_name)));

    // College list comes directly from the colleges master table (set above via setColleges(col.data...))
    // — always reflects newly added colleges in Master Data immediately, even with 0 students enrolled.

    // Qualification list: Master Data > Qualifications (falls back to the values students already have)
    try {
      const { data: ql, error: qe } = await supabase.from('qualifications').select('name').eq('is_active', true).order('sort_order').order('name');
      if (!qe && ql && ql.length) setQualOptions(ql.map(x => x.name));
      else {
        const { data: qd } = await supabase.from('students')
          .select('academic_qualification').not('academic_qualification','is',null).neq('academic_qualification','').limit(2000);
        const qNames = [...new Set((qd||[]).map(x=>x.academic_qualification).filter(Boolean))].sort();
        const first = ['B.Tech-Mechanical', 'B.Tech-Civil'];
        if (qNames.length) setQualOptions([...first, ...qNames.filter(q => !first.includes(q))]);
      }
    } catch(e) {}
  }


  async function generateReport(format='excel') {
    toast.loading('Generating report...', {id:'report'});
    try {
      // Fetch ALL filtered students (no pagination limit)
      let allData = [];
      if (filterPackage.length > 0) {
        const results = await Promise.all(filterPackage.map(cn => {
          let q = supabase.from('students')
            .select('*').eq('course_name', cn).order(sortField,{ascending:sortDir==='asc'});
          if (filterDiscipline.length) q = q.in('discipline', filterDiscipline.map(v=>v.toLowerCase()));
          if (effBatch.length)      q = q.in('batch_name', effBatch);
          q = applyJoined(q);
          if (filterStatus.length)     q = q.in('student_status', filterStatus);
          if (filterCentre.length)     q = q.in('centre', filterCentre);
          if (filterCollege.length===1) q = q.eq('college_name', filterCollege[0]);
          else if (filterCollege.length>1) q = q.in('college_name', filterCollege);
          q = q.order('id');
          return fetchAllPaged(() => q);
        }));
        allData = results.flat();
        allData = [...new Map(allData.map(s=>[s.id,s])).values()];
      } else {
        let q = supabase.from('students').select('*').order(sortField,{ascending:sortDir==='asc'});
        if (cleanSearch(search)) { const s=cleanSearch(search); q=q.or(`full_name.ilike.%${s}%,register_number.ilike.%${s}%,${collegeLike(s)}`); }
        if (filterDiscipline.length) q = q.in('discipline', filterDiscipline.map(v=>v.toLowerCase()));
        if (effBatch.length)      q = q.in('batch_name', effBatch);
        q = applyJoined(q);
        if (filterStatus.length)     q = q.in('student_status', filterStatus);
        else                         q = q.eq('is_active', true);
        if (filterCentre.length)     q = q.in('centre', filterCentre);
        if (filterMode.length)       q = q.in('training_mode', filterMode);
        if (filterQual.length)       q = q.in('academic_qualification', filterQual);
        if (filterCollege.length===1) q = q.eq('college_name', filterCollege[0]);
        else if (filterCollege.length>1) q = q.in('college_name', filterCollege);
        q = q.order('id');
        allData = await fetchAllPaged(() => q);
      }

      if (format === 'excel') {
        const rows = allData.map((s,i) => ({
          '#': i+1,
          'Reg No': s.register_number||'',
          'Full Name': s.full_name||'',
          'Batch': s.batch_name||'',
          'Course': s.course_name||'',
          'College': s.college_name||'',
          'Discipline': s.discipline||'',
          'Centre': s.centre||'',
          'Status': s.student_status||'Continuing',
          'Discontinue Reason': s.discontinue_reason||'',
          'Qualification': s.academic_qualification||'',
          'Phone': s.contact_number||'',
          'WhatsApp': s.whatsapp_number||'',
          'Email': s.email||'',
          'Joining Date': s.joining_date||'',
          'District': s.district||'',
          'State': s.state||'',
        }));
        const fname = `Students_Report_${localDate()}.xlsx`;
        saveStyledXlsx(fname,[{name:'Students',title:'Student Report — Armstech Engineers',rows}]);
        toast.success(`Exported ${allData.length} students`, {id:'report'});
      } else {
        // PDF via print
        const printWin = window.open('','_blank');
        if (!printWin) { toast.error('Pop-up blocked — allow pop-ups to print', {id:'report'}); return; }
        const esc = v => String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
        const filterDesc = [
          filterYear.length?'Batch year: '+filterYear.join(', '):'',
          filterBatch.length?'Batch: '+filterBatch.join(', '):'',
          filterPackage.length?'Course: '+filterPackage.join(', '):'',
          filterCollege.length?'College: '+filterCollege.join(', '):'',
          filterDiscipline.length?'Discipline: '+filterDiscipline.join(', '):'',
          filterStatus.length?'Status: '+filterStatus.join(', '):'',
          filterCentre.length?'Centre: '+filterCentre.join(', '):'',
          filterQual.length?'Qualification: '+filterQual.join(', '):'',
          search?'Search: '+search:'',
        ].filter(Boolean).join(' | ');
        printWin.document.write(`
          <html><head><title>Student Report</title>
          <style>
            body{font-family:Arial,sans-serif;font-size:11px;margin:20px}
            h2{font-size:16px;margin-bottom:4px}
            .sub{color:#666;font-size:10px;margin-bottom:12px}
            table{width:100%;border-collapse:collapse}
            th{background:#1e4d8c;color:#fff;padding:6px 8px;text-align:left;font-size:10px}
            td{padding:5px 8px;border-bottom:1px solid #e5e7eb;font-size:10px}
            tr:nth-child(even){background:#f8fafc}
            @media print{@page{size:A4 landscape;margin:10mm}}
          </style></head><body>
          <h2>ARMSTECH ERP — Student Report</h2>
          <div class="sub">${filterDesc||'All Students'} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString()} &nbsp;|&nbsp; Total: ${allData.length}</div>
          <table>
            <thead><tr><th>#</th><th>Reg No</th><th>Name</th><th>Batch</th><th>Course</th><th>College</th><th>Disc.</th><th>Centre</th><th>Status</th><th>Phone</th><th>Joining</th></tr></thead>
            <tbody>
              ${allData.map((s,i)=>`<tr>
                <td>${i+1}</td><td>${esc(s.register_number)}</td><td><b>${esc(s.full_name)}</b></td>
                <td>${esc(s.batch_name)}</td><td>${esc(s.course_name)}</td>
                <td>${esc(s.college_name)}</td><td>${esc(s.discipline)}</td><td>${esc(s.centre)}</td>
                <td>${esc(s.student_status||'Continuing')}</td><td>${esc(s.contact_number)}</td>
                <td>${esc(s.joining_date)}</td>
              </tr>`).join('')}
            </tbody>
          </table>
          </body></html>`);
        printWin.document.close();
        printWin.focus();
        setTimeout(()=>printWin.print(), 500);
        toast.success(`Report ready — ${allData.length} students`, {id:'report'});
      }
    } catch(err) {
      toast.error('Report failed: '+err.message, {id:'report'});
    }
  }


  async function saveTransfer() {
    if (!showTransfer) return;
    const { type, toBatch, toCourse, toCentre, date, reason } = transferForm;
    if (type==='batch' && !toBatch) { toast.error('Select new batch'); return; }
    if (type==='course' && !toCourse) { toast.error('Select new course'); return; }
    try {
      if (type === 'centre') {
        if (!toCentre) { toast.error('Select new centre'); setSaving && setSaving(false); return; }
        const { error: cErr } = await supabase.from('students').update({ centre: toCentre }).eq('id', showTransfer.id);
        if (cErr) { toast.error(cErr.message); return; }
        logStudentAudit(user, 'Centre change', showTransfer, r => `${r.centre || '—'} → ${toCentre}`);
        toast.success(`Centre changed to ${toCentre}`);
        setShowTransfer(null);
        setTransferForm({ type:'batch', toBatch:'', toCourse:'', toCentre:'', date:'', reason:'' });
        loadStudentsPage();
        return;
      }
      if (type === 'batch') {
        // Save history
        const { error: hErr } = await supabase.from('student_batch_history').insert({
          student_id: showTransfer.id,
          from_batch: showTransfer.batch_name,
          to_batch: toBatch,
          transfer_date: date || localDate(),
          reason,
        });
        if (hErr) { toast.error('Could not save transfer history: ' + hErr.message); return; }
        // Update student
        const newBatch = batches.find(b=>b.batch_name===toBatch);
        const { error: bErr } = await supabase.from('students').update({ batch_name: toBatch, batch_id: newBatch?.id||showTransfer.batch_id }).eq('id', showTransfer.id);
        if (bErr) { toast.error(bErr.message); return; }
        logStudentAudit(user, 'Batch transfer', showTransfer, r => `${r.batch_name || '—'} → ${toBatch}${reason ? ' (' + reason + ')' : ''}`);
        toast.success(`Transferred to ${toBatch}`);
      } else {
        // Save history
        const { error: chErr } = await supabase.from('student_course_history').insert({
          student_id: showTransfer.id,
          from_course: showTransfer.course_name,
          to_course: toCourse,
          change_date: date || localDate(),
          reason,
        });
        if (chErr) { toast.error('Could not save course history: ' + chErr.message); return; }
        // Update student
        const _tc = courses.find(x=>x.course_name===toCourse);
        const normalizedCourse = (_tc?.course_short_code && _tc.course_short_code!==toCourse) ? _tc.course_short_code : toCourse;
        const { error: cnErr } = await supabase.from('students').update({ course_name: normalizedCourse }).eq('id', showTransfer.id);
        if (cnErr) { toast.error(cnErr.message); return; }
        logStudentAudit(user, 'Course change', showTransfer, r => `${r.course_name || '—'} → ${normalizedCourse}${reason ? ' (' + reason + ')' : ''}`);
        toast.success(`Course changed to ${toCourse}`);
      }
      setShowTransfer(null);
      setTransferForm({ type:'batch', toBatch:'', toCourse:'', toCentre:'', date:'', reason:'' });
      loadStudentsPage();
    } catch(err) { toast.error(err.message); }
  }

  async function loadAll() { await loadStudentsPage(); }

  function toggleFilter(setter, val) {
    setter(prev => prev.includes(val) ? prev.filter(v=>v!==val) : [...prev, val]);
    setServerPage(1);
  }

  // Amount filter mode: Paid/Balance are not stored on the student, so we need every matching student's fee_total and
  // payment total. Only 4 light columns are fetched (in parallel), paid totals come from one RPC when it exists
  // (else parallel paged fetch), and full details are loaded only for the 12 rows on screen. Cached for 3 minutes.
  async function fetchParallel(mk) {
    const first = await mk().range(0, 999);
    if (first.error) throw first.error;
    const out = [...(first.data || [])];
    const total = first.count || out.length;
    if (total > 1000) {
      const jobs = [];
      for (let f = 1000; f < total; f += 1000) jobs.push(mk().range(f, f + 999));
      const res = await Promise.all(jobs);
      res.forEach(r => { if (r.error) throw r.error; out.push(...(r.data || [])); });
    }
    return out;
  }
  async function loadPaidTotals() {
    try {
      const { data, error } = await supabase.rpc('student_paid_totals');
      if (!error && data && typeof data === 'object') return data;     // { student_id: paid }
    } catch (e) {}
    const rows = await fetchParallel(() => supabase.from('fee_payments').select('student_id, amount', { count: 'exact' }).order('id'));
    const m = {}; rows.forEach(p => { m[p.student_id] = (m[p.student_id] || 0) + Number(p.amount || 0); });
    return m;
  }
  async function loadStudentsByAmount() {
    try {
      const COLS = 'id, full_name, register_number, discipline, centre, student_status, course_end_date, discontinue_reason, contact_number, whatsapp_number, email, guardian_name, guardian_contact, training_timing, training_mode, batch_name, batch_id, college_name, course_name, academic_qualification, joining_date, is_active, hostel_needed, fee_total, fee_tuition, fee_attestation, fee_package_change, fee_manpower, fee_certification, fee_other, fee_discount, hostel_fee, change_log, documents_checklist, passport_photos_collected';
      const key = JSON.stringify([search, filterDiscipline, filterBatch, filterYear, batches.length, filterPackage, filterStatus, filterCollege, filterCentre, filterMode, filterQual, filterJoined]);
      let c = amtCache.current;
      if (!c || c.key !== key || Date.now() - c.ts > 180000) {
        const build = (cn) => {
          let q = supabase.from('students').select('id, register_number, batch_name, fee_total', { count: 'exact' }).eq('is_active', true);
          if (cleanSearch(search)) { const t = cleanSearch(search); q = q.or(`full_name.ilike.%${t}%,register_number.ilike.%${t}%,contact_number.ilike.%${t}%,batch_name.ilike.%${t}%,${collegeLike(t)}`); }
          if (cn) q = q.eq('course_name', cn);
          if (filterDiscipline.length) q = q.in('discipline', filterDiscipline.map(v => v.toLowerCase()));
          if (effBatch.length)      q = q.in('batch_name', effBatch);
          q = applyJoined(q);
          if (filterStatus.length)     q = q.in('student_status', filterStatus);
          if (filterCentre.length)     q = q.in('centre', filterCentre);
          if (filterMode.length)       q = q.in('training_mode', filterMode);
          if (filterQual.length)       q = q.in('academic_qualification', filterQual);
          if (filterCollege.length === 1) q = q.eq('college_name', filterCollege[0]);
          else if (filterCollege.length > 1) q = q.in('college_name', filterCollege);
          return q.order('id');
        };
        const [rows0, paidMap] = await Promise.all([
          filterPackage.length ? Promise.all(filterPackage.map(cn => fetchParallel(() => build(cn)))).then(r => r.flat()) : fetchParallel(() => build()),
          loadPaidTotals(),
        ]);
        const rows = [...new Map(rows0.map(r => [r.id, r])).values()];
        c = { key, ts: Date.now(), rows, paidMap };
        amtCache.current = c;
      }
      const hit = c.rows.filter(r => {
        const tot = Number(r.fee_total || 0), paid = Number(c.paidMap[r.id] || 0), bal = Math.max(0, tot - paid);
        return amtMatch(tot, amtFilter.total) && amtMatch(paid, amtFilter.paid) && amtMatch(bal, amtFilter.balance);
      });
      const order = { ongoing: 0, upcoming: 1, completed: 2 };
      hit.sort((a, b) => {
        const sa = order[batchStatusRef.current[a.batch_name]] ?? 2, sb = order[batchStatusRef.current[b.batch_name]] ?? 2;
        if (sa !== sb) return sa - sb;
        const bc = (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
        if (bc !== 0) return bc;
        return (a.register_number || '').localeCompare(b.register_number || '', undefined, { numeric: true });
      });
      const from = (serverPage - 1) * SERVER_PER_PAGE;
      const ids = hit.slice(from, from + SERVER_PER_PAGE).map(r => r.id);
      let slice = [];
      if (ids.length) {
        const { data, error } = await supabase.from('students').select(COLS).in('id', ids);
        if (error) throw error;
        const byId = new Map((data || []).map(r => [r.id, r]));
        slice = ids.map(id => byId.get(id)).filter(Boolean);
      }
      setStudents(slice);
      setFeePayments(slice.map(r => ({ student_id: r.id, amount: c.paidMap[r.id] || 0 })));
      setTotalCount(hit.length);
    } catch (err) {
      console.error('Amount filter error:', err);
      toast.error('Filter error: ' + err.message);
    }
    setLoading(false);
  }

  async function loadStudentsPage(silent) {
    if (silent !== true) setLoading(true);
    if (amtActive) { await loadStudentsByAmount(); return; }
    try {
      const from = (serverPage - 1) * SERVER_PER_PAGE;
      const to   = from + SERVER_PER_PAGE - 1;

      let q = supabase
        .from('students')
        .select('id, full_name, register_number, discipline, centre, student_status, course_end_date, discontinue_reason, contact_number, whatsapp_number, email, guardian_name, guardian_contact, training_timing, training_mode, batch_name, batch_id, college_name, course_name, academic_qualification, joining_date, is_active, hostel_needed, fee_total, fee_tuition, fee_attestation, fee_package_change, fee_manpower, fee_certification, fee_other, fee_discount, hostel_fee, change_log, documents_checklist, passport_photos_collected', { count: 'exact' })
        .eq('is_active', true)
        .order(filterBatch.length>0 ? 'register_number' : sortField, { ascending: filterBatch.length>0 ? true : sortDir==='asc' })
        .order('register_number', { ascending: true })
        .range(from, to);

      if (cleanSearch(search)) {
        const s = cleanSearch(search);
        q = q.or(`full_name.ilike.%${s}%,register_number.ilike.%${s}%,contact_number.ilike.%${s}%,batch_name.ilike.%${s}%,${collegeLike(s)}`);
      }
      if (filterDiscipline.length) q = q.in('discipline', filterDiscipline.map(v=>v.toLowerCase()));
      if (effBatch.length)      q = q.in('batch_name', effBatch);
      q = applyJoined(q);
      if (filterStatus.length)     q = q.in('student_status', filterStatus);
      if (filterCentre.length)     q = q.in('centre', filterCentre);
      if (filterMode.length)       q = q.in('training_mode', filterMode);
      if (filterQual.length)       q = q.in('academic_qualification', filterQual);

      // College filter - use eq for single, multiple passes through client
      if (filterCollege.length === 1) q = q.eq('college_name', filterCollege[0]);
      else if (filterCollege.length > 1) q = q.in('college_name', filterCollege);

      // Course filter - special chars need eq, apply all other filters too
      if (filterPackage.length > 0) {
        const courseResults = await Promise.all(
          filterPackage.map(cn => {
            let cq = supabase.from('students')
              .select('id, full_name, register_number, discipline, centre, student_status, course_end_date, discontinue_reason, contact_number, whatsapp_number, email, guardian_name, guardian_contact, training_timing, training_mode, batch_name, batch_id, college_name, course_name, academic_qualification, joining_date, is_active, hostel_needed, fee_total, fee_tuition, fee_attestation, fee_package_change, fee_manpower, fee_certification, fee_other, fee_discount, hostel_fee, change_log')
              .eq('course_name', cn)
              .eq('is_active', true)
              .order(filterBatch.length > 0 ? 'register_number' : sortField, {ascending: sortDir==='asc'}).limit(2000);
            // Apply all other active filters
            if (cleanSearch(search)) { const s=cleanSearch(search); cq=cq.or(`full_name.ilike.%${s}%,register_number.ilike.%${s}%,${collegeLike(s)}`); }
            if (filterDiscipline.length) cq = cq.in('discipline', filterDiscipline.map(v=>v.toLowerCase()));
            if (effBatch.length)      cq = cq.in('batch_name', effBatch);
            cq = applyJoined(cq);
            if (filterStatus.length)     cq = cq.in('student_status', filterStatus);
            if (filterCentre.length)     cq = cq.in('centre', filterCentre);
            if (filterMode.length)       cq = cq.in('training_mode', filterMode);
            if (filterQual.length)       cq = cq.in('academic_qualification', filterQual);
            if (filterCollege.length===1) cq = cq.eq('college_name', filterCollege[0]);
            else if (filterCollege.length>1) cq = cq.in('college_name', filterCollege);
            return cq;
          })
        );
        const merged = courseResults.flatMap(r => r.data||[]);
        const unique = [...new Map(merged.map(s=>[s.id,s])).values()].sort((a,b) => { const key = filterBatch.length > 0 ? 'register_number' : sortField; const va = String(a[key]||''), vb = String(b[key]||''); return sortDir==='asc' ? va.localeCompare(vb, undefined, {numeric:true}) : vb.localeCompare(va, undefined, {numeric:true}); });

      // Sort: ongoing first → upcoming → completed, then batch_name desc, then register_number asc
      const statusOrder = { ongoing: 0, upcoming: 1, completed: 2 };
      const sortByStatus = (arr) => arr.slice().sort((a, b) => {
        const sa = statusOrder[batchStatusRef.current[a.batch_name]] ?? 2;
        const sb = statusOrder[batchStatusRef.current[b.batch_name]] ?? 2;
        if (sa !== sb) return sa - sb;
        const bc = (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
        if (bc !== 0) return bc;
        return (a.register_number || '').localeCompare(b.register_number || '', undefined, { numeric: true });
      });
        const sortedUnique = sortByStatus(unique);
        setStudents(sortedUnique);
        loadFeePaymentsFor(sortedUnique);
        setTotalCount(unique.length);
        setLoading(false);
        return;
      }

      const { data, count, error } = await q;
      if (error) throw error;

      // Sort: ongoing first → upcoming → completed, then batch_name desc, then register_number asc
      const statusOrder = { ongoing: 0, upcoming: 1, completed: 2 };
      const sortByStatus = (arr) => arr.slice().sort((a, b) => {
        const sa = statusOrder[batchStatusRef.current[a.batch_name]] ?? 2;
        const sb = statusOrder[batchStatusRef.current[b.batch_name]] ?? 2;
        if (sa !== sb) return sa - sb;
        const bc = (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
        if (bc !== 0) return bc;
        return (a.register_number || '').localeCompare(b.register_number || '', undefined, { numeric: true });
      });
      const pageRows = sortByStatus(data || []);
      setStudents(pageRows);
      loadFeePaymentsFor(pageRows);
      setTotalCount(count || 0);
    } catch(err) {
      console.error('Load error:', err);
      toast.error('Filter error: '+err.message);
    }
    setLoading(false);
  }

  async function bulkUpdateStatus() {
    if (!bulkStatus || !selectedIds.length) return;
    let before = [];
    try { before = await fetchByIds('students', 'id,full_name,register_number,batch_name,student_status', 'id', selectedIds); } catch (e) {}
    const { error } = await supabase.from('students')
      .update({ student_status: bulkStatus, course_end_date: bulkStatus === 'Passed Out' ? (bulkEndDate || (() => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); })()) : null })
      .in('id', selectedIds);
    if (error) { toast.error(error.message); return; }
    logStudentAudit(user, 'Bulk status change', before, r => `${r.student_status || 'Continuing'} → ${bulkStatus}`);
    toast.success(`✅ ${selectedIds.length} students marked as ${bulkStatus}`);
    setSelectedIds([]); setBulkStatus(''); setBulkEndDate(''); setShowBulkModal(false); loadStudentsPage();
  }



  function openAdd() { setVerif(null); setForm(emptyStudent); setEditing(null); setActiveTab(0); setShowModal(true); }

  // Auto-generate register number based on batch
  async function generateRegisterNumber(batchId) {
    if (!batchId) return '';
    const batch = batches.find(b => b.id === batchId);
    if (!batch) return '';
    const batchName = batch.batch_name || batch.batch_code || '';
    // Get all register numbers for this batch - check both batch_name and batch_id
    const [byName, byId] = await Promise.all([
      supabase.from('students').select('register_number').eq('batch_name', batchName),
      supabase.from('students').select('register_number').eq('batch_id', batchId),
    ]);
    const allRegs = [...(byName.data||[]), ...(byId.data||[])];
    let maxNum = 0;
    allRegs.forEach(s => {
      if (s.register_number && s.register_number.toUpperCase().startsWith(batchName.toUpperCase())) {
        const numStr = s.register_number.slice(batchName.length);
        const num = parseInt(numStr);
        if (!isNaN(num) && num > maxNum) maxNum = num;
      }
    });
    const next = maxNum + 1;
    const padded = String(next).padStart(3, '0');
    return `${batchName}${padded}`;
  }

  async function openEdit(s) {
    if (!canEditStudent(s)) { toast.error('You can edit only the students of your assigned batches'); return; }
    // Fetch fresh full student data from DB
    const { data: fresh } = await supabase.from('students').select('*').eq('id', s.id).single();
    if (fresh) s = { ...s, ...fresh };
    setVerif(fresh || null);
    editingOriginalRef.current = s;
    // Auto-populate fee from course package if new batch and fee not set
    let autoFee = s.fee_tuition || '';
    if (!autoFee && s.course_name && isNewBatch(s.batch_name)) {
      const cpEntry = cpFeesRef.current[s.course_name];
      if (cpEntry?.fee) autoFee = String(cpEntry.fee);
    }
    setForm({
      ...emptyStudent, ...s,
      register_number: s.register_number || '',
      batch_id: s.batch_id || '',
      college_id: s.college_id || '',
      course_package_id: s.course_package_id || '',
      classplus_installed: s.classplus_installed || false,
      fee_tuition:            autoFee,
      hostel_fee:             s.hostel_fee || '',
      fee_attestation:        s.fee_attestation || '',
      attest_hrd_mea_count:   s.attest_hrd_mea_count || '',
      attest_hrd_mea_amount:  s.attest_hrd_mea_amount || '',
      attest_apostile_count:  s.attest_apostile_count || '',
      attest_apostile_amount: s.attest_apostile_amount || '',
      attest_uae_count:       s.attest_uae_count || '',
      attest_uae_amount:      s.attest_uae_amount || '',
      fee_package_change:     s.fee_package_change || '',
      fee_manpower: s.fee_manpower || '',
      fee_certification: s.fee_certification || '',
      fee_other: s.fee_other || '',
      fee_discount: s.fee_discount || '',
      discount_reason: s.discount_reason || '',
      documents_checklist: {
        id_proof: s.documents_checklist?.id_proof || s.documents_checklist?.aadhar_copy || false,
        passport_copy: s.documents_checklist?.passport_copy || false,
        sslc_certificate: s.documents_checklist?.sslc_certificate || false,
        course_completion_certificate: s.documents_checklist?.course_completion_certificate || false,
        details_verification: s.documents_checklist?.details_verification || false,
        photo_softcopy: s.documents_checklist?.photo_softcopy || false,
        parent_contact_verification: s.documents_checklist?.parent_contact_verification || false,
        nactet_registration: s.documents_checklist?.nactet_registration || false,
      },
      passport_photos_collected: s.passport_photos_collected || '',
      student_status: s.student_status || 'Continuing',
      course_end_date: s.course_end_date || '',
    });

    // If student has no register number yet, auto-generate one
    if (!s.register_number && s.batch_id) {
      const regNo = await generateRegisterNumber(s.batch_id);
      setForm(f => ({ ...f, register_number: regNo }));
    }

    setEditing(s.id);
    setActiveTab(0);
    setShowModal(true);
  }

  // Verification badges (name, register number and course package): one by the batch coordinator, one by the office admin
  const stuBatchEntry = () => batches.find(b => b.batch_name === (form.batch_name || (editingOriginalRef.current && editingOriginalRef.current.batch_name)));
  const canVerifyBatch = isOwner || (!!stuBatchEntry() && lcEmail(stuBatchEntry().coordinator_email) === meEmail);
  const canVerifyAdmin = isOwner || isOffice;
  async function setVerified(kind, on) {
    if (!editing) return;
    if (!on && !isOwner) { toast.error('Only ' + OWNER_EMAIL + ' can remove a verification'); return; }
    const now = new Date().toISOString();
    const cols = kind === 'batch'
      ? { verified_batch: on, verified_batch_by: on ? user?.email : null, verified_batch_at: on ? now : null }
      : { verified_admin: on, verified_admin_by: on ? user?.email : null, verified_admin_at: on ? now : null };
    const { error } = await supabase.from('students').update(cols).eq('id', editing);
    if (error) { toast.error(/column|verified_/.test(error.message) && !/only|Only/.test(error.message) ? 'Run students_permissions.sql in Supabase first (adds the verification columns)' : error.message); return; }
    setVerif(v => ({ ...(v || {}), ...cols }));
    toast.success(on ? (kind === 'batch' ? 'Verified by batch coordinator' : 'Verified by office admin') : 'Verification removed');
    logStudentAudit(user, on ? 'Verify' : 'Unverify', editingOriginalRef.current || { id: editing }, () => (kind === 'batch' ? 'Batch coordinator' : 'Office admin') + ' verification of name, register number and course package');
  }

  async function deleteStudent(id, name) {
    if (!canDeleteStudent) { toast.error('Only ' + OWNER_EMAIL + ' and the office admins can delete a student'); return; }
    let payCount = 0;
    try { const { count } = await supabase.from('fee_payments').select('id', { count: 'exact', head: true }).eq('student_id', id); payCount = count || 0; } catch (e) {}
    const extra = payCount ? `\n\n⚠ This student has ${payCount} fee payment(s) recorded. Fee reports will no longer include them.` : '';
    if (!window.confirm(`Delete student "${name}"?${extra}`)) return;
    const { error } = await supabase.from('students').update({ is_active: false }).eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Student deleted (can be restored from 🗑 Deleted & Audit)');
    logStudentAudit(user, 'Delete', students.find(x => x.id === id) || { id, full_name: name }, () => payCount ? `${payCount} fee payment(s) on record` : null);
    setStudents(prev => prev.filter(s => s.id !== id));
    setServerPage(1);
    setTimeout(() => loadStudentsPage(), 300);
  }

  async function save() {
    if (!form.full_name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);

    // Helper — converts empty string to null for optional fields
    const str  = v => v?.toString().trim() || null;
    const num  = v => v === '' || v === null || v === undefined ? 0 : Number(v) || 0;
    const bool = v => Boolean(v);
    const date = v => v || null;

    const fee_total = calcTotalFees(form);

    // Duplicate register number guard (two staff adding to the same batch at once can get the same number)
    const regNo = (form.register_number || '').toString().trim();
    if (regNo) {
      let dq = supabase.from('students').select('id, full_name').eq('register_number', regNo).eq('is_active', true).limit(1); // REG-ACTIVE-ONLY: deleted students do not block a register number
      if (editing) dq = dq.neq('id', editing);
      const { data: dup } = await dq;
      if (dup && dup.length) {
        toast.error(`Register number ${regNo} is already used by ${dup[0].full_name}. Change it and save again.`);
        setSaving(false); return;
      }
    }

    // Duplicate phone / name warning (new student, or when phone / name was changed while editing)
    {
      const phone10 = (form.contact_number || '').replace(/\D/g, '').slice(-10);
      const nm = form.full_name.trim();
      const orig = editing ? editingOriginalRef.current : null;
      const phoneChanged = phone10.length === 10 && (!orig || (orig.contact_number || '').replace(/\D/g, '').slice(-10) !== phone10);
      const nameChanged = !orig || (orig.full_name || '').trim().toLowerCase() !== nm.toLowerCase();
      if (phoneChanged || nameChanged) {
        const found = new Map();
        try {
          if (phoneChanged) {
            const { data } = await supabase.from('students').select('id,full_name,register_number,batch_name,is_active').ilike('contact_number', '%' + phone10).limit(5);
            (data || []).forEach(r => { if (r.id !== editing) found.set(r.id, { ...r, why: 'same phone number' }); });
          }
          if (nameChanged) {
            const { data } = await supabase.from('students').select('id,full_name,register_number,batch_name,is_active').ilike('full_name', nm.replace(/[\\%_]/g, m => '\\' + m)).limit(5);
            (data || []).forEach(r => { if (r.id !== editing) found.set(r.id, { ...(found.get(r.id) || r), why: found.has(r.id) ? 'same phone number and name' : 'same name' }); });
          }
        } catch (e) { /* duplicate check is best effort */ }
        if (found.size) {
          const lines = [...found.values()].slice(0, 4).map(r => `• ${r.full_name} — ${r.register_number || 'no reg no'}${r.batch_name ? ', ' + r.batch_name : ''}${r.is_active === false ? ' (deleted)' : ''} — ${r.why}`).join('\n');
          if (!window.confirm(`Possible duplicate student:\n\n${lines}\n\nSave anyway?`)) { setSaving(false); return; }
        }
      }
    }

    // Fully explicit payload — NO spread operator
    const payload = {
      register_number:              str(form.register_number),
      centre:                       str(form.centre),
      full_name:                    form.full_name.trim(),
      dob:                          date(form.dob),
      contact_number:               str(form.contact_number),
      whatsapp_number:              str(form.whatsapp_number),
      email:                        str(form.email),
      guardian_name:                str(form.guardian_name),
      guardian_contact:             str(form.guardian_contact),
      place:                        str(form.place),
      pincode:                      str(form.pincode),
      district:                     str(form.district),
      state:                        str(form.state),
      known_location:               str(form.known_location),
      academic_qualification:       str(form.academic_qualification),
      professional_qualification:   str(form.professional_qualification),
      experience:                   str(form.experience),
      registration_date:            date(form.registration_date),
      nactet_register_number:       str(form.nactet_register_number),
      training_timing:              form.training_timing || null,
      training_mode:                form.training_mode || null,
      joining_date:                 date(form.joining_date),
      discipline:                   form.discipline || 'mechanical',
      hostel_needed:                bool(form.hostel_needed),
      hostel_fee:                   num(form.hostel_fee),
      hostel_joining_date:          date(form.hostel_joining_date),
      id_proof_type:                str(form.id_proof_type),
      id_proof_submitted:           bool(form.id_proof_submitted),
      course_certificate_issued:    bool(form.course_certificate_issued),
      classplus_installed:          bool(form.classplus_installed),
      google_review:              bool(form.google_review),
      video_feedback:             bool(form.video_feedback),
      placement_photos_website:   bool(form.placement_photos_website),
      recently_placed_website:    bool(form.recently_placed_website),
      documents_checklist:          form.documents_checklist || {},
      passport_photos_collected:    form.passport_photos_collected ? Number(form.passport_photos_collected) : null,
      student_status:               form.student_status || 'Continuing',
      course_end_date:              form.student_status === 'Passed Out' ? date(form.course_end_date) : null,
      discontinue_reason:           form.student_status === 'Discontinued' ? (form.discontinue_reason||'') : null,
      fee_tuition:                  num(form.fee_tuition),
      hostel_fee:                   num(form.hostel_fee),
      fee_attestation:              num(calcAttestationTotal(form)),
      attest_hrd_mea_count:         num(form.attest_hrd_mea_count),
      attest_hrd_mea_amount:        num(form.attest_hrd_mea_amount),
      attest_apostile_count:        num(form.attest_apostile_count),
      attest_apostile_amount:       num(form.attest_apostile_amount),
      attest_uae_count:             num(form.attest_uae_count),
      attest_uae_amount:            num(form.attest_uae_amount),
      fee_package_change:           num(form.fee_package_change),
      fee_manpower:                 num(form.fee_manpower),
      fee_certification:            num(form.fee_certification),
      fee_other:                    num(form.fee_other),
      fee_discount:                 num(form.fee_discount),
      discount_reason:              form.discount_reason || null,
      fee_total:                    num(fee_total),
      is_active:                    true,
    };

    // FK fields
    if (form.batch_id)          payload.batch_id           = form.batch_id;
    if (form.college_id)        payload.college_id         = form.college_id;
    if (form.course_package_id) payload.course_package_id  = form.course_package_id;
    // Text fields - always include
    payload.batch_name   = form.batch_name   || (batches.find(b=>b.id===form.batch_id)?.batch_name)   || null;
    payload.college_name = form.college_name || (colleges.find(c=>c.id===form.college_id)?.college_name) || null;
    // Normalize course_name to short_code only when safe (short_code is not another package's course_name)
  const _rawCourse = form.course_name || null;
  const _ce = courses.find(x => x.course_name === _rawCourse);
  const _shortCode = _ce?.course_short_code;
  const _isAnotherPkgName = _shortCode && courses.some(x => x.course_name === _shortCode && x.id !== _ce.id);
  payload.course_name = (_shortCode && _shortCode !== _rawCourse && !_isAnotherPkgName) ? _shortCode : _rawCourse;

    // Build change log
    if (editing) {
      const existing = editingOriginalRef.current && editingOriginalRef.current.id === editing ? editingOriginalRef.current : students.find(s => s.id === editing);
      if (existing) {
        const FIELD_LABELS = {
          full_name:'Full Name', contact_number:'Contact', whatsapp_number:'WhatsApp',
          email:'Email', guardian_name:'Guardian Name', guardian_contact:'Guardian Contact',
          dob:'DOB', academic_qualification:'Qualification', college_name:'College',
          course_name:'Course', batch_name:'Batch', joining_date:'Joining Date',
          training_timing:'Timing', training_mode:'Mode', centre:'Centre',
          hostel_needed:'Hostel Needed', fee_total:'Fee Total', fee_discount:'Fee Discount',
        };
        const newLog = [];
        Object.keys(FIELD_LABELS).forEach(k => {
          const oldVal = String(existing[k]||'');
          const newVal = String(payload[k]||'');
          if (oldVal !== newVal) {
            newLog.push({ field: FIELD_LABELS[k], old: oldVal, new: newVal, by: user?.email, at: new Date().toISOString() });
          }
        });
        if (newLog.length > 0) {
          const prevLog = existing.change_log || [];
          payload.change_log = [...prevLog, ...newLog].slice(-50); // keep last 50 changes
        }
      }
    }
    if (editing && identityLocked) {
      // verified by the coordinator and the office admin: name, register number and course package stay as they are
      ['full_name', 'register_number', 'course_package_id', 'course_name'].forEach(k => { delete payload[k]; });
    }
    if (editing && !canFees) {
      // fees can be changed only by the owner and the office admins
      ['fee_tuition', 'hostel_fee', 'fee_attestation', 'attest_hrd_mea_count', 'attest_hrd_mea_amount', 'attest_apostile_count', 'attest_apostile_amount', 'attest_uae_count', 'attest_uae_amount', 'fee_package_change', 'fee_manpower', 'fee_certification', 'fee_other', 'fee_discount', 'discount_reason', 'fee_total'].forEach(k => { delete payload[k]; });
    }
    let savedId = editing;
    if (editing) {
      const { error } = await supabase.from('students').update(payload).eq('id', editing);
      if (error) { toast.error(error.message); setSaving(false); return; }
    } else {
      const { data: inserted, error } = await supabase.from('students').insert(payload).select('id').single();
      if (error) { toast.error(error.message); setSaving(false); return; }
      savedId = inserted?.id;
    }

    toast.success(editing ? 'Student updated' : 'Student added');
    try { sessionStorage.removeItem('armstech_report_students_v3'); sessionStorage.removeItem('armstech_report_students_v3_ts'); } catch(e) {}

    // Auto-generate fee schedule for new student if batch already has schedules
    if (!editing && savedId && payload.course_package_id && payload.batch_name) {
      try {
        // Get other students in this batch to check if schedules exist
        const {data: batchStudents} = await supabase.from('students')
          .select('id').eq('batch_name', payload.batch_name)
          .neq('id', savedId).limit(5);
        const otherIds = (batchStudents||[]).map(s=>s.id);
        if (otherIds.length > 0) {
          const {data: existingScheds} = await supabase.from('student_fee_schedules')
            .select('id').in('student_id', otherIds).limit(1);
          if (existingScheds?.length > 0) {
            // Batch has schedules - generate for new student
            const {data: batch} = await supabase.from('batches')
              .select('*').eq('batch_name', payload.batch_name).single();
            if (batch?.start_date) {
              const {data: plans} = await supabase.from('fee_installment_plans')
                .select('*').eq('package_id', payload.course_package_id)
                .order('installment_number');
              if (plans?.length) {
                let monthlyCount = 0;
                const addDays = (d,n) => new Date(new Date(d).getTime()+n*86400000).toISOString().split('T')[0];
                const rows = plans.map(p => {
                  let dueDate = null;
                  if (p.type==='registration') dueDate = addDays(batch.start_date, 10);
                  else if (p.type==='monthly') { monthlyCount++; dueDate = addDays(batch.start_date, 10+30*monthlyCount); }
                  return { student_id:savedId, plan_id:p.id, installment_number:p.installment_number,
                    label:p.label, amount:p.amount, due_date:dueDate, status:'pending', paid_amount:0 };
                });
                await supabase.from('student_fee_schedules').insert(rows);
                toast.success('Fee schedule auto-generated');
              }
            }
          }
        }
      } catch(e) { console.error('Auto schedule:', e); }
    }

    const existingForLog = editing ? students.find(s=>s.id===editing) : null;
    setShowModal(false);
    loadStudentsPage();
    setSaving(false);
  }

  function docCount(s) {
    const checklist = s.documents_checklist || {};
    const submitted = DOCUMENT_LIST.filter(d => checklist[d.key]).length;
    return `${submitted}/${DOCUMENT_LIST.length}`;
  }

  // Helper to set a single form field
  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function setDocField(k, v) { setForm(f => ({ ...f, documents_checklist: { ...f.documents_checklist, [k]: v } })); }

  // Fee row component for clean rendering
  function FeeRow({ label, fieldKey, isDiscount }) {
    return (
      <div className="form-group">
        <label style={{ color: isDiscount ? 'var(--red)' : undefined }}>{label}</label>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 13 }}>₹</span>
          <input
            key={fieldKey + '_' + (form[fieldKey] === '' ? 'empty' : 'filled')}
            type="number"
            defaultValue={form[fieldKey]}
            onBlur={e => setField(fieldKey, e.target.value)}
            placeholder="0"
            style={{ paddingLeft: 24 }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <DriveTokenWarning userEmail={user?.email||''} />
      <div className="page-header">
        <div>
          <h1 className="page-title">Student profile</h1>
          <p className="page-subtitle">
            <strong style={{color:'var(--accent)'}}>{totalCount}</strong> students
            {filterBatch && (() => {
              const singleBatch = filterBatch.length === 1 ? filterBatch[0] : null;
              const batchObj = singleBatch ? batches.find(b=>b.batch_name===singleBatch) : null;
              const coordinator = batchObj?.coordinator;
              return coordinator ? <span style={{marginLeft:12,fontSize:12,color:'var(--text2)',background:'var(--accent-light)',padding:'2px 8px',borderRadius:4}}>👤 {coordinator}</span> : null;
            })()}
            {totalPages > 1 && <span style={{color:'var(--text3)',marginLeft:8}}>
              — Page {serverPage}/{totalPages}
              <button className="page-btn" style={{padding:'1px 8px',fontSize:11,marginLeft:6}} onClick={()=>setServerPage(p=>Math.max(1,p-1))} disabled={serverPage===1}>←</button>
              <button className="page-btn" style={{padding:'1px 8px',fontSize:11,marginLeft:4}} onClick={()=>setServerPage(p=>Math.min(totalPages,p+1))} disabled={serverPage===totalPages}>→</button>
            </span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ImportButton configKey="students" onImported={()=>loadAll()}/>
          {(canEdit('students')||isCoordinator) && <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Student</button>}
          {canEdit('students') && canDeleteStudent && <button className="btn btn-secondary" onClick={()=>setShowDeleted(true)} title="Restore deleted students / view audit log">🗑 Deleted & Audit</button>}
          <ExportButton
            filename="Students_Export.xlsx"
            label="Export All"
            getData={async () => {
              const all = await fetchAllPaged(() => supabase.from('students').select('*, batches(batch_name), colleges(college_name), course_packages(course_name)').eq('is_active', true).order('full_name').order('id'));
              const buildRow = (s, i) => ({
                'S.No': i+1, 'Register Number': s.register_number||'', 'Full Name': s.full_name||'',
                'Discipline': s.discipline||'', 'Contact': s.contact_number||'', 'WhatsApp': s.whatsapp_number||'',
                'Email': s.email||'', 'Batch': s.batches?.batch_name || s.batch_name ||'', 'College': s.colleges?.college_name || s.college_name ||'',
                'Course Package': s.course_packages?.course_name || s.course_name ||'', 'Training Timing': s.training_timing||'',
                'Joining Date': fmtDate(s.joining_date), 'Hostel': s.hostel_needed?'Yes':'No',
                'Tuition Fees': fmtCurrency(s.fee_tuition), 'Total Fees': fmtCurrency(s.fee_total),
              });
              const batchGroups={}, pkgGroups={};
              (all||[]).forEach(s => {
                const bk = s.batches?.batch_name||'No Batch';
                const pk = s.course_packages?.course_name||'No Package';
                if (!batchGroups[bk]) batchGroups[bk]=[];
                if (!pkgGroups[pk])   pkgGroups[pk]=[];
                batchGroups[bk].push(s);
                pkgGroups[pk].push(s);
              });
              return [
                { name: 'All Students', data: (all||[]).map(buildRow) },
                ...Object.entries(batchGroups).map(([n,stds])=>({ name:`Batch-${n}`.slice(0,31), data:stds.map(buildRow) })),
                ...Object.entries(pkgGroups).map(([n,stds])=>({ name:`Pkg-${n}`.slice(0,31), data:stds.map(buildRow) })),
              ];
            }}
          />
        </div>
      </div>

      {showDeleted && <DeletedStudentsModal user={user} onClose={()=>setShowDeleted(false)} onRestored={()=>loadStudentsPage()} />}

      {/* Main tabs */}
      <div className="tabs">
        {canView('students_list') && <div className={`tab ${mainTab==='students'?'active':''}`} onClick={()=>setMainTab('students')}>
          Students <span style={{marginLeft:5,fontSize:11,background:'var(--surface2)',color:'var(--text3)',padding:'1px 6px',borderRadius:10}}>{students.length}</span>
        </div>}
        {canView('students_reports') && <div className={`tab ${mainTab==='reports'?'active':''}`} onClick={()=>setMainTab('reports')}>
          📊 Reports
        </div>}
      </div>

      {/* ── STUDENTS TAB ── */}
      {mainTab === 'reports' && (
        <StudentReports batches={batches} isActive={mainTab==='reports'} supabase={supabase} onReload={loadAll}/>
      )}

      {mainTab === 'students' && (
        <>
        <div>
          {/* Filters */}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:16}}>
            <div className="search-bar">
              <Search />
              <input placeholder="Search name, reg no, phone, college..." value={search} onChange={e=>{setSearch(e.target.value);setServerPage(1);}} />
            </div>
            <MultiFilter
              label="Discipline"
              options={['mechanical','civil']}
              selected={filterDiscipline}
              onToggle={v=>toggleFilter(setFilterDiscipline,v)}
              onClear={()=>{setFilterDiscipline([]);setServerPage(1);}}
            />
            <MultiFilter
              label="Batch Year"
              options={[...new Set(batches.map(b => b.start_date ? String(b.start_date).slice(0, 4) : '').filter(Boolean))].sort().reverse()}
              selected={filterYear}
              onToggle={v=>{toggleFilter(setFilterYear,v);setServerPage(1);}}
              onClear={()=>{setFilterYear([]);setServerPage(1);}}
            />
            <MultiFilter
              label="Batch"
              options={batches.filter(b=>!filterYear.length||(b.start_date&&filterYear.includes(String(b.start_date).slice(0,4)))).map(b=>b.batch_name)}
              selected={filterBatch}
              onToggle={v=>toggleFilter(setFilterBatch,v)}
              onClear={()=>{setFilterBatch([]);setServerPage(1);}}
            />
            <MultiFilter
              label="Course"
              options={(filterBatch.length>0 ? batchCourses : courses.map(c => c.course_name).sort())}
              selected={filterPackage}
              onToggle={v=>toggleFilter(setFilterPackage,v)}
              onClear={()=>{setFilterPackage([]);setServerPage(1);}}
            />
            <MultiFilter
              label="Status"
              options={['Continuing','Discontinued','Passed Out','Shifted']}
              selected={filterStatus}
              onToggle={v=>toggleFilter(setFilterStatus,v)}
              onClear={()=>{setFilterStatus([]);setServerPage(1);}}
            />
            <MultiFilter
              label="College"
              options={(filterBatch.length>0 ? batchColleges : [...new Set(colleges.map(col=>col.college_name).filter(Boolean))].sort())}
              selected={filterCollege}
              onToggle={v=>toggleFilter(setFilterCollege,v)}
              onClear={()=>{setFilterCollege([]);setServerPage(1);}}
            />
            <MultiFilter
              label="Centre"
              options={['EKM','TVM']}
              selected={filterCentre}
              onToggle={v=>toggleFilter(setFilterCentre,v)}
              onClear={()=>{setFilterCentre([]);setServerPage(1);}}
            />
            <MultiFilter
              label="Mode"
              options={['Offline','Online']}
              selected={filterMode}
              onToggle={v=>toggleFilter(setFilterMode,v)}
              onClear={()=>{setFilterMode([]);setServerPage(1);}}
            />
            <MultiFilter
              label="Qualification"
              options={qualOptions}
              selected={filterQual}
              onToggle={v=>toggleFilter(setFilterQual,v)}
              onClear={()=>{setFilterQual([]);setServerPage(1);}}
            />
            <select value={sortKey} onChange={e=>{const[f,...rest]=e.target.value.split('|');setSortField(f);setSortDir(rest[0]);setServerPage(1);}} style={{fontSize:12,padding:'4px 8px',borderRadius:6,border:'1px solid var(--border)'}}>
              <option value="full_name|asc">Name A→Z</option>
              <option value="full_name|desc">Name Z→A</option>
              <option value="joining_date|desc">Joined (Newest)</option>
              <option value="joining_date|asc">Joined (Oldest)</option>
              <option value="batch_name|asc">Batch A→Z</option>
              <option value="register_number|asc">Reg No A→Z</option>
            </select>
            {(filterDiscipline.length||filterBatch.length||filterYear.length||filterPackage.length||filterStatus.length||filterCollege.length||filterCentre.length||filterMode.length||filterQual.length||filterJoined||search) && (<>
              <button className="btn btn-ghost btn-sm" onClick={()=>{setSearch('');setFilterDiscipline([]);setFilterBatch([]);setFilterYear([]);setFilterPackage([]);setFilterStatus([]);setFilterCollege([]);setFilterCentre([]);setFilterMode([]);setFilterQual([]);setFilterJoined('');setServerPage(1);}}>✕ Clear</button>
              {filterJoined && <span className="badge badge-amber" style={{fontSize:11}}>Admitted {new Date(Number(filterJoined.slice(0,4)), Number(filterJoined.slice(5,7))-1, 1).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}</span>}
              <button className="btn btn-secondary btn-sm" onClick={()=>generateReport('excel')} style={{display:'flex',alignItems:'center',gap:4,fontSize:11}}><Download size={12}/> Excel</button>
              <button className="btn btn-secondary btn-sm" onClick={()=>generateReport('pdf')} style={{display:'flex',alignItems:'center',gap:4,fontSize:11}}><FileText size={12}/> PDF/Print</button>
            </>)}
          </div>

          {/* Student table */}
          {loading ? <div className="loading"><div className="spinner" /></div> : (
            <div>
              {selectedIds.length > 0 && (
                <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'#eff6ff',border:'1px solid #93c5fd',borderRadius:10,marginBottom:10,flexWrap:'wrap'}}>
                  <span style={{fontWeight:700,fontSize:13,color:'#1d4ed8'}}>✅ {selectedIds.length} student{selectedIds.length>1?'s':''} selected</span>
                  <select value={bulkStatus} onChange={e=>setBulkStatus(e.target.value)} style={{fontSize:12,padding:'4px 8px',borderRadius:6}}>
                    <option value="">— Set Status —</option>
                    {['Continuing','Passed Out','Discontinued','Shifted','On Hold'].map(st=><option key={st} value={st}>{st}</option>)}
                  </select>
                  <button className="btn btn-primary btn-sm" disabled={!bulkStatus} onClick={()=>setShowBulkModal(true)}>Apply to Selected</button>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setSelectedIds([]);setBulkStatus('');}}>✕ Clear</button>
                </div>
              )}
              {showBulkModal && (
                <div className="modal-overlay" style={{zIndex:5000}} onClick={e=>e.target===e.currentTarget&&setShowBulkModal(false)}>
                  <div className="modal" style={{maxWidth:400}}>
                    <div className="modal-header"><span className="modal-title">⚠️ Bulk Status Update</span></div>
                    <div className="modal-body">
                      <p style={{marginBottom:12}}>Mark <strong>{selectedIds.length} student{selectedIds.length>1?'s':''}</strong> as:</p>
                      <div style={{background:'var(--accent-light)',borderRadius:8,padding:'10px 14px',fontWeight:700,fontSize:16,color:'var(--accent)',textAlign:'center',marginBottom:12}}>{bulkStatus}</div>
                      {bulkStatus === 'Passed Out' && (
                        <div style={{marginBottom:12}}>
                          <label style={{fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>Course completed on</label>
                          <DateInput value={bulkEndDate} onChange={e => setBulkEndDate(e.target.value)} />
                          <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>Leave empty to use today. Attendance is taken up to and including this date.</div>
                        </div>
                      )}
                      <p style={{fontSize:12,color:'var(--text3)'}}>This will update all selected students. Continue?</p>
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-secondary" onClick={()=>setShowBulkModal(false)}>Cancel</button>
                      <button className="btn btn-primary" onClick={bulkUpdateStatus}>✅ Confirm</button>
                    </div>
                  </div>
                </div>
              )}
              <div style={{position:'relative'}}>
                {/* Top scroll mirror - shows horizontal scrollbar at top */}
                <style>{`
                  .bottom-scroll-mirror::-webkit-scrollbar { height: 18px; }
                  .bottom-scroll-mirror::-webkit-scrollbar-track { background: var(--bg2); }
                  .bottom-scroll-mirror::-webkit-scrollbar-thumb { background: #15803d; border-radius: 8px; border: 3px solid var(--bg2); }
                  .bottom-scroll-mirror::-webkit-scrollbar-thumb:hover { background: #166534; }
                  .bottom-scroll-mirror { scrollbar-color: #15803d var(--bg2); scrollbar-width: auto; }
                `}</style>
                <div id="top-scroll-mirror" className="bottom-scroll-mirror" style={{overflowX:'auto',height:20,marginBottom:2,position:'fixed',bottom:0,background:'var(--surface)',zIndex:50,borderTop:'1px solid var(--border)',display:'none'}}
                  onScroll={e=>{if(window.__scrollingSrc==='main')return;window.__scrollingSrc='mirror';const tw=document.getElementById('main-table-wrap');if(tw)tw.scrollLeft=e.target.scrollLeft;requestAnimationFrame(()=>{window.__scrollingSrc=null;});}}>
                  <div style={{width:'max-content',minWidth:'100%',height:1}} id="top-scroll-inner"/>
                </div>
                
                {amtActive && (
                  <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:6,margin:'6px 0',fontSize:12}}>
                    <span style={{color:'var(--text3)'}}>Amount filter:</span>
                    {['total','paid','balance'].filter(c=>amtFilter[c]).map(c=>(
                      <span key={c} style={{background:'var(--accent-light)',border:'1px solid var(--accent)',borderRadius:12,padding:'2px 10px',fontWeight:600}}>
                        {AMT_LABEL[c]} {amtText(amtFilter[c])}
                        <button type="button" title="Remove" style={{border:'none',background:'none',cursor:'pointer',marginLeft:4,color:'var(--red)'}}
                          onClick={()=>{ setAmtFilter(p=>({...p,[c]:null})); setServerPage(1); }}>✕</button>
                      </span>
                    ))}
                    <button type="button" className="btn btn-ghost btn-sm" onClick={()=>{ setAmtFilter({total:null,paid:null,balance:null}); setServerPage(1); }}>Clear all</button>
                  </div>
                )}
                {amtPop && (
                  <>
                    <div style={{position:'fixed',inset:0,zIndex:9998}} onClick={()=>setAmtPop(null)}/>
                    <div style={{position:'fixed',left:amtPop.x,top:amtPop.y,zIndex:9999,width:250,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:8,boxShadow:'0 8px 24px rgba(0,0,0,.25)',padding:12}}>
                      <div style={{fontWeight:700,fontSize:13,marginBottom:8}}>Filter: {AMT_LABEL[amtPop.col]}</div>
                      <select className="form-control" value={amtPop.op} onChange={e=>setAmtPop(p=>({...p,op:e.target.value}))} style={{marginBottom:8}}>
                        <option value="lt">Less than</option>
                        <option value="lte">Less than or equal to</option>
                        <option value="gt">Greater than</option>
                        <option value="gte">Greater than or equal to</option>
                        <option value="eq">Equal to</option>
                        <option value="between">Between</option>
                      </select>
                      <input className="form-control" type="number" min="0" autoFocus placeholder={amtPop.op==='between'?'From amount (₹)':'Amount (₹)'} value={amtPop.a}
                        onChange={e=>setAmtPop(p=>({...p,a:e.target.value}))} style={{marginBottom:8}}/>
                      {amtPop.op==='between' && (
                        <input className="form-control" type="number" min="0" placeholder="To amount (₹)" value={amtPop.b}
                          onChange={e=>setAmtPop(p=>({...p,b:e.target.value}))} style={{marginBottom:8}}/>
                      )}
                      <div style={{display:'flex',gap:6,justifyContent:'flex-end'}}>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={()=>{ setAmtFilter(p=>({...p,[amtPop.col]:null})); setServerPage(1); setAmtPop(null); }}>Clear</button>
                        <button type="button" className="btn btn-primary btn-sm"
                          disabled={amtPop.a===''||(amtPop.op==='between'&&amtPop.b==='')}
                          onClick={()=>{ setAmtFilter(p=>({...p,[amtPop.col]:{op:amtPop.op,a:amtPop.a,b:amtPop.b}})); setServerPage(1); setAmtPop(null); }}>Apply</button>
                      </div>
                    </div>
                  </>
                )}
                {(() => {
                  window.__stickyCol = () => ({});   // no pinned columns any more
                  return null;
                })()}
                <div className="table-wrap" id="main-table-wrap"
                  onScroll={e=>{if(window.__scrollingSrc==='mirror')return;window.__scrollingSrc='main';const m=document.getElementById('top-scroll-mirror');if(m)m.scrollLeft=e.target.scrollLeft;requestAnimationFrame(()=>{window.__scrollingSrc=null;});}}>
                  {/* Sync width of top mirror */}
                  {typeof window!=='undefined' && setTimeout(()=>{const tw=document.getElementById('main-table-wrap');const inner=document.getElementById('top-scroll-inner');if(tw&&inner)inner.style.width=tw.scrollWidth+'px';},100) && null}
                <table>
                  <thead style={{position:'sticky',top:0,zIndex:5}}>
                    <tr>
                      <th style={{...window.__stickyCol(0, 'var(--bg2)'), zIndex:3}}>
                        <input type="checkbox" title="Select all filtered"
                          checked={selectedIds.length===students.length&&students.length>0}
                          onChange={e=>setSelectedIds(e.target.checked?students.map(s=>s.id):[])}/>
                      </th>
                      <th style={{...window.__stickyCol(1, 'var(--bg2)'), zIndex:3}}>Reg No.</th>
                      <th style={{...window.__stickyCol(2, 'var(--bg2)'), zIndex:3}}>Student Name</th>
                      <th>Batch</th>
                      <th>Course Package</th>
                      <th>Contact</th>
                      <th>College</th>{amtHead('total')}{amtHead('paid')}{amtHead('balance')}<th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.length === 0 ? (
                      <tr><td colSpan={11}><div className="empty-state"><p>No students found</p></div></td></tr>
                    ) : students.map(s => {
                      const paid = feePayments.filter(p => p.student_id === s.id).reduce((a, p) => a + Number(p.amount || 0), 0);
                      const balance = Math.max(0, Number(s.fee_total || 0) - paid);
                      return (
                      <tr key={s.id} title="Click to open the student profile" style={{background:selectedIds.includes(s.id)?'#f0fdf4':'',cursor:'pointer'}}
                        onClick={e=>{ if (e.target.closest && e.target.closest('button, a, input, select, textarea, label')) return; if (window.getSelection && String(window.getSelection()).length > 0) return; setShowDetail(s); }}>
                        <td style={{...window.__stickyCol(0, selectedIds.includes(s.id)?'#f0fdf4':undefined), textAlign:'center'}}>
                          <input type="checkbox"
                            checked={selectedIds.includes(s.id)}
                            onChange={e=>setSelectedIds(prev=>e.target.checked?[...prev,s.id]:prev.filter(x=>x!==s.id))}/>
                        </td>
                        <td style={window.__stickyCol(1, selectedIds.includes(s.id)?'#f0fdf4':undefined)}><span style={{fontFamily:'monospace',fontSize:12,color:'var(--accent)'}}>{s.register_number || '—'}</span></td>
                        <td style={{...window.__stickyCol(2, selectedIds.includes(s.id)?'#f0fdf4':undefined), fontWeight:500}}>{s.full_name}
                            {s.student_status && s.student_status !== 'Continuing' && <div style={{fontSize:10,fontWeight:600,color:s.student_status==='Discontinued'?'var(--red)':'var(--text3)'}} title={s.student_status==='Discontinued'&&s.discontinue_reason?'Reason: '+s.discontinue_reason:''}>{s.student_status}</div>}
                          </td>
                        <td style={{fontSize:12}}>
                          <div>{s.batches?.batch_name || s.batch_name || '—'}</div>
                          {(()=>{ const b=batches.find(b=>b.id===s.batch_id||b.batch_name===s.batch_name); return b?.start_date?<div style={{fontSize:10,color:'var(--text3)'}}>{new Date(b.start_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>:null; })()}
                        </td>
                        <td style={{fontSize:11}}>{courseShortCode(s.course_name) || '—'}</td>
                        <td style={{fontSize:12}}>
                          <div style={{display:'flex',alignItems:'center',gap:4}}>
                            <span style={{fontFamily:'monospace'}}>{s.contact_number||'—'}</span>
                            {s.contact_number&&<CallBtn phone={s.contact_number}/>}
                            {s.whatsapp_number&&<WABtn phone={s.whatsapp_number}/>}
                          </div>
                        </td>
                        <td style={{fontSize:12}}>{s.colleges?.college_short_code || s.colleges?.college_name || s.college_name || '—'}</td>
                        <td style={{fontSize:12,fontWeight:600}}>{s.fee_total ? fmt(s.fee_total) : '—'}</td>
                        <td style={{fontSize:12,color:'var(--green)',fontWeight:600}}>{paid > 0 ? fmt(paid) : '—'}</td>
                        <td style={{fontSize:12,color:balance > 0 ? 'var(--red)' : 'var(--green)',fontWeight:600}}>{fmt(balance)}</td>
                        <td style={{whiteSpace:'nowrap'}}>
                          <div style={{display:'flex',gap:4}}>
                            <button className="btn btn-ghost btn-sm" onClick={()=>setShowDetail(s)} title="View"><Eye size={13} /></button>
                            <button className="btn btn-ghost btn-sm" onClick={()=>setTimelineStudent(s)} title="Placement Timeline"><Clock size={13} /></button>
                            {canEditStudent(s) && <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(s)} title="Edit"><Edit2 size={13} /></button>}
                            {canEdit('students') && canDeleteStudent && <button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>deleteStudent(s.id,s.full_name)} title="Delete"><Trash2 size={13} /></button>}
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>{/* end table-wrap */}
              </div>{/* end scroll-wrapper */}
              {totalPages > 1 && (
                <div className="pagination" style={{marginTop:8}}>
                  <button className="page-btn" onClick={()=>{setServerPage(p=>Math.max(1,p-1));window.scrollTo(0,0);}} disabled={serverPage===1}>←</button>
                  {Array.from({length:Math.min(5,totalPages)},(_,i)=>{
                    const p = serverPage<=3?i+1:serverPage-2+i;
                    if(p<1||p>totalPages) return null;
                    return <button key={p} className={`page-btn ${p===serverPage?'active':''}`} onClick={()=>{setServerPage(p);window.scrollTo(0,0);}}>{p}</button>;
                  })}
                  <button className="page-btn" onClick={()=>{setServerPage(p=>Math.min(totalPages,p+1));window.scrollTo(0,0);}} disabled={serverPage===totalPages}>→</button>
                  <span style={{color:'var(--text3)',fontSize:12,marginLeft:8}}>{serverPage} / {totalPages} &nbsp;({totalCount} total)</span>
                </div>
              )}
            </div>
          )}
        </div>
        </>
      )}

      {showModal && (
        <div className="modal-overlay" style={{zIndex:5000}} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal modal-xl">
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit Student' : 'Add New Student'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="tabs">
                {TABS.map((t, i) => (
                  <div key={t} className={`tab ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{t}</div>
                ))}
              </div>

              {/* TAB 0 — PERSONAL */}
              {activeTab === 0 && (
                <div>
                  {/* Passport photo — shown only when editing existing student */}
                  {editing && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                      <PassportPhotoUpload student={students.find(s => s.id === editing)} />
                    </div>
                  )}
                  {/* Details verification: name, register number, course package */}
                  {editing && (() => {
                    const row = (label, on, by, at, canDo, kind) => (
                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',padding:'4px 0',fontSize:12}}>
                        <span style={{padding:'2px 8px',borderRadius:10,fontWeight:700,fontSize:11,background:on?'#dcfce7':'#fef3c7',color:on?'#166534':'#92400e',border:'1px solid '+(on?'#86efac':'#fcd34d')}}>{on?'✅ ':'⏳ '}{label}</span>
                        {on ? <span style={{color:'var(--text3)'}}>{String(by||'').split('@')[0]}{at?' · '+new Date(at).toLocaleDateString('en-IN'):''}</span> : <span style={{color:'var(--text3)'}}>not verified</span>}
                        {!on && canDo && <button type="button" className="btn btn-primary btn-sm" onClick={()=>setVerified(kind,true)}>Verify</button>}
                        {on && isOwner && <button type="button" className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>{ if(window.confirm('Remove this verification?')) setVerified(kind,false); }}>Remove</button>}
                      </div>);
                    return (
                      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 12px',marginBottom:14}}>
                        <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.6px'}}>Details verification (name, register number, course package)</div>
                        {row('Batch coordinator', !!(verif&&verif.verified_batch), verif&&verif.verified_batch_by, verif&&verif.verified_batch_at, canVerifyBatch, 'batch')}
                        {row('Office admin', !!(verif&&verif.verified_admin), verif&&verif.verified_admin_by, verif&&verif.verified_admin_at, canVerifyAdmin, 'admin')}
                        {bothVerified && <div style={{fontSize:11,color:'var(--text2)',marginTop:4}}>🔒 Fully verified - name, register number and course package can now be changed only by {OWNER_EMAIL}.</div>}
                      </div>);
                  })()}
                  <div className="form-grid">
                  <div className="form-group"><label>Full Name *</label><input disabled={identityLocked} title={identityLocked?'Locked after verification':''} value={form.full_name} onChange={e => setField('full_name', e.target.value)} placeholder="Trainee full name" /></div>
                  <div className="form-group"><label>Date of Birth</label><DateInput value={form.dob} onChange={e => setField('dob', e.target.value)} /></div>
                  <div className="form-group"><label>Contact Number</label><input value={form.contact_number} onChange={e => setField('contact_number', e.target.value)} /></div>
                  <div className="form-group"><label>WhatsApp Number</label><input value={form.whatsapp_number} onChange={e => setField('whatsapp_number', e.target.value)} /></div>
                  <div className="form-group"><label>Email ID</label><input type="email" value={form.email} onChange={e => setField('email', e.target.value)} /></div>
                  <div className="form-group"><label>Guardian Name</label><input value={form.guardian_name} onChange={e => setField('guardian_name', e.target.value)} /></div>
                  <div className="form-group"><label>Guardian Contact</label><input value={form.guardian_contact} onChange={e => setField('guardian_contact', e.target.value)} /></div>
                  <div className="form-group full"><label>Address</label><textarea value={form.place} onChange={e => setField('place', e.target.value)} placeholder="Enter full address..." rows={3} style={{resize:'vertical',width:'100%'}}/></div>
                  <div className="form-group"><label>Pincode</label><input value={form.pincode} onChange={e => setField('pincode', e.target.value)} /></div>
                  <div className="form-group"><label>State</label>
                    <select value={form.state||''} onChange={e=>setField('state',e.target.value)}>
                      <option value=''>Select State</option>
                      {['Kerala','Tamil Nadu','Karnataka','Andhra Pradesh','Telangana','Maharashtra','Goa','Other'].map(s=><option key={s} value={s}>{s}</option>)}
                    </select></div>
                  <div className="form-group"><label>District</label>
                    <select value={form.district||''} onChange={e=>setField('district',e.target.value)}>
                      <option value=''>Select District</option>
                      {['Thiruvananthapuram','Kollam','Pathanamthitta','Alappuzha','Kottayam','Idukki','Ernakulam','Thrissur','Palakkad','Malappuram','Kozhikode','Wayanad','Kannur','Kasaragod','Other'].map(d=><option key={d} value={d}>{d}</option>)}
                    </select>
                    {form.district==='Other' && <input value={form.district_other||''} onChange={e=>setField('district_other',e.target.value)} placeholder='Enter district name' style={{marginTop:4}}/>}
                  </div>
                  <div className="form-group"><label>Known Location</label>
                    <input value={form.known_location||''} onChange={e=>setField('known_location',e.target.value)}
                      placeholder='Type to search location...' list='known-loc-list'/>
                    <datalist id='known-loc-list'>
                      {KNOWN_LOCATIONS.filter(l=>!form.known_location||l.toLowerCase().includes((form.known_location||'').toLowerCase())).map(l=><option key={l} value={l}/>)}
                    </datalist>
                  </div>
                  <div className="form-group">
                    <label>Student Status</label>
                    <select value={form.student_status || 'Continuing'} onChange={e => {
                      const v = e.target.value;
                      setField('student_status', v);
                      if (v === 'Passed Out' && !form.course_end_date) { const d = new Date(); setField('course_end_date', d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')); }
                    }}>
                      <option value="Continuing">Continuing</option>
                      <option value="Discontinued">Discontinued</option>
                      <option value="Shifted">Shifted</option>
                      <option value="Passed Out">Passed Out</option>
                    </select>
                  </div>
                  {(form.student_status === 'Passed Out') && (
                    <div className="form-group">
                      <label>Course completed on</label>
                      <DateInput value={form.course_end_date} onChange={e => setField('course_end_date', e.target.value)} />
                      <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>Attendance is taken up to and including this date</div>
                    </div>
                  )}
                  {(form.student_status === 'Discontinued') && (
                    <div className="form-group" style={{gridColumn:'1/-1'}}>
                      <label>Reason for Discontinuation <span style={{color:'var(--red)'}}>*</span></label>
                      <textarea
                        value={form.discontinue_reason || ''}
                        onChange={e => setField('discontinue_reason', e.target.value)}
                        placeholder="Enter reason for discontinuation..."
                        rows={3}
                        style={{width:'100%',resize:'vertical'}}
                      />
                    </div>
                  )}
                </div>
                </div>
              )}

              {/* TAB 1 — ACADEMIC */}
              {activeTab === 1 && (
                <div className="form-grid">
                  <div className="form-group full"><label>Academic Qualification</label>
                    <SearchableSelect
                      options={[...new Set([form.academic_qualification, ...qualOptions].filter(Boolean))].map(q=>({value:q,label:q}))}
                      value={form.academic_qualification||''}
                      onChange={v=>setField('academic_qualification',v)}
                      placeholder="Select qualification..."
                      searchPlaceholder="Search qualification..."
                    />
                  </div>
                  <div className="form-group full"><label>Professional Qualification (if any)</label><input value={form.professional_qualification} onChange={e => setField('professional_qualification', e.target.value)} /></div>
                  <div className="form-group full"><label>Experience (if any)</label><textarea value={form.experience} onChange={e => setField('experience', e.target.value)} placeholder="Company, Role, Duration..." /></div>
                </div>
              )}

              {/* TAB 2 — ENROLLMENT */}
              {activeTab === 2 && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Discipline *</label>
                    <select value={form.discipline} onChange={e => setField('discipline', e.target.value)}>
                      <option value="mechanical">Mechanical</option>
                      <option value="civil">Civil</option>
                    </select>
                  </div>
                  <div className="form-group"><label>Registration Date</label><DateInput value={form.registration_date} onChange={e => setField('registration_date', e.target.value)} /></div>
                  <div className="form-group"><label>Joining Date</label><DateInput value={form.joining_date} onChange={e => setField('joining_date', e.target.value)} /></div>

                  {/* Batch — SearchableSelect with auto register number */}
                  <div className="form-group">
                    <label>Batch</label>
                    <SearchableSelect
                      options={batches.map(b => ({ value: b.batch_name, label: b.batch_name }))}
                      value={form.batches?.batch_name || form.batch_name || form.batch_id || ''}
                      onChange={async v => {
                        // Try to find batch by name and get its id
                        const batch = batches.find(b => b.batch_name === v || b.id === v);
                        setForm(f => ({ ...f, batch_id: batch?.id || '', batch_name: v }));
                        if (!editing && batch?.id) {
                          const regNo = await generateRegisterNumber(batch.id);
                          setForm(f => {
                            const updates = { batch_id: batch.id, batch_name: v, register_number: regNo };
                            // If course already selected and new batch series, auto-populate fee
                            const cpEntry = cpFeesRef.current[f.course_name];
                            if (cpEntry?.fee && isNewBatch(v)) {
                              updates.fee_tuition = String(cpEntry.fee);
                              toast.success(`Tuition fee set to ₹${Number(cpEntry.fee).toLocaleString('en-IN')}`);
                            }
                            return { ...f, ...updates };
                          });
                        } else {
                          // For edit mode, still auto-populate if course selected
                          setForm(f => {
                            const cpEntry = cpFeesRef.current[f.course_name];
                            // never overwrite a tuition fee that is already set on an existing student
                            if (cpEntry?.fee && isNewBatch(v) && !Number(f.fee_tuition)) {
                              return { ...f, batch_id: batch?.id||'', batch_name: v, fee_tuition: String(cpEntry.fee) };
                            }
                            return { ...f, batch_id: batch?.id||'', batch_name: v };
                          });
                        }
                      }}
                      placeholder="Select Batch"
                      searchPlaceholder="Search batch..."
                    />
                  </div>

                  {/* Course Package — SearchableSelect */}
                  <div className="form-group">
                    <label>Course Package{identityLocked&&<span style={{fontSize:10,color:'var(--text3)',marginLeft:6}}>🔒 locked after verification</span>}</label>
                    <div style={identityLocked?{pointerEvents:'none',opacity:0.6}:undefined}>
                    <SearchableSelect
                      options={[
                        ...courses.map(c => ({ value: c.course_name, label: `${c.course_short_code ? c.course_short_code + ' — ' : ''}${c.course_name}${c.course_fees?' (₹'+Number(c.course_fees).toLocaleString('en-IN')+')':''}` })),
                      ]}
                      value={form.course_name || ''}
                      onChange={v => {
                        // Find course fees from ref (always populated after loadMeta)
                        const cpEntry = cpFeesRef.current[v];
                        const updates = {
                          course_package_id: cpEntry?.id || '',
                          course_name: v
                        };
                        // Auto-populate tuition fee for new batch series only
                        if (cpEntry?.fee && isNewBatch(form.batch_name)) {
                          updates.fee_tuition = String(cpEntry.fee);
                          toast.success(`Tuition fee set to ₹${Number(cpEntry.fee).toLocaleString('en-IN')}`);
                        }
                        setForm(f => ({ ...f, ...updates }));
                      }}
                      placeholder="Select Course Package"
                      searchPlaceholder="Search package..."
                    />
                    </div>
                  </div>

                  {/* College — SearchableSelect */}
                  <div className="form-group">
                    <label>College</label>
                    <SearchableSelect
                      options={[...new Set(colleges.map(c => c.college_name).filter(Boolean))].sort().map(n => ({ value: n, label: n }))}
                      value={form.colleges?.college_name || form.college_name || ''}
                      onChange={v => {
                        const col = colleges.find(c => c.college_name === v || c.id === v);
                        setField('college_id', col?.id || '');
                        setField('college_name', v);
                      }}
                      placeholder="Select College"
                      searchPlaceholder="Search college..."
                    />
                  </div>

                  <div className="form-group"><label>NACTET Register Number</label><input value={form.nactet_register_number} onChange={e => setField('nactet_register_number', e.target.value)} /></div>

                  {/* Register Number — auto-filled, editable */}
                  <div className="form-group">
                    <label>
                      Register Number
                      {!editing && form.register_number && <span style={{ fontSize: 10, color: 'var(--green)', marginLeft: 8 }}>✓ Auto-generated</span>}
                    </label>
                    <input
                      disabled={identityLocked}
                      value={form.register_number || ''}
                      onChange={e => setField('register_number', e.target.value)}
                      placeholder="Select batch to auto-generate"
                      style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent)' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Centre</label>
                    <select value={form.centre || ''} onChange={e => setField('centre', e.target.value)}>
                      <option value="">Select Centre</option>
                      <option value="EKM">EKM</option>
                      <option value="TVM">TVM</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Training Timing</label>
                    <select value={form.training_timing} onChange={e => setField('training_timing', e.target.value)}>
                      <option value="">Select Timing</option>
                      <option value="Morning">Morning</option>
                      <option value="Afternoon">Afternoon</option>
                      <option value="Full Day">Full Day</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Training Mode</label>
                    <select value={form.training_mode || ''} onChange={e => setField('training_mode', e.target.value)}>
                      <option value="">Select Mode</option>
                      <option value="Offline">Offline</option>
                      <option value="Online">Online</option>
                    </select>
                  </div>
                </div>
              )}

              {/* TAB 3 — DOCUMENTS */}
              {activeTab === 3 && (
                <div>
                  <div className="form-section-heading">Document Submission Status</div>
                  <div className="form-grid">
                    {DOCUMENT_LIST.map(doc => (
                      <div className="form-group" key={doc.key}>
                        <label>{doc.label}</label>
                        <select value={form.documents_checklist[doc.key] ? 'yes' : 'no'} onChange={e => setDocField(doc.key, e.target.value === 'yes')}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                    ))}
                    <div className="form-group">
                      <label>No. of Passport Photos Collected</label>
                      <input type="number" min="0" value={form.passport_photos_collected} onChange={e => setField('passport_photos_collected', e.target.value)} placeholder="0" />
                    </div>
                  </div>

                  <div className="form-section-heading" style={{ marginTop: 24 }}>App Status</div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Classplus App Installed?</label>
                      <select value={form.classplus_installed ? 'yes' : 'no'} onChange={e => setField('classplus_installed', e.target.value === 'yes')}>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                  </div>

                  {/* Visual summary */}
                  <div style={{ marginTop: 24, background: 'var(--surface)', borderRadius: 8, padding: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Document Summary</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                      {DOCUMENT_LIST.map(doc => (
                        <div key={doc.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg2)', borderRadius: 6, border: `1px solid ${form.documents_checklist[doc.key] ? 'var(--green)' : 'var(--border)'}` }}>
                          {form.documents_checklist[doc.key] ? <CheckCircle size={14} color="var(--green)" /> : <XCircle size={14} color="var(--text3)" />}
                          <span style={{ fontSize: 12, color: form.documents_checklist[doc.key] ? 'var(--green)' : 'var(--text3)' }}>{doc.label}</span>
                        </div>
                      ))}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg2)', borderRadius: 6, border: `1px solid ${Number(form.passport_photos_collected) > 0 ? 'var(--amber)' : 'var(--border)'}` }}>
                        {Number(form.passport_photos_collected) > 0 ? <CheckCircle size={14} color="var(--amber)" /> : <XCircle size={14} color="var(--text3)" />}
                        <span style={{ fontSize: 12, color: Number(form.passport_photos_collected) > 0 ? 'var(--amber)' : 'var(--text3)' }}>Passport Photos: {form.passport_photos_collected || 0}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg2)', borderRadius: 6, border: `1px solid ${form.classplus_installed ? 'var(--cyan)' : 'var(--border)'}` }}>
                        {form.classplus_installed ? <CheckCircle size={14} color="var(--cyan)" /> : <XCircle size={14} color="var(--text3)" />}
                        <span style={{ fontSize: 12, color: form.classplus_installed ? 'var(--cyan)' : 'var(--text3)' }}>Classplus App</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 4 — FEES */}
              {activeTab === 4 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {feesLocked && <div style={{background:'#fef3c7',border:'1px solid #fcd34d',color:'#92400e',borderRadius:8,padding:'8px 12px',fontSize:12}}>🔒 Fees can be changed only by {OWNER_EMAIL} and the office admins ({OFFICE_EMAILS.join(', ')}). You can view them here.</div>}
                  <fieldset disabled={feesLocked} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, opacity: feesLocked ? 0.7 : 1 }}>

                  {/* ── Row 1: Basic fees in a clean grid ── */}
                  <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '16px 18px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 14 }}>Basic Fees</div>
                    <div className="form-grid">
                      <FeeRow label="Tuition Fees" fieldKey="fee_tuition" />
                      {isNewBatch(form.batch_name) && form.course_name && (
                        <div style={{gridColumn:'1/-1',fontSize:11,color:'var(--green)',marginTop:-8,marginBottom:4}}>
                          ✓ Fee auto-populated from course package for batch {form.batch_name}
                        </div>
                      )}
                      <FeeRow label="Hostel Fees" fieldKey="hostel_fee" />
                      <FeeRow label="Package Changing Fees" fieldKey="fee_package_change" />
                      <FeeRow label="Manpower Fees" fieldKey="fee_manpower" />
                      <FeeRow label="Certification Fees" fieldKey="fee_certification" />
                      <FeeRow label="Other Fees" fieldKey="fee_other" />
                    </div>
                  </div>

                  {/* ── Row 2: Attestation fees expanded ── */}
                  <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '16px 18px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 14 }}>
                      Attestation Fees
                    </div>

                    {/* Table header */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 160px', gap: 10, marginBottom: 8, padding: '0 4px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>Type</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textAlign: 'center' }}>No. of Certificates</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textAlign: 'right' }}>Amount (₹)</div>
                    </div>

                    {/* HRD + MEA */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 160px', gap: 10, alignItems: 'center', padding: '10px 4px', borderBottom: '1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>HRD + MEA</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>₹1,200 per certificate</div>
                      </div>
                      <input type="number" min="0" value={form.attest_hrd_mea_count}
                        onChange={e => { const n = e.target.value; setForm(f => ({ ...f, attest_hrd_mea_count: n, attest_hrd_mea_amount: n ? Number(n) * 1200 : '' })); }}
                        placeholder="0" style={{ textAlign: 'center' }} />
                      <input type="number" value={form.attest_hrd_mea_amount}
                        onChange={e => setForm(f => ({ ...f, attest_hrd_mea_amount: e.target.value }))}
                        placeholder="0" style={{ textAlign: 'right', fontWeight: 600, color: Number(form.attest_hrd_mea_amount) > 0 ? 'var(--accent)' : undefined }} />
                    </div>

                    {/* Apostile */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 160px', gap: 10, alignItems: 'center', padding: '10px 4px', borderBottom: '1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>Apostile</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>₹1,250 per certificate</div>
                      </div>
                      <input type="number" min="0" value={form.attest_apostile_count}
                        onChange={e => { const n = e.target.value; setForm(f => ({ ...f, attest_apostile_count: n, attest_apostile_amount: n ? Number(n) * 1250 : '' })); }}
                        placeholder="0" style={{ textAlign: 'center' }} />
                      <input type="number" value={form.attest_apostile_amount}
                        onChange={e => setForm(f => ({ ...f, attest_apostile_amount: e.target.value }))}
                        placeholder="0" style={{ textAlign: 'right', fontWeight: 600, color: Number(form.attest_apostile_amount) > 0 ? 'var(--accent)' : undefined }} />
                    </div>

                    {/* UAE */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 160px', gap: 10, alignItems: 'center', padding: '10px 4px', borderBottom: '1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>UAE</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>₹4,550 per certificate</div>
                      </div>
                      <input type="number" min="0" value={form.attest_uae_count}
                        onChange={e => { const n = e.target.value; setForm(f => ({ ...f, attest_uae_count: n, attest_uae_amount: n ? Number(n) * 4550 : '' })); }}
                        placeholder="0" style={{ textAlign: 'center' }} />
                      <input type="number" value={form.attest_uae_amount}
                        onChange={e => setForm(f => ({ ...f, attest_uae_amount: e.target.value }))}
                        placeholder="0" style={{ textAlign: 'right', fontWeight: 600, color: Number(form.attest_uae_amount) > 0 ? 'var(--accent)' : undefined }} />
                    </div>

                    {/* Total Attestation row */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 160px', gap: 10, alignItems: 'center', padding: '12px 4px', background: 'var(--accent-light)', borderRadius: '0 0 8px 8px', marginTop: 2 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--accent)' }}>Total Attestation</div>
                      <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>
                        {Number(form.attest_hrd_mea_count || 0) + Number(form.attest_apostile_count || 0) + Number(form.attest_uae_count || 0)} certs
                      </div>
                      <div style={{ textAlign: 'right', fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, color: 'var(--accent)' }}>
                        {fmt(calcAttestationTotal(form))}
                      </div>
                    </div>
                  </div>

                  {/* ── Row 3: Discount ── */}
                  <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '16px 18px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 14 }}>Discount</div>
                    <div className="form-grid">
                      <FeeRow label="Discount Amount (–)" fieldKey="fee_discount" isDiscount />
                      <div className="form-group" style={{ opacity: Number(form.fee_discount) > 0 ? 1 : 0.5 }}>
                        <label style={{ color: 'var(--red)' }}>
                          Reason for Discount
                          {Number(form.fee_discount) > 0 && <span style={{ color: 'var(--red)', marginLeft: 4 }}>*</span>}
                        </label>
                        <input
                          value={form.discount_reason || ''}
                          onChange={e => setField('discount_reason', e.target.value)}
                          placeholder={Number(form.fee_discount) > 0 ? 'Enter reason for discount...' : 'Enter discount amount first'}
                          disabled={!Number(form.fee_discount) > 0}
                          style={{ borderColor: Number(form.fee_discount) > 0 && !form.discount_reason ? 'var(--red)' : undefined }}
                        />
                      </div>
                    </div>

                    {/* Discount History */}
                    {form.id && Number(form.fee_discount)>0 && (
                      <DiscountHistory studentId={form.id} enrollmentDiscount={Number(form.fee_discount)} enrollmentReason={form.discount_reason}/>
                    )}
                  </div>

                  {/* ── Row 4: Grand Total summary ── */}
                  <div style={{ background: 'var(--surface)', borderRadius: 10, padding: '16px 18px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 14 }}>Fee Summary</div>

                    {/* Line items — only show if > 0 */}
                    {[
                      { label: 'Tuition Fees',           val: form.fee_tuition },
                      { label: 'Hostel Fees',            val: form.hostel_fee },
                      { label: 'Package Changing Fees',  val: form.fee_package_change },
                      { label: 'Manpower Fees',          val: form.fee_manpower },
                      { label: 'Certification Fees',     val: form.fee_certification },
                      { label: 'Other Fees',             val: form.fee_other },
                      { label: 'HRD + MEA Attestation',  val: form.attest_hrd_mea_amount },
                      { label: 'Apostile Attestation',   val: form.attest_apostile_amount },
                      { label: 'UAE Attestation',        val: form.attest_uae_amount },
                    ].filter(r => Number(r.val) > 0).map(r => (
                      <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                        <span style={{ color: 'var(--text2)' }}>{r.label}</span>
                        <span style={{ fontWeight: 500 }}>{fmt(r.val)}</span>
                      </div>
                    ))}

                    {/* Attestation subtotal if any */}
                    {calcAttestationTotal(form) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, borderBottom: '1px solid var(--border)', color: 'var(--accent)' }}>
                        <span style={{ fontWeight: 500 }}>Total Attestation</span>
                        <span style={{ fontWeight: 600 }}>{fmt(calcAttestationTotal(form))}</span>
                      </div>
                    )}

                    {/* Discount */}
                    {Number(form.fee_discount) > 0 && (
                      <div style={{ padding: '6px 0', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--red)' }}>Discount</span>
                          <span style={{ color: 'var(--red)', fontWeight: 500 }}>– {fmt(form.fee_discount)}</span>
                        </div>
                        {form.discount_reason && (
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Reason: {form.discount_reason}</div>
                        )}
                      </div>
                    )}

                    {/* Grand total */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14, marginTop: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <IndianRupee size={18} color="var(--accent)" />
                        <span style={{ fontWeight: 700, fontSize: 15 }}>Total Fees</span>
                      </div>
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800, color: 'var(--accent)' }}>
                        {fmt(calcTotalFees(form))}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
                      ✓ This total will be saved and shown as balance due in Fee Management.
                    </div>
                  </div>
                  </fieldset>

                </div>
              )}

              {/* TAB 5 — SOCIAL MEDIA */}
              {activeTab === 5 && (
                <div>
                  <div className="form-section-heading">Social Media & Website</div>
                  <div style={{ background: 'var(--accent-light)', border: '1px solid var(--accent)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--accent)' }}>
                    Track digital presence and student feedback activities
                  </div>
                  <div className="form-grid">
                    {[
                      ['google_review',           'Google Review Given (Y/N)'],
                      ['video_feedback',           'Video Feedback Given (Y/N)'],
                      ['placement_photos_website', 'Placement Photos Added in Website (Y/N)'],
                      ['recently_placed_website',  'Recently Placed Students in Website (Y/N)'],
                    ].map(([key, label]) => (
                      <div key={key} className="form-group">
                        <label>{label}</label>
                        <select value={form[key] ? 'yes' : 'no'} onChange={e => setField(key, e.target.value === 'yes')}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                    ))}
                  </div>

                  {/* Summary */}
                  <div style={{ marginTop: 20, background: 'var(--surface)', borderRadius: 8, padding: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12 }}>Summary</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                      {[
                        ['Google Review',          'google_review',           '⭐'],
                        ['Video Feedback',         'video_feedback',          '🎥'],
                        ['Placement Photos',       'placement_photos_website','📸'],
                        ['Recently Placed',        'recently_placed_website', '🏆'],
                      ].map(([label, key, icon]) => (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: `1.5px solid ${form[key] ? 'var(--accent)' : 'var(--border)'}`, background: form[key] ? 'var(--accent-light)' : 'var(--surface)' }}>
                          <span style={{ fontSize: 18 }}>{icon}</span>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
                            <div style={{ fontSize: 11, color: form[key] ? 'var(--accent)' : 'var(--text3)', fontWeight: 500 }}>{form[key] ? '✓ Done' : '✗ Not Done'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 6 — DRIVE DOCUMENTS */}
              {activeTab === 7 && (
                <div style={{padding:16}}>
                  <h4 style={{marginBottom:12}}>Change History</h4>
                  {(() => {
                    const s = students.find(s=>s.id===editing);
                    const log = s?.change_log || [];
                    if (log.length === 0) return <p style={{color:'var(--text3)'}}>No changes recorded yet.</p>;
                    return (
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                        <thead><tr style={{background:'#f3f4f6'}}>
                          <th style={{padding:'6px 10px',textAlign:'left'}}>Field</th>
                          <th style={{padding:'6px 10px',textAlign:'left'}}>Old Value</th>
                          <th style={{padding:'6px 10px',textAlign:'left'}}>New Value</th>
                          <th style={{padding:'6px 10px',textAlign:'left'}}>Changed By</th>
                          <th style={{padding:'6px 10px',textAlign:'left'}}>Date</th>
                        </tr></thead>
                        <tbody>{[...log].reverse().map((l,i)=>(
                          <tr key={i} style={{background:i%2?'#f9fafb':'#fff',borderBottom:'1px solid #f3f4f6'}}>
                            <td style={{padding:'5px 10px',fontWeight:600}}>{l.field}</td>
                            <td style={{padding:'5px 10px',color:'var(--red)'}}>{l.old||'—'}</td>
                            <td style={{padding:'5px 10px',color:'var(--green)'}}>{l.new||'—'}</td>
                            <td style={{padding:'5px 10px',color:'var(--text3)'}}>{l.by||'—'}</td>
                            <td style={{padding:'5px 10px',color:'var(--text3)'}}>{l.at?new Date(l.at).toLocaleDateString('en-IN'):''}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    );
                  })()}
                </div>
              )}
              {activeTab === 6 && (
                <GoogleDriveTab student={editing ? students.find(s => s.id === editing) : null} />
              )}
            </div>

            <div className="modal-footer">
              {activeTab > 0 && <button className="btn btn-secondary" onClick={() => setActiveTab(t => t - 1)}>← Back</button>}
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              {editing && activeTab < TABS.length - 1 && (
                <button className="btn btn-success" onClick={save} disabled={saving} style={{background:'var(--green)',color:'#fff',border:'none'}}>
                  {saving ? 'Saving...' : '💾 Update Student'}
                </button>
              )}
              {activeTab < TABS.length - 1
                ? <button className="btn btn-primary" onClick={() => setActiveTab(t => t + 1)}>Next →</button>
                : <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update Student' : 'Add Student'}</button>
              }
            </div>
          </div>
        </div>
      )}

      {/* ── DETAIL VIEW MODAL ── */}
      {timelineStudent && <StudentTimelineModal student={timelineStudent} onClose={()=>setTimelineStudent(null)} />}

      {showDetail && (
        <div className="modal-overlay" style={{zIndex:5000}} onClick={e => e.target === e.currentTarget && setShowDetail(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <PassportPhotoUpload student={showDetail} />
                <div>
                  <div className="modal-title">{showDetail.full_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--accent)', fontFamily: 'monospace' }}>{showDetail.register_number}</div>
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowDetail(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {/* Basic details */}
              {[
                ['Contact', showDetail.contact_number],
                ['WhatsApp', showDetail.whatsapp_number],
                ['Email', showDetail.email],
                ['DOB', showDetail.dob],
                ['Guardian', showDetail.guardian_name],
                ['Guardian Contact', showDetail.guardian_contact ? React.createElement('span',{style:{display:'flex',gap:4,alignItems:'center'}},showDetail.guardian_contact,React.createElement(CallBtn,{phone:showDetail.guardian_contact})) : '—'],
                ['Address', [showDetail.place, showDetail.district, showDetail.state, showDetail.pincode].filter(Boolean).join(', ')],
                ['Discipline', showDetail.discipline],
                ['Status', React.createElement('span',{className:`badge ${(showDetail.student_status||'Continuing')==='Continuing'?'badge-green':(showDetail.student_status||'Continuing')==='Discontinued'?'badge-red':(showDetail.student_status||'Continuing')==='Shifted'?'badge-purple':'badge-blue'}`},showDetail.student_status||'Continuing')],
                ['Discontinue Reason', showDetail.student_status==='Discontinued' ? showDetail.discontinue_reason : ''],
                ['Centre', showDetail.centre],
                ['Batch', showDetail.batches?.batch_name || showDetail.batch_name],
                ['Course Package', showDetail.course_packages?.course_name || showDetail.course_name],
                ['College', showDetail.colleges?.college_name || showDetail.college_name],
                ['NACTET No.', showDetail.nactet_register_number],
                ['Training Timing', showDetail.training_timing],
                ['Registration Date', showDetail.registration_date],
                ['Joining Date', showDetail.joining_date],
                ['Certificate Issued', showDetail.course_certificate_issued ? 'Yes' : 'No'],
                ['Hostel Needed', showDetail.hostel_needed ? 'Yes' : 'No'],
              ].map(([label, val]) => val && (
                <div key={label} className="detail-row">
                  <div className="detail-label">{label}</div>
                  <div className="detail-value">{val}</div>
                </div>
              ))}

              {/* Fee summary */}
              {showDetail.fee_total > 0 && (
                <>
                  <div style={{ marginTop: 16, fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>Fees</div>
                  {[
                    ['Tuition Fees', showDetail.fee_tuition],
                    ['Attestation Fees', showDetail.fee_attestation],
                    ['Package Changing Fees', showDetail.fee_package_change],
                    ['Manpower Fees', showDetail.fee_manpower],
                    ['Certification Fees', showDetail.fee_certification],
                    ['Other Fees', showDetail.fee_other],
                    ['Discount', showDetail.fee_discount],
                  ].filter(([, v]) => Number(v) > 0).map(([label, val]) => (
                    <div key={label} className="detail-row">
                      <div className="detail-label">{label}</div>
                      <div className="detail-value" style={{ color: label === 'Discount' ? 'var(--red)' : undefined }}>
                        {label === 'Discount' ? `– ${fmt(val)}` : fmt(val)}
                      </div>
                    </div>
                  ))}
                  <div className="detail-row" style={{ borderColor: 'var(--accent)' }}>
                    <div className="detail-label" style={{ fontWeight: 700, color: 'var(--text)' }}>Total Fees</div>
                    <div className="detail-value" style={{ fontWeight: 800, fontSize: 16, color: 'var(--accent)' }}>{fmt(showDetail.fee_total)}</div>
                  </div>
                  {(() => {
                    const _paid = feePayments.filter(p => p.student_id === showDetail.id).reduce((a, p) => a + Number(p.amount || 0), 0);
                    const _bal = Math.max(0, Number(showDetail.fee_total || 0) - _paid);
                    return (<>
                      <div className="detail-row"><div className="detail-label">Paid</div><div className="detail-value" style={{ color: 'var(--green)', fontWeight: 700 }}>{fmt(_paid)}</div></div>
                      <div className="detail-row"><div className="detail-label">Balance</div><div className="detail-value" style={{ color: _bal > 0 ? 'var(--red)' : 'var(--green)', fontWeight: 700 }}>{fmt(_bal)}</div></div>
                    </>);
                  })()}
                </>
              )}

              <StudentScheduleCard studentId={showDetail.id} />

              {/* Documents section */}
              <div style={{ marginTop: 16, fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>Documents</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                {DOCUMENT_LIST.map(doc => {
                  const submitted = showDetail.documents_checklist?.[doc.key];
                  return (
                    <div key={doc.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--surface)', borderRadius: 6, border: `1px solid ${submitted ? 'var(--green)' : 'var(--border)'}` }}>
                      {submitted ? <CheckCircle size={14} color="var(--green)" /> : <XCircle size={14} color="var(--text3)" />}
                      <span style={{ fontSize: 12, color: submitted ? 'var(--green)' : 'var(--text3)' }}>{doc.label}</span>
                      <span className={`badge ${submitted ? 'badge-green' : 'badge-red'}`} style={{ marginLeft: 'auto', fontSize: 10 }}>{submitted ? 'Yes' : 'No'}</span>
                    </div>
                  );
                })}
              </div>

              {/* Classplus */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--surface)', borderRadius: 6, border: `1px solid ${showDetail.classplus_installed ? 'var(--cyan)' : 'var(--border)'}`, marginBottom: 8 }}>
                {showDetail.classplus_installed ? <CheckCircle size={14} color="var(--cyan)" /> : <XCircle size={14} color="var(--text3)" />}
                <span style={{ fontSize: 13, fontWeight: 500 }}>Classplus App</span>
                <span className={`badge ${showDetail.classplus_installed ? 'badge-cyan' : 'badge-red'}`} style={{ marginLeft: 'auto' }}>
                  {showDetail.classplus_installed ? 'Installed' : 'Not Installed'}
                </span>
              </div>
              {/* Passport photos */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--surface)', borderRadius: 6, border: `1px solid ${showDetail.passport_photos_collected > 0 ? 'var(--amber)' : 'var(--border)'}` }}>
                {showDetail.passport_photos_collected > 0 ? <CheckCircle size={14} color="var(--amber)" /> : <XCircle size={14} color="var(--text3)" />}
                <span style={{ fontSize: 13, fontWeight: 500 }}>Passport Photos Collected</span>
                <span className={`badge ${showDetail.passport_photos_collected > 0 ? 'badge-amber' : 'badge-gray'}`} style={{ marginLeft: 'auto' }}>
                  {showDetail.passport_photos_collected || 0}
                </span>
              </div>

              {/* Placement Status (read-only summary) */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Placement Status</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setTimelineStudent(showDetail)} title="View full placement timeline">
                    <Clock size={13} /> View Details
                  </button>
                </div>
                <PlacementSummaryInline studentId={showDetail.id} />
              </div>
            </div>
            <div style={{padding:'0 20px 12px'}}>
              <StudentHistory studentId={showDetail.id}/>
            </div>
            {detailVerif && (
              <div style={{padding:'0 20px 10px',display:'flex',gap:8,flexWrap:'wrap',fontSize:11}}>
                <span style={{padding:'2px 8px',borderRadius:10,fontWeight:700,background:detailVerif.verified_batch?'#dcfce7':'#fef3c7',color:detailVerif.verified_batch?'#166534':'#92400e'}}>{detailVerif.verified_batch?'✅':'⏳'} Batch coordinator verification</span>
                <span style={{padding:'2px 8px',borderRadius:10,fontWeight:700,background:detailVerif.verified_admin?'#dcfce7':'#fef3c7',color:detailVerif.verified_admin?'#166534':'#92400e'}}>{detailVerif.verified_admin?'✅':'⏳'} Office admin verification</span>
              </div>
            )}
            <div className="modal-footer">
              {canEditStudent(showDetail) && <button className="btn btn-secondary" onClick={() => { setShowDetail(null); openEdit(showDetail); }}><Edit2 size={13} /> Edit</button>}
              {canEditStudent(showDetail) && <button className="btn btn-secondary" onClick={() => { setShowTransfer(showDetail); setTransferForm({type:'batch',toBatch:'',toCourse:'',date:'',reason:''}); setShowDetail(null); }} style={{background:'#8b5cf6',color:'#fff',border:'none'}}>⇄ Transfer Batch</button>}
              {canEditStudent(showDetail) && (isOwner || !(detailVerif && detailVerif.verified_batch && detailVerif.verified_admin)) && <button className="btn btn-secondary" onClick={() => { setShowTransfer(showDetail); setTransferForm({type:'course',toBatch:'',toCourse:'',toCentre:'',date:'',reason:''}); setShowDetail(null); }} style={{background:'#0891b2',color:'#fff',border:'none'}}>📦 Change Course</button>}
              {canEditStudent(showDetail) && <button className="btn btn-secondary" onClick={() => { setShowTransfer(showDetail); setTransferForm({type:'centre',toBatch:'',toCourse:'',toCentre:'',date:'',reason:''}); setShowDetail(null); }} style={{background:'#7c3aed',color:'#fff',border:'none'}}>🏢 Change Centre</button>}
              {canEdit('students') && canDeleteStudent && <button className="btn btn-secondary" style={{color:'var(--red)'}} onClick={() => { const st = showDetail; setShowDetail(null); deleteStudent(st.id, st.full_name); }}><Trash2 size={13} /> Delete</button>}
              <button className="btn btn-primary" onClick={() => setShowDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* Transfer / Course Change Modal */}
      {showTransfer&&(
        <div className="modal-overlay" style={{zIndex:5000}} onClick={e=>e.target===e.currentTarget&&setShowTransfer(null)}>
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-header">
              <h3>{transferForm.type==='batch'?'⇄ Transfer Batch':transferForm.type==='course'?'📦 Change Course':'🏢 Change Centre'}</h3>
              <button className="modal-close" onClick={()=>setShowTransfer(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{background:'var(--bg3)',borderRadius:8,padding:'10px 14px',marginBottom:14,fontSize:13}}>
                <b>{showTransfer.full_name}</b> ({showTransfer.register_number})<br/>
                <span style={{color:'var(--text3)',fontSize:12}}>
                  Current {transferForm.type==='batch'?'Batch':transferForm.type==='course'?'Course':'Centre'}: <b>{transferForm.type==='batch'?showTransfer.batch_name:transferForm.type==='course'?showTransfer.course_name:showTransfer.centre||'—'}</b>
                </span>
              </div>
              <div className="form-grid">
                {transferForm.type==='batch' ? (
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>New Batch *</label>
                    <SearchableSelect
                      options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))}
                      value={transferForm.toBatch}
                      onChange={v=>setTransferForm(f=>({...f,toBatch:v}))}
                      placeholder="Select new batch..."
                    />
                  </div>
                ) : transferForm.type==='centre' ? (
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>New Centre *</label>
                    <SearchableSelect
                      options={['EKM','TVM'].map(n=>({value:n,label:n}))}
                      value={transferForm.toCentre}
                      onChange={v=>setTransferForm(f=>({...f,toCentre:v}))}
                      placeholder="Select new centre..."
                    />
                  </div>
                ) : (
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label>New Course *</label>
                    <SearchableSelect
                      options={courses.map(c => c.course_name).map(n=>({value:n,label:n}))}
                      value={transferForm.toCourse}
                      onChange={v=>setTransferForm(f=>({...f,toCourse:v}))}
                      placeholder="Select new course..."
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Date</label>
                  <DateInput value={transferForm.date} onChange={e=>setTransferForm(f=>({...f,date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Reason</label>
                  <input type="text" value={transferForm.reason} onChange={e=>setTransferForm(f=>({...f,reason:e.target.value}))} placeholder="Optional reason..."/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={()=>setShowTransfer(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveTransfer}>
                {transferForm.type==='batch'?'Transfer':transferForm.type==='course'?'Change Course':'Change Centre'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

  );
}

// ── Location-based Student Report ─────────────────────────────
function LocationStudentReport({ supabase }) {
  const [districtOptions, setDistrictOptions] = React.useState([]);
  const [stateOptions, setStateOptions] = React.useState([]);
  const [disciplineOptions, setDisciplineOptions] = React.useState([]);
  const [batchOptions, setBatchOptions] = React.useState([]);
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [searched, setSearched] = React.useState(false);
  const [fDistricts, setFDistricts] = React.useState([]);
  const [fState, setFState] = React.useState('');
  const [locationOptions, setLocationOptions] = React.useState([]);
  const [fLocation, setFLocation] = React.useState('');
  const [fDiscipline, setFDiscipline] = React.useState('');
  const [fBatch, setFBatch] = React.useState('');
  const [fStatus, setFStatus] = React.useState('');

  React.useEffect(() => {
    fetchAllPaged(() => supabase.from('students').select('discipline,batch_name,district').eq('is_active', true).order('id'))
      .then(data => {
        if (!data) return;
        setDisciplineOptions([...new Set(data.map(s=>s.discipline).filter(Boolean))].sort());
        setBatchOptions([...new Set(data.map(s=>s.batch_name).filter(Boolean))].sort().reverse());
        setDistrictOptions([...new Set(data.map(s=>s.district).filter(Boolean))].sort());
      });
    fetchAllPaged(() => supabase.from('students').select('known_location').not('known_location','is',null).order('id')).then(data=>{
      if (data) setLocationOptions([...new Set(data.map(s=>s.known_location).filter(Boolean).map(s=>s.trim()))].sort());
    });
    setStateOptions(['Kerala','Tamil Nadu','Karnataka','Andhra Pradesh','Telangana','Maharashtra','Goa','Other']);
    setDistrictOptions(['Thiruvananthapuram','Kollam','Pathanamthitta','Alappuzha','Kottayam',
      'Idukki','Ernakulam','Thrissur','Palakkad','Malappuram','Kozhikode',
      'Wayanad','Kannur','Kasaragod']);
  }, []);

  // (removed: a realtime subscription here called an undefined loadMeta() on every students/fee_payments change)

  React.useEffect(()=>{ if(searched) search(); },[fState,fDiscipline,fBatch,fStatus,fDistricts,fLocation]); // eslint-disable-line

  async function search() {
    setLoading(true); setSearched(true);
    const buildQ = () => { let q = supabase.from('students')
      .select('id,full_name,register_number,batch_name,discipline,contact_number,whatsapp_number,email,district,state,known_location,student_status,college_name,academic_qualification')
      .eq('is_active', true).order('register_number').order('id');
    if (fState) q = q.ilike('state', fState);
    if (fDiscipline) q = q.eq('discipline', fDiscipline);
    if (fBatch) q = q.eq('batch_name', fBatch);
    if (fStatus) q = q.eq('student_status', fStatus);
    if (fDistricts.length > 0) {
      // case-insensitive district match
      q = q.or(fDistricts.map(d=>`district.ilike.${d}`).join(','));
    }
    if (fLocation) q = q.ilike('known_location', '%'+fLocation+'%');
      return q; };
    try { const found = await fetchAllPaged(buildQ); const regNoCmp = (a, b) => { const x = String(a.register_number || '').trim(), y = String(b.register_number || '').trim(); if (!x !== !y) return x ? -1 : 1; return x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' }); }; setResults((fStatus ? found : found.filter(isReportable)).slice().sort(regNoCmp)); } catch (e) { toast.error('Search failed: ' + e.message); }
    setLoading(false);
  }

  function exportCSV() {
    if (!results.length) return;
    const rows = results.map((s,i)=>({
      '#':i+1,'Reg No':s.register_number||'','Name':s.full_name||'','Batch':s.batch_name||'','Discipline':s.discipline||'',
      'Phone':s.contact_number||'','WhatsApp':s.whatsapp_number||'','Email':s.email||'',
      'District':s.district||'','State':s.state||'','Known Location':s.known_location||'',
      'Status':s.student_status||'','College':s.college_name||'','Qualification':s.academic_qualification||''
    }));
    saveStyledXlsx('Location_Report_'+localDate()+'.xlsx',[{name:'Location',title:'Location-based Student Report',rows}]);
  }

  return (
    <div>
      <div style={{fontWeight:700,fontSize:15,marginBottom:12}}>📍 Location-based Student Report</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:10,marginBottom:12}}>
        <div className="form-group">
          <label style={{fontSize:11}}>State</label>
          <select value={fState} onChange={e=>setFState(e.target.value)} style={{fontSize:12}}>
            <option value="">All States</option>
            {stateOptions.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label style={{fontSize:11}}>Discipline/Stream</label>
          <select value={fDiscipline} onChange={e=>setFDiscipline(e.target.value)} style={{fontSize:12}}>
            <option value="">All</option>
            {disciplineOptions.map(d=><option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label style={{fontSize:11}}>Batch</label>
          <SearchableSelect
            options={[{value:'',label:'All Batches'},...orderBatchNames(batchOptions).map(b=>({value:b,label:b}))]}
            value={fBatch}
            onChange={v=>setFBatch(v||'')}
            placeholder="All Batches"
          />
        </div>
        <div className="form-group">
          <label style={{fontSize:11}}>Student Status</label>
          <select value={fStatus} onChange={e=>setFStatus(e.target.value)} style={{fontSize:12}}>
            <option value="">All</option>
            {['Active','Continuing','Completed','Discontinued','On Hold'].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label style={{fontSize:11}}>Known Location</label>
          <input value={fLocation} onChange={e=>setFLocation(e.target.value)}
            placeholder="Type to search location..." style={{fontSize:12}}
            list="location-options-report"/>
          <datalist id="location-options-report">
            {KNOWN_LOCATIONS.filter(l=>!fLocation||l.toLowerCase().includes(fLocation.toLowerCase())).map(l=>
              <option key={l} value={l}/>
            )}
          </datalist>
        </div>
      </div>
      <div style={{marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:600,marginBottom:6}}>
          Districts
          {fDistricts.length>0&&<span style={{marginLeft:8,color:'var(--accent)'}}>({fDistricts.length} selected)</span>}
          {fDistricts.length>0&&<button className="btn btn-ghost btn-sm" style={{fontSize:10,marginLeft:6}} onClick={()=>setFDistricts([])}>Clear</button>}
        </div>
        <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
          {districtOptions.map(d=>(
            <button key={d} onClick={()=>setFDistricts(p=>p.includes(d)?p.filter(x=>x!==d):[...p,d])}
              style={{padding:'3px 10px',borderRadius:12,border:'1px solid var(--border)',cursor:'pointer',fontSize:11,
                background:fDistricts.includes(d)?'var(--accent)':'var(--surface2)',
                color:fDistricts.includes(d)?'#fff':'var(--text)'}}>
              {d}
            </button>
          ))}
        </div>
      </div>
      <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
        <button className="btn btn-primary" onClick={search} disabled={loading}>
          {loading?'Searching...':'🔍 Search Students'}
        </button>
        {results.length>0&&<button className="btn btn-ghost btn-sm" onClick={exportCSV}>📥 Export {results.length} Students to Excel</button>}
        <button className="btn btn-ghost btn-sm" onClick={()=>{setFDistricts([]);setFState('');setFDiscipline('');setFBatch('');setFLocation('');setFStatus('Active');setFDistricts([]);setFState('');setFDiscipline('');}}>✕ Clear</button>
      </div>
      {searched&&(
        <div>
          <div style={{fontSize:12,fontWeight:600,marginBottom:8,color:'var(--text3)'}}>
            {results.length} students found{fDistricts.length>0?' in '+fDistricts.join(', '):''}
            {fState?' · '+fState:''}{fDiscipline?' · '+fDiscipline:''}
          </div>
          {results.length>0?(
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:900}}>
                <thead>
                  <tr style={{background:'var(--accent-light)'}}>
                    {['Reg No','Name','Batch','Discipline','Phone','WhatsApp','District','State','Known Location','Status'].map(h=>(
                      <th key={h} style={{padding:'6px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.map((s,i)=>(
                    <tr key={s.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                      <td style={{padding:'6px 10px'}}>{s.register_number||'—'}</td>
                      <td style={{padding:'6px 10px',fontWeight:500}}>{s.full_name}</td>
                      <td style={{padding:'6px 10px'}}>{s.batch_name||'—'}</td>
                      <td style={{padding:'6px 10px'}}>{s.discipline||'—'}</td>
                      <td style={{padding:'6px 10px'}}>{s.contact_number||'—'}</td>
                      <td style={{padding:'6px 10px'}}>{s.whatsapp_number||'—'}</td>
                      <td style={{padding:'6px 10px'}}>{s.district||'—'}</td>
                      <td style={{padding:'6px 10px'}}>{s.state||'—'}</td>
                      <td style={{padding:'6px 10px'}}>{s.known_location||'—'}</td>
                      <td style={{padding:'6px 10px'}}>
                        <span style={{padding:'1px 6px',borderRadius:8,fontSize:10,
                          background:s.student_status==='Active'?'var(--green-light)':s.student_status==='Completed'?'var(--blue-light)':'var(--red-light)',
                          color:s.student_status==='Active'?'var(--green)':s.student_status==='Completed'?'var(--blue)':'var(--red)'}}>
                          {s.student_status||'—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ):(
            <div style={{padding:20,textAlign:'center',color:'var(--text3)'}}>No students found matching the selected criteria</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Outstanding Balance Report ─────────────────────────────────
function OutstandingBalanceReport({ supabase, batches }) {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fBatch, setFBatch] = React.useState('');
  const [sortBy, setSortBy] = React.useState('balance');
  const [searched, setSearched] = React.useState(false);

  React.useEffect(()=>{ if(searched) search(); },[fBatch]); // eslint-disable-line

  async function search() {
    setLoading(true); setSearched(true);
    try {
    const students = await fetchAllPaged(() => {
      let q = supabase.from('students')
        .select('id,full_name,register_number,batch_name,contact_number,whatsapp_number,fee_total,fee_discount,student_status')
        .eq('is_active', true).gt('fee_total', 0).order('full_name').order('id');
      if (fBatch) q = q.eq('batch_name', fBatch);
      return q;
    }).then(list => list.filter(isReportable));

    // Payments + overdue schedules (chunked: hundreds of ids in one .in() breaks the request URL)
    const ids = students.map(s=>s.id);
    const payments = await fetchByIds('fee_payments', 'student_id,amount', 'student_id', ids);
    const today = localDate();
    const schedules = await fetchByIds('student_fee_schedules', 'student_id,due_date,status,amount,paid_amount', 'student_id', ids,
      q => q.neq('status','paid').neq('status','waived').lt('due_date', today));

    const payMap = {};
    (payments||[]).forEach(p=>{ payMap[p.student_id] = (payMap[p.student_id]||0) + Number(p.amount); });
    const overdueMap = {};
    (schedules||[]).forEach(s=>{ overdueMap[s.student_id] = (overdueMap[s.student_id]||0) + Math.max(0, Number(s.amount||0) - Number(s.paid_amount||0)); });

    // fee_total is already net of discount (calcTotalFees subtracts it), so do NOT subtract fee_discount again
    const rows = students.map(s=>({
      ...s,
      totalPaid: payMap[s.id]||0,
      balance: Number(s.fee_total||0) - (payMap[s.id]||0),
      overdueAmount: Math.min(overdueMap[s.id]||0, Math.max(0, Number(s.fee_total||0) - (payMap[s.id]||0))),
    })).filter(r=>r.balance>0);

    setData(rows);
    } catch (e) { toast.error('Report failed: ' + e.message); }
    setLoading(false);
  }

  function exportCSV() {
    const rows = sorted.map((r,i)=>({
      '#': i+1,
      'Reg No': r.register_number||'',
      'Name': r.full_name,
      'Batch': r.batch_name||'',
      'Phone': r.contact_number||'',
      'WhatsApp': r.whatsapp_number||'',
      'Total Fee': r.fee_total||0,
      'Paid': r.totalPaid,
      'Balance': r.balance,
      'Overdue Amount': r.overdueAmount,
    }));
    if (!rows.length) return;
    rows.push({ '#':'','Reg No':'','Name':'TOTAL','Batch':'','Phone':'','WhatsApp':'',
      'Total Fee': rows.reduce((t,r)=>t+r['Total Fee'],0), 'Paid': rows.reduce((t,r)=>t+r['Paid'],0),
      'Balance': rows.reduce((t,r)=>t+r['Balance'],0), 'Overdue Amount': rows.reduce((t,r)=>t+r['Overdue Amount'],0) });
    saveStyledXlsx('Outstanding_Balance_'+localDate()+'.xlsx',[{name:'Outstanding',title:'Outstanding Balance Report',rows}]);
  }

  const sorted = [...data].sort((a,b)=>{
    if (sortBy==='balance') return b.balance - a.balance;
    if (sortBy==='overdue') return b.overdueAmount - a.overdueAmount;
    if (sortBy==='name') return a.full_name.localeCompare(b.full_name);
    return 0;
  });

  const totalBalance = data.reduce((s,r)=>s+r.balance,0);
  const totalOverdue = data.reduce((s,r)=>s+r.overdueAmount,0);

  return (
    <div>
      <div style={{fontWeight:700,fontSize:15,marginBottom:12}}>💰 Outstanding Balance Report</div>
      <div style={{display:'flex',gap:10,marginBottom:12,flexWrap:'wrap',alignItems:'flex-end'}}>
        <div className="form-group" style={{margin:0}}>
          <label style={{fontSize:11}}>Batch</label>
          <SearchableSelect
            options={[{value:'',label:'All Batches'},...batches.map(b=>({value:b.batch_name,label:b.batch_name}))]}
            value={fBatch}
            onChange={v=>setFBatch(v||'')}
            placeholder="All Batches"
            style={{minWidth:200}}
          />
        </div>
        <div className="form-group" style={{margin:0}}>
          <label style={{fontSize:11}}>Sort By</label>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{fontSize:12}}>
            <option value="balance">Highest Balance</option>
            <option value="overdue">Overdue Amount</option>
            <option value="name">Name</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={search} disabled={loading}>
          {loading?'Loading...':'🔍 Generate Report'}
        </button>
        {data.length>0&&<button className="btn btn-ghost btn-sm" onClick={exportCSV}>📥 Export Excel</button>}
      </div>

      {searched&&data.length>0&&(
        <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:12}}>
            <div className="card" style={{textAlign:'center',padding:12}}>
              <div style={{fontSize:20,fontWeight:800,color:'var(--red)'}}>₹{totalBalance.toLocaleString('en-IN')}</div>
              <div style={{fontSize:12,color:'var(--text3)'}}>Total Outstanding</div>
            </div>
            <div className="card" style={{textAlign:'center',padding:12}}>
              <div style={{fontSize:20,fontWeight:800,color:'var(--amber)'}}>₹{totalOverdue.toLocaleString('en-IN')}</div>
              <div style={{fontSize:12,color:'var(--text3)'}}>Total Overdue</div>
            </div>
            <div className="card" style={{textAlign:'center',padding:12}}>
              <div style={{fontSize:20,fontWeight:800,color:'var(--accent)'}}>{data.length}</div>
              <div style={{fontSize:12,color:'var(--text3)'}}>Students with Balance</div>
            </div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'var(--accent-light)'}}>
                  {['#','Reg No','Name','Batch','Phone','Total Fee','Paid','Balance','Overdue'].map(h=>(
                    <th key={h} style={{padding:'6px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r,i)=>(
                  <tr key={r.id} style={{borderBottom:'1px solid var(--border)',background:r.overdueAmount>0?'rgba(239,68,68,0.04)':i%2===0?'':'var(--surface2)'}}>
                    <td style={{padding:'6px 10px',color:'var(--text3)'}}>{i+1}</td>
                    <td style={{padding:'6px 10px',fontFamily:'monospace',fontSize:11}}>{r.register_number||'—'}</td>
                    <td style={{padding:'6px 10px',fontWeight:500}}>{r.full_name}</td>
                    <td style={{padding:'6px 10px'}}>{r.batch_name||'—'}</td>
                    <td style={{padding:'6px 10px'}}>{r.contact_number||'—'}</td>
                    <td style={{padding:'6px 10px',textAlign:'right'}}>₹{Number(r.fee_total||0).toLocaleString('en-IN')}</td>
                    <td style={{padding:'6px 10px',textAlign:'right',color:'var(--green)'}}>₹{r.totalPaid.toLocaleString('en-IN')}</td>
                    <td style={{padding:'6px 10px',textAlign:'right',fontWeight:700,color:'var(--red)'}}>₹{r.balance.toLocaleString('en-IN')}</td>
                    <td style={{padding:'6px 10px',textAlign:'right',color:r.overdueAmount>0?'var(--red)':'var(--text3)'}}>
                      {r.overdueAmount>0?`₹${r.overdueAmount.toLocaleString('en-IN')}`:'—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {searched&&data.length===0&&!loading&&(
        <div style={{textAlign:'center',padding:32,color:'var(--green)',fontSize:14}}>✅ No outstanding balances found</div>
      )}
    </div>
  );
}

// ── Fee Collection Summary by Date ─────────────────────────────
function FeeCollectionReport({ supabase }) {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [period, setPeriod] = React.useState('today');
  const [fromDate, setFromDate] = React.useState(localDate());
  const [toDate, setToDate] = React.useState(localDate());

  async function load() {
    setLoading(true);
    const today = localDate();
    let from = fromDate, to = toDate;
    if (period==='today') { from = today; to = today; }
    else if (period==='week') {
      from = localDate(new Date(Date.now()-7*86400000)); to = today;
    } else if (period==='month') {
      from = localDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)); to = today;
    }

    try {
      const payments = await fetchAllPaged(() => supabase.from('fee_payments')
        .select('*,students(full_name,register_number,batch_name)')
        .gte('payment_date', from).lte('payment_date', to)
        .order('payment_date', {ascending:false}).order('id'));
      setData(payments||[]);
    } catch (e) { toast.error('Could not load payments: ' + e.message); }
    setLoading(false);
  }

  React.useEffect(()=>{ load(); },[period]);

  const total = data.reduce((s,p)=>s+Number(p.amount||0),0);
  const byMode = {};
  data.forEach(p=>{ byMode[p.payment_mode||'Other'] = (byMode[p.payment_mode||'Other']||0)+Number(p.amount||0); });

  return (
    <div>
      <div style={{fontWeight:700,fontSize:15,marginBottom:12}}>📅 Fee Collection Summary</div>
      <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap',alignItems:'flex-end'}}>
        {['today','week','month','custom'].map(p=>(
          <button key={p} className={`btn btn-sm ${period===p?'btn-primary':'btn-ghost'}`} onClick={()=>setPeriod(p)}>
            {p==='today'?'Today':p==='week'?'Last 7 Days':p==='month'?'This Month':'Custom'}
          </button>
        ))}
        {period==='custom'&&<>
          <DateInput value={fromDate} onChange={e=>setFromDate(e.target.value)} style={{fontSize:12}}/>
          <DateInput value={toDate} onChange={e=>setToDate(e.target.value)} style={{fontSize:12}}/>
          <button className="btn btn-primary btn-sm" onClick={load}>🔍 Search</button>
        </>}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10,marginBottom:12}}>
        <div className="card" style={{textAlign:'center',padding:12}}>
          <div style={{fontSize:20,fontWeight:800,color:'var(--green)'}}>₹{total.toLocaleString('en-IN')}</div>
          <div style={{fontSize:12,color:'var(--text3)'}}>Total Collected</div>
        </div>
        <div className="card" style={{textAlign:'center',padding:12}}>
          <div style={{fontSize:20,fontWeight:800,color:'var(--accent)'}}>{data.length}</div>
          <div style={{fontSize:12,color:'var(--text3)'}}>Transactions</div>
        </div>
        {Object.entries(byMode).map(([mode,amt])=>(
          <div key={mode} className="card" style={{textAlign:'center',padding:12}}>
            <div style={{fontSize:18,fontWeight:700,color:'var(--blue)'}}>₹{amt.toLocaleString('en-IN')}</div>
            <div style={{fontSize:11,color:'var(--text3)'}}>{mode}</div>
          </div>
        ))}
      </div>

      {loading?<div style={{textAlign:'center',padding:20}}>Loading...</div>:
        data.length>0?(
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'var(--accent-light)'}}>
                  {['Date','Receipt No','Student','Batch','Amount','Mode','Fee Head'].map(h=>(
                    <th key={h} style={{padding:'6px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((p,i)=>(
                  <tr key={p.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'':'var(--surface2)'}}>
                    <td style={{padding:'6px 10px'}}>{fmtDMY(p.payment_date)}</td>
                    <td style={{padding:'6px 10px',fontFamily:'monospace',fontSize:11}}>{p.receipt_no||'—'}</td>
                    <td style={{padding:'6px 10px',fontWeight:500}}>{p.students?.full_name||'—'}</td>
                    <td style={{padding:'6px 10px'}}>{p.students?.batch_name||'—'}</td>
                    <td style={{padding:'6px 10px',fontWeight:700,color:'var(--green)'}}>₹{Number(p.amount||0).toLocaleString('en-IN')}</td>
                    <td style={{padding:'6px 10px'}}>{p.payment_mode||'—'}</td>
                    <td style={{padding:'6px 10px',color:'var(--text3)'}}>{p.fee_head||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ):<div style={{textAlign:'center',padding:20,color:'var(--text3)'}}>No payments in this period</div>
      }
    </div>
  );
}

// ── Discount History Component ──────────────────────────────────
function DiscountHistory({ studentId, enrollmentDiscount, enrollmentReason }) {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(()=>{
    if (!studentId) return;
    supabase.from('fee_discount_log').select('*').eq('student_id', studentId)
      .order('created_at',{ascending:false})
      .then(({data})=>{ setLogs(data||[]); setLoading(false); });
  },[studentId]);

  const totalAdjustments = logs.reduce((s,l)=>s+Number(l.amount||0),0);
  const initialDiscount = enrollmentDiscount - totalAdjustments;

  return (
    <div style={{marginTop:12,padding:12,background:'var(--surface2)',borderRadius:8,fontSize:12}}>
      <div style={{fontWeight:700,marginBottom:8,color:'var(--text2)'}}>🎫 Discount History</div>
      {loading ? <div style={{color:'var(--text3)'}}>Loading...</div> : (
        <>
          {/* Initial enrollment discount */}
          {initialDiscount>0&&(
            <div style={{padding:'6px 10px',background:'#fff',borderRadius:6,marginBottom:4,
              display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <span style={{fontWeight:600,color:'var(--green)'}}>₹{initialDiscount.toLocaleString('en-IN')}</span>
                <span style={{marginLeft:8,color:'var(--text3)'}}>at enrollment</span>
                {enrollmentReason&&<span style={{marginLeft:8,color:'var(--text3)'}}>— {enrollmentReason}</span>}
              </div>
              <span style={{fontSize:10,padding:'1px 6px',borderRadius:8,background:'var(--accent-light)',color:'var(--accent)'}}>Initial</span>
            </div>
          )}
          {/* Post-enrollment adjustments */}
          {logs.map(l=>(
            <div key={l.id} style={{padding:'6px 10px',background:'#fff',borderRadius:6,marginBottom:4,
              display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <span style={{fontWeight:600,color:'var(--green)'}}>₹{Number(l.amount).toLocaleString('en-IN')}</span>
                <span style={{marginLeft:8,color:'var(--text3)'}}>— {l.reason}</span>
                {l.approved_by&&<span style={{marginLeft:8,color:'var(--text3)'}}>approved by {l.approved_by}</span>}
                <span style={{marginLeft:8,color:'var(--text3)',fontSize:10}}>{l.created_at?.split('T')[0]}</span>
              </div>
              <span style={{fontSize:10,padding:'1px 6px',borderRadius:8,background:'var(--green-light)',color:'var(--green)'}}>Adjustment</span>
            </div>
          ))}
          {logs.length===0&&initialDiscount<=0&&(
            <div style={{color:'var(--text3)'}}>No discount history</div>
          )}
          {/* Total */}
          {(logs.length>0||initialDiscount>0)&&(
            <div style={{marginTop:6,paddingTop:6,borderTop:'1px solid var(--border)',display:'flex',justifyContent:'space-between',fontWeight:700}}>
              <span>Total Discount:</span>
              <span style={{color:'var(--green)'}}>₹{enrollmentDiscount.toLocaleString('en-IN')}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
